import type { ArabicCatalog } from "@ncp/i18n";
import type en from "./en";
import requests from "./requests.ar";
import reviewChecks from "./reviewChecks.ar";
import agentic from "./agentic.ar";
import decisionControls from "./decisionControls.ar";
import stepLetters from "./stepLetters.ar";
import processes from "./processes.ar";

const ar: ArabicCatalog<typeof en> = {
  requests,
  reviewChecks,
  agentic,
  decisionControls,
  stepLetters,
  processes,
  caseRecords: {
    fees: {
      title: "الرسوم",
      hint: "كل رسم مع ما أفادت به بوابة الدفع عند آخر استعلام. والرسم الذي عكس سداده أو استرد لم يعد مستلما، ولا يعد الرسم مردودا إلا بعد تأكيد تحويله.",
      none: "لا يوجد رسم مسجل على هذا الملف.",
      receipt: "الإيصال {{receipt}}",
      gatewayChecked: "آخر استعلام من البوابة في {{date}}: {{status}}",
      reversed: "عكس السداد في {{date}}",
      refundRequested: "صدرت التعليمات بالرد في {{date}}",
      refunded: "رد في {{date}} · المرجع {{reference}}",
      recheck: "إعادة الاستعلام من البوابة",
      recheckDone: "أعيد الاستعلام من البوابة",
      changes: { None: "لا تغيير", Reversed: "عكس السداد", RefundConfirmed: "تم تأكيد الرد" },
      recordReversal: "تسجيل عكس السداد",
      reversalTitle: "تسجيل عكس السداد أو استرداده",
      reversalHint: "عندما تفيد الوحدة المالية بأن رسما مسددا قد استرد – استرداد من البنك أو تحويل مرتجع. وتبقى خطوة الرسم مفتوحة إلى أن يسدد الرسم.",
      reason: "السبب",
      confirmRefund: "تأكيد الرد",
      refundTitle: "تأكيد الرد",
      refundHint: "من السجل المالي: مرجع التحويل أو الرد، وتاريخ عودة المبلغ إلى الدافع.",
      reference: "مرجع التحويل أو الرد",
      refundedOn: "تاريخ الرد",
      saved: "تم التسجيل",
    },
    meetings: {
      purpose: "الغرض",
      purposeHint: "ما تحتاجه الإدارة من الاجتماع (المادة 14 من اللائحة التنفيذية).",
      noPurpose: "لم يسجل الغرض",
      iAttend: "أحضر هذا الاجتماع",
      iAttendHint: "لا يعتمد المحضر إلا موظف حضر الاجتماع.",
      attendance: "تسجيل حضوري",
      attendanceDone: "سجل حضورك",
      approvedBy: "اعتمده {{name}}",
      problems: {
        NoMinutes: "لم يعد المحضر بعد",
        NotApproved: "المحضر لم يعتمد بعد",
        ApproverUnknown: "اعتمد دون تسجيل من اعتمده",
        ApproverDidNotAttend: "اعتمده موظف غير مسجل بين الحاضرين",
      },
      partyReview: {
        title: "اطلاع الطرف على المحضر",
        hint: "يجوز للأطراف الذين استمع إليهم الاطلاع على المحضر وتوقيعه (المادة 23(5)–(6) من اللائحة التنفيذية). سجل ما إذا وقعوه أو وقعوه مع التحفظ أو امتنعوا عن توقيعه.",
        record: "تسجيل اطلاع الطرف",
        status: "النتيجة",
        signatory: "وقعه، أو امتنع عن توقيعه",
        note: "التحفظات أو سبب الامتناع",
        reviewedOn: "التاريخ",
        saved: "سجل اطلاع الطرف",
        statuses: { Pending: "بانتظار اطلاع الطرف", Signed: "وقعه الطرف", SignedWithReservations: "وقعه مع التحفظ", RefusedToSign: "امتنع الطرف عن التوقيع" },
      },
    },
    jurisdiction: {
      title: "الاختصاص – المادة 4 من المرسوم بقانون",
      hint: "استثناءات المادة (4): قواعد المنافسة الخاصة بجهة تنظيمية قطاعية (4(1))، والمنشآت المملوكة للحكومة الاتحادية أو لحكومة إمارة تمارس نشاطها فيها وفق ما يحدده قرار (4(2)–(3)). ولا تعد الشكوى ضمن الاختصاص إلا بعد الإجابة عن كل منها.",
      regulators: "الجهات التنظيمية القطاعية لقطاع الشكوى",
      noRegulator: "لا يقع القطاع المعلن ضمن اختصاص جهة تنظيمية قطاعية.",
      noIsic: "لا تحمل الشكوى رمز ISIC.",
      stateOwned: "تظهر في بنك المعلومات بوصفها جهة حكومية",
      regulator: "الجهة التنظيمية المعنية",
      ownRules: "يمنح قانون آخر الجهة التنظيمية قواعد خاصة بالمنافسة لهذه السلعة أو الخدمة",
      referred: "طلبت الجهة التنظيمية كتابيا من الوزارة تولي المسألة ووافقت الوزارة",
      ownership: "ملكية المشكو ضده",
      ownerships: { None: "غير مملوكة للحكومة", Federal: "مملوكة للحكومة الاتحادية", Emirate: "مملوكة لحكومة إمارة" },
      designated: "محددة بقرار من مجلس الوزراء أو من الحكومة المحلية",
      withinEmirate: "تمارس النشاط المعني في تلك الإمارة",
      resolution: "القرار",
      note: "ملاحظة",
      answer: { unknown: "لم يجب بعد", yes: "نعم", no: "لا" },
      save: "تسجيل النتائج",
      saved: "سجلت النتائج",
    },
    exemption: {
      validity: "مدة الصلاحية",
      validityMissing: "مدة الصلاحية غير مسجلة: لا يمكن تقديم تعديل إلى أن تسجل.",
      validFrom: "ساري من",
      validUntil: "ينتهي في",
      source: "المصدر",
      recordValidity: "تسجيل المدة من القرار",
      validityHint: "المدة التي يذكرها القرار الموقع (المادة 9(1) من اللائحة التنفيذية) – لا مدة الاتفاق نفسه.",
      endedEarly: "انتهى قبل أوانه في {{date}}",
      recordEarlyEnd: "تسجيل الإلغاء أو الانتهاء المبكر",
      earlyEndHint: "يلغى الاستثناء إذا زالت الظروف التي منح على أساسها أو لم تستوف شروطه أو كانت المعلومات المقدمة مضللة (المادة 10 من المرسوم بقانون)، وينتهي كذلك بانتهاء الاتفاق.",
      kind: "كيف انتهى",
      kinds: { Revoked: "ألغته الوزارة", Terminated: "أنهي الاتفاق" },
      ground: "السبب (المادة 10 من المرسوم بقانون)",
      grounds: { CircumstancesChanged: "زالت الظروف", ConditionsNotFulfilled: "لم تستوف الشروط", MisleadingInformation: "كانت المعلومات المقدمة مضللة" },
      endedOn: "تاريخ الانتهاء",
      note: "قرار الإلغاء أو إشعار الأطراف",
      saved: "تم التسجيل",
      notInForce: "غير ساري",
    },
    commitments: {
      toConfirm: "يلزم التأكيد",
      terms: { Obligor: "الملتزم", Kind: "النوع", DueOn: "الموعد", VerificationMethod: "طريقة التحقق", Wording: "الصياغة الموقعة" },
      confirm: "تأكيد الشروط",
      confirmHint: "شروط لا يذكرها القرار الموقع، أو استنتجها السجل من الصياغة: أكدها، أو سجل تاريخ الواقعة التي يحتسب منها الموعد.",
      obligor: "الملتزم",
      kind: "النوع",
      verification: "طريقة التحقق",
      triggerOn: "تاريخ {{trigger}}",
      dueOn: "الموعد",
      owner: "المسؤول عن المتابعة",
      confirmWording: "تسجيل الشرط بصياغته الموقعة",
      signed: "كما وقع",
      runsFrom: "الموعد بعد {{period}} من {{trigger}}",
      triggers: { Decision: "القرار", Completion: "الإتمام", Implementation: "التنفيذ", Notification: "الإخطار" },
      months_zero: "صفر شهر",
      months_one: "شهر واحد",
      months_two: "شهرين",
      months_few: "{{count}} أشهر",
      months_many: "{{count}} شهرا",
      months_other: "{{count}} شهر",
      days_zero: "صفر يوم",
      days_one: "يوم واحد",
      days_two: "يومين",
      days_few: "{{count}} أيام",
      days_many: "{{count}} يوما",
      days_other: "{{count}} يوم",
      saved: "أكدت الشروط",
    },
    commitmentAlerts: {
      overdue: "متأخر منذ {{date}}",
      breached: "مخالف",
      implementationDate: "موعد التنفيذ {{date}}",
      with: "لدى {{level}}",
      levels: { Owner: "المسؤول عن المتابعة", Director: "المدير", AssistantUndersecretary: "الوكيل المساعد" },
      evidence: "الأدلة المسجلة عند إطلاق التنبيه",
      lastVerified: "آخر تحقق في {{date}}",
      noVerification: "لا يوجد تحقق مسجل",
      measured: "آخر قياس {{value}} في {{date}} مقابل هدف {{target}}",
      noMeasurement: "لا يوجد قياس مسجل مقابل الهدف",
      documents: "في الملف",
      noDocuments: "لا يوجد تقرير متابعة أو مستند مودع",
      history: "التصعيد",
      automatic: "من السجل",
      escalate: "التصعيد إلى {{level}}",
      escalateTitle: "تصعيد الالتزام {{n}} إلى {{level}}",
      escalateHint: "يسجل التصعيد في الملف باسمك ووقته وأسبابه، وتوجه مهمة وإشعار إلى {{level}}.",
      reason: "الأسباب",
      escalated: "تم التصعيد إلى {{level}}",
    },
    /** After the decision (scenario 6.44): the undertaking's compliance reports and their review, a status step with reasons, the waiver and the enforcement file. */
    commitmentLifecycle: {
      reports: "تقارير الامتثال من المنشأة الملتزمة",
      evidence: "الأدلة",
      review: "مراجعة التقرير",
      reviewTitle: "مراجعة تقرير الامتثال للالتزام {{n}}",
      outcome: "النتيجة",
      outcomes: { Accepted: "ثبت الامتثال", Insufficient: "يلزم المزيد" },
      reviewNote: "ما انتهت إليه المراجعة",
      recordReview: "تسجيل المراجعة",
      reviewed: "سجلت المراجعة",
      awaitingReview: "بانتظار المراجعة",
      stepTitle: "تسجيل حالة {{status}} للالتزام رقم {{n}}",
      stepHint: "يسجل الإخلال وإعادة الفتح بأسبابك واسمك ووقت التسجيل في سجل الملف.",
      waive: "إعفاء",
      waiveTitle: "الإعفاء من الالتزام {{n}}",
      waiveHint: "يسجل الإعفاء بأسبابك وسنده القانوني واسمك ووقته، وتطلع عليه المنشأة في ملفها، ويخرج الالتزام من التنبيهات.",
      legalBasis: "السند القانوني",
      legalBasisHint: "الحكم أو القرار الذي يجيز الإعفاء.",
      legalBasisAr: "السند القانوني بالعربية (اختياري)",
      waived: "أعفي من الالتزام",
      waivedBy: "أعفى منه {{by}} في {{date}}:",
      openEnforcement: "فتح ملف إنفاذ",
      enforcementTitle: "فتح ملف إنفاذ على الإخلال بالالتزام {{n}}",
      enforcementHint: "ملف بمبادرة من الإدارة عن تنفيذ التركز بالمخالفة للقرار (المادة 26)، يفتح بقرارك المسجل. ويحمل الأدلة المسجلة – التنبيه وآخر تحقق وقياس وتقارير الامتثال والمستندات – ويرتبط بهذا الملف.",
      enforcementOpened: "فتح ملف الإنفاذ {{number}}",
      enforcementFile: "ملف الإنفاذ {{number}}",
    },
  },
  /** رفع مستندات الملف في المكتب الخلفي (6.12 و6.13): سبب رفض الملف، وخيار «ترجمة لـ». */
  filingUploads: {
    notUploaded: "لم يرفع الملف",
    legacyWord: "الملف {{name}} مستند Word بالصيغة القديمة 97–2003 (.doc). لا تستطيع المنصة قراءة هذه الصيغة القديمة، فلا يمكن قراءة ما فيه أو فحصه. يرجى حفظه بصيغة Word الحديثة (.docx) أو بصيغة PDF ثم رفعه من جديد.",
    legacyOffice: "الملف {{name}} ملف Excel أو PowerPoint بالصيغة القديمة 97–2003 لا تستطيع المنصة قراءته. يرجى حفظه بالصيغة الحديثة (.xlsx أو .pptx) أو بصيغة PDF ثم رفعه من جديد.",
    refused: "لم يرفع الملف {{name}}: لا يقبل هذا النوع من الملفات هنا. يرجى رفع ملف PDF أو مستند Word (.docx) أو مصنف Excel (.xlsx) أو ملف نصي أو CSV أو صورة (PNG أو JPEG).",
    tooLarge: "لم يرفع الملف {{name}}: حجمه أكبر من الحجم المقبول هنا.",
    translationOf: "ترجمة لـ",
    notATranslation: "ليس ترجمة – مستند أصلي",
    translationHint: "إذا كان هذا الملف ترجمة لمستند موجود في الملف فيختار الموظف الأصل: يقارن النصان عند قراءتهما وتعلم الاختلافات، ويحال أي تعارض في مستندات الأطراف إلى الموظف المختص.",
    linked: "أدرج بوصفه ترجمة",
    unlinked: "أدرج بوصفه مستندا أصليا",
  },
  /** The structure of a concentration: its type recorded with reasons (6.10) and a joint venture's parents (6.9); theories from the figures (5.2). */
  concentrationStructure: {
    typeTitle: "نوع التركز",
    typeHint: "يسجل نوع كل سوق ونوع الملف مع أسباب التصنيف. وتعبئ المنصة التصنيف مسبقا من الأرقام – أفقي فقط حيث ينشط الطرفان معا – وتنبه إلى أوصاف مقدم الطلب التي تخالفها الأرقام. ويبين البند 3 من تقرير الدراسة التصنيف المسجل.",
    types: { Horizontal: "أفقي", Vertical: "رأسي", Conglomerate: "تكتلي", None: "لا يوجد تداخل", none: "غير محدد" },
    applicantLabel: "مقدم الطلب: {{type}}",
    bothActive: "ينشط الطرفان معا",
    notBothActive: "لا ينشط الطرفان معا",
    conflict: "تخالفه الأرقام",
    type: "النوع",
    fileType: "نوع التركز",
    reasons: "أسبابك",
    fileReasons: "أسبابك لتصنيف نوع التركز",
    reasonsHint: "جملة على الأقل: فاختيار النوع من قائمة ليس تصنيفا.",
    choose: "اختر…",
    save: "تسجيل التصنيف",
    saved: "سجل التصنيف",
    notSaved: "لم يسجل التصنيف",
    recorded: "{{type}} · {{name}} · {{date}}",
    incomplete: "مسجل وغير مكتمل",
    notRecorded: "لم يسجل بعد",
    departs: "يخالف تصنيفك الأرقام في {{markets}}: ويلزم أن تجيب أسبابك عنها.",
    jvTitle: "المشروع المشترك والشركات الأم",
    fullFunction: "كامل الوظائف",
    notFullFunction: "غير كامل الوظائف",
    jointControl: "سيطرة مشتركة",
    controlUnclear: "السيطرة غير ثابتة",
    notFiled: "الهيكل غير مقدم",
    spillOver_zero: "لا توجد سوق تبقى فيها شركتان أم نشطتين",
    spillOver_one: "سوق واحدة تبقى فيها شركتان أم أو أكثر نشطتين",
    spillOver_two: "سوقان تبقى فيهما شركتان أم أو أكثر نشطتين",
    spillOver_few: "{{count}} أسواق تبقى فيها شركتان أم أو أكثر نشطتين",
    spillOver_many: "{{count}} سوقا تبقى فيها شركتان أم أو أكثر نشطتين",
    spillOver_other: "{{count}} سوق تبقى فيها شركتان أم أو أكثر نشطتين",
    noSpillOver: "لا توجد سوق تبقى فيها شركتان أم نشطتين",
    parent: "الشركة الأم",
    stake: "الحصة",
    controlRights: "حقوق السيطرة",
    activities: "الأنشطة",
    noOtherActivity: "لم يعلن عن أي نشاط",
    rights: { BoardMajority: "أغلبية مجلس الإدارة", VetoStrategic: "حق الاعتراض على القرارات الاستراتيجية", CastingVote: "الصوت المرجح", ManagementContract: "عقد إدارة", OptionToAcquireControl: "خيار اكتساب السيطرة", MinorityProtectionOnly: "حماية الأقلية فقط" },
    relations: { Same: "سوق المشروع المشترك", Upstream: "سوق سابقة", Downstream: "سوق لاحقة", Neighbouring: "سوق مجاورة" },
    marketsAtRisk: "التنسيق بين الشركات الأم الواجب تقييمه",
    together: "ومجموعها {{share}}",
    missing: "غير وارد في النموذج: {{items}}.",
    jvCriterion: "يلزم هذا الملف معيار «تقييم التنسيق بين الشركات الأم»: ويقيم بند الآثار المنسقة في تقرير الدراسة كل سوق مبينة هنا.",
    noTheoryOnFigures: "لا تؤيد الأرقام الحالية أي نظرية ضرر.",
  },
  r3Metrics: {
    notRecorded: "غير مسجل",
    none: "لا شيء",
    inputs: "يقوم على",
    methodVersion: "منهجية الاحتساب {{version}}",
    aboutFigure: "عن {{name}}",
    measures: {
      hhi: "مؤشر هيرفندال-هيرشمان (HHI)",
      deltaHhi: "التغير في مؤشر هيرفندال-هيرشمان",
      crN: "نسبة التركز",
      combinedShare: "الحصة المجمعة",
      unallocatedShare: "الحصة غير الموزعة",
      riskScore: "درجة المخاطر الهيكلية",
      guppi: "مؤشر الضغط التصاعدي على الأسعار (GUPPI)",
      cmcr: "الخفض التعويضي في التكلفة الحدية (CMCR)",
      criticalLoss: "الخسارة الحرجة",
      assumedMargin: "الهامش الإجمالي المفترض",
      turnoverGroup: "المبيعات العالمية للمجموعة",
      turnoverState: "المبيعات في الدولة",
      turnoverMarket: "المبيعات في السوق المعنية",
      shareStatus: "حالة الحصة",
      completeness: "اكتمال الملف",
      efficiencyCredit: "الكفاءات المحتسبة",
      dealScreeningScore: "درجة فرز الصفقة",
      marketScreeningScore: "درجة فرز السوق",
      marketSignalScore: "درجة إشارة بنك البيانات",
      draftQuality: "الفحص الآلي (القراءة الثانية للمسودة)",
      figureCheck: "حالة الرقم والتحقق منه",
      criterionConfidence: "ثقة قراءة المنصة",
      draftConfidence: "ثقة مسودة الوكيل",
      fileRiskScore: "درجة مخاطر الملف خارج رقابة التركز",
      triagePriority: "أولوية الفرز (الشكاوى وبلاغات المخالفات)",
      article9Score: "درجة التوافق مع المادة (9) (الاستثناءات وتعديلاتها)",
      filingReadiness: "درجة جاهزية الطلب",
      penaltyIndication: "نطاق الغرامة والتصالح الاسترشادي (الإنفاذ)",
      assistantConfidence: "ثقة إجابة المساعد القانوني",
      dataBankConfidence: "ثقة رقم بنك البيانات",
      mergerSimulation: "محاكاة التركز (الأثر السعري)",
      localCatchment: "النطاق المحلي",
      marketStudyAnalyses: "تحليلات دراسة السوق",
      operationalStatistics: "الإحصاءات التشغيلية",
    },
    figureStatus: { Declared: "معلن من مقدم الطلب", Entered: "أدخله موظف", Calculated: "محسوب", Extracted: "مقروء من مستند", NotRecorded: "غير مسجل" },
    figureDeclaredBy: { applicant: "معلن من مقدم الطلب", complainant: "معلن من الشاكي", parties: "معلن من الأطراف" },
    figureCheck: {
      notIndependent: "لم يتحقق منه مقابل مصدر مستقل",
      unchecked: "غير متحقق منه",
      inputsUnchecked: "محسوب من أرقام غير متحقق منها – يتحقق منها أولا: {{inputs}}",
      page: "الصفحة {{page}}",
      check: "التحقق مقابل المصدر",
      checkedBy: "تحقق منه {{name}} بتاريخ {{date}}",
      recorded: "سجل المصدر بتاريخ {{date}}",
      title: "التحقق من هذا الرقم مقابل مصدره",
      hint: "لا يقتبس الرقم المحسوب أو المقروء في تقرير أو قرار أو خطاب معتمد قبل أن يتحقق منه موظف. ويبين الموظف المصدر الذي تحقق منه مقابله وما يعطيه. والتحقق ليس توثيقا: لا يوثق الرقم إلا مصدر مستقل عن مقدم الطلب.",
      note: "كيف تحققت منه",
      confirm: "تسجيل التحقق",
      saved: "سجل التحقق",
    },
    asFiled: "كما قدمت",
    hhiPre: "مؤشر هيرفندال-هيرشمان قبل الصفقة",
    hhiPost: "مؤشر هيرفندال-هيرشمان بعد الصفقة",
    crPre: "نسبة تركز أكبر المنشآت ({{n}}) قبل الصفقة",
    turnoverGroup: "مبيعات المجموعة (في العالم)",
    turnoverState: "المبيعات في الدولة",
    turnoverMarket: "المبيعات في هذه السوق",
    partiesShareHighest: "حصة الأطراف (أعلى سوق)",
    partiesShare: "حصة الأطراف",
    competitorsIdentified: "المنافسون المحددون",
    noOverlap: "لا تداخل",
    noOverlapAnywhere: "لا ينشط الطرفان معا في أي سوق",
    mostAffected: "السوق الأكثر تأثرا",
    concentrationTitle: "التركز",
    deltaHhi: "التغير في مؤشر هيرفندال-هيرشمان",
    crPost: "نسبة تركز أكبر المنشآت ({{n}}) بعد الصفقة",
    hhiAfter: "مؤشر هيرفندال-هيرشمان بعد الصفقة",
    hhiShort: "مؤشر هيرفندال-هيرشمان",
    unallocated: "الحصة غير الموزعة",
    identifiedFirms_zero: "لا منشآت محددة",
    identifiedFirms_one: "منشأة محددة واحدة",
    identifiedFirms_two: "منشأتان محددتان",
    identifiedFirms_few: "{{count}} منشآت محددة",
    identifiedFirms_many: "{{count}} منشأة محددة",
    identifiedFirms_other: "{{count}} منشأة محددة",
    unallocatedLeftOut: "{{share}} غير موزعة ومستبعدة من المؤشر",
    lowerBound: "حد أدنى: {{share}} من السوق غير موزعة. ولو كانت منشأة واحدة لبلغ المؤشر بعد الصفقة {{max}}.",
    sharesDoNotAddUp: "مجموع الحصص {{total}} لا 100%. وتستخدم كما أعلنت.",
    sharesAndSources: "الحصص المعلنة والمصادر المستقلة",
    declaredCombined: "الحصة المجمعة المعلنة",
    declaredPartiesShare: "حصة الأطراف المعلنة",
    independentCombined: "المصادر المستقلة",
    declaredShare: "المعلنة",
    independentShare: "مصدر مستقل",
    difference: "الفرق (نقاط)",
    applicantFiling: "طلب مقدم الطلب فقط",
    notOneUndertaking: "ليست منشأة واحدة",
    shareStatus: { Confirmed: "مؤكدة", Discrepancy: "تباين", Declared: "معلنة من مقدم الطلب – غير متحقق منها", Unallocated: "غير موزعة", SourceOnly: "في المصدر المستقل فقط" },
    independentSources: "المصادر المستقلة",
    noIndependentSource: "لا يغطي هذه السوق أي مصدر مستقل عن مقدم الطلب.",
    records_zero: "لا سجلات",
    records_one: "سجل واحد",
    records_two: "سجلان",
    records_few: "{{count}} سجلات",
    records_many: "{{count}} سجلا",
    records_other: "{{count}} سجل",
    riskScore: "درجة المخاطر الهيكلية",
    scoreRestsOn: "تقوم على السوق الأكثر تأثرا: {{market}}",
    verticalLink: "علاقة رأسية معلنة",
    dominanceAbove: "يعد الوضع مسيطرا فوق {{share}}%.",
    quantitativeTests: "الاختبارات الكمية",
    horizontalNotApplicable: "لا تنطبق: لا ينشط الطرفان معا في هذه السوق.",
    guppiOn: "مؤشر الضغط التصاعدي على الأسعار على منتجات {{party}}",
    cmcrOn: "الخفض التعويضي في التكلفة الحدية على منتجات {{party}}",
    criticalLoss: "الخسارة الحرجة",
    notDecided: "لم يحسم",
    marginLabel: "الهامش الإجمالي",
    assumedMargin: "مفترض – لم تقدمه الأطراف",
    margin: "الهامش {{margin}}",
    priceRise: "الزيادة السعرية",
    scenariosNotApplicable: "لا ينطبق: لا ينشط الطرفان معا في هذه السوق، فلا يوجد تداخل يعالج بالتنازل.",
    scenarioBaseline: "خط الأساس: مؤشر هيرفندال-هيرشمان قبل الصفقة كما أخطر به {{hhi}}",
    changeFromNotified: "التغير عن خط الأساس",
    calculateDivestiture: "احتساب هذا التنازل",
    covered: "تغطي المنشآت المحددة {{share}}",
    declaredByApplicant: "معلن من مقدم الطلب",
    efficienciesTitle_zero: "اختبار الكفاءات",
    efficienciesTitle_one: "اختبار الكفاءات · ادعاء واحد",
    efficienciesTitle_two: "اختبار الكفاءات · ادعاءان",
    efficienciesTitle_few: "اختبار الكفاءات · {{count}} ادعاءات",
    efficienciesTitle_many: "اختبار الكفاءات · {{count}} ادعاء",
    efficienciesTitle_other: "اختبار الكفاءات · {{count}} ادعاء",
    verdicts: { Yes: "نعم", Partial: "جزئيا", No: "لا", NotVerified: "غير متحقق منه" },
    countsZero: "يحتسب صفرا",
    recordVerified: "تسجيل التحقق",
    withdrawVerification: "سحب التحقق",
    verifiedAgainst: "ما جرى التحقق في ضوئه (المستند والصفحة)",
    preDealSource: "التقدير السابق للصفقة في (ورقة مجلس الإدارة أو التقدير الداخلي، التاريخ، الصفحة)",
    preDealPercent: "الوفر قبل الصفقة، % من التكلفة الحدية",
    preDealAed: "الوفر قبل الصفقة، بالدرهم سنويا",
    preDealHint: "الوفر كما قدرته الأطراف في السياق المعتاد قبل الصفقة. والرقم المعد للإخطار وحده لا يعد متحققا منه، ولا يتجاوز الاحتساب التقدير السابق للصفقة.",
    preDealRecorded: "قورن بالتقدير السابق للصفقة الوارد في {{source}}",
    assessSpecific: "تقييم الخصوصية بالتركز",
    specificityReasons: "أي سبيل أقل تقييدا بحث، ولماذا يحقق الوفر أو لا يحققه",
    confirmSpecific: "خاص بالتركز",
    rejectSpecific: "غير خاص بالتركز",
    withdrawSpecific: "سحب القرار",
    proposedByAgent: "مقترح من {{agent}} · {{date}} · لم يعتمد بعد",
    determinedByOfficer: "حدده {{name}} · {{date}}",
    openFindings_zero: "لا ملاحظات قائمة",
    openFindings_one: "ملاحظة قائمة واحدة",
    openFindings_two: "ملاحظتان قائمتان",
    openFindings_few: "{{count}} ملاحظات قائمة",
    openFindings_many: "{{count}} ملاحظة قائمة",
    openFindings_other: "{{count}} ملاحظة قائمة",
    harmStructural: "مؤشر هيكلي: يبلغ مؤشر هيرفندال-هيرشمان بعد الصفقة {{post}} ({{band}}) بتغير قدره {{delta}}، وهو فوق حدود المنهجية المنشورة، فيلزم تحليل مفصل لآثار الصفقة على المنافسة.",
    harmUnilateral: "آثار أحادية: الحصة المجمعة {{share}}% تتجاوز نسبة {{mark}}% التي يعد عندها الوضع مسيطرا.",
    supersededDraft: "مسودة سابقة للوكيل (قبل منهجية الحساب 1.0)",
    supersededDraftNote: "حلت محل أرقامها الأرقام الواردة أعلاه، المحسوبة وفق المنهجية المنشورة.",
    reportBeforeMethod: "صيغ قبل منهجية الحساب 1.0",
    reportBeforeMethodNote: "قد تختلف أرقامه عن التحليل الحالي، ويلزم صوغ مسودة جديدة قبل مضي الملف.",
  },
  login: {
    title: "تسجيل الدخول",
    subtitle: "الوصول مقصور على موظفي الوزارة عبر الشبكة الاتحادية.",
    sessionNote: "تتطلب كل جلسة كلمة المرور ورمز المصادقة. وتنتهي الجلسة بعد 30 دقيقة دون نشاط و10 ساعات كحد أقصى.",
    rightNow: "الآن",
    agents: {
      triage: { name: "وكيل الفرز", activity: "يفرز إخطارا جديدا بتركز اقتصادي" },
      market: { name: "وكيل تحديد السوق والتركز", activity: "يقيس التركز في سوق الخرسانة الجاهزة" },
      citations: { name: "مدقق الاستشهادات", activity: "يدقق كل استشهاد وفق المرسوم بقانون اتحادي رقم 36 لسنة 2023" },
      sla: { name: "مراقب المدد القانونية", activity: "يراقب المدد القانونية لكل حالة مفتوحة" },
      correspondence: { name: "وكيل المراسلات", activity: "يصوغ خطاب استكمال ثنائي اللغة للتوقيع" },
      intake: { name: "مساعد استلام الشكاوى", activity: "يفحص بلاغا مجهول الهوية عن مخالفة" },
      brief: { name: "كاتب مذكرة القرار", activity: "يعد مذكرة قرار للوزير" },
    },
  },
  risk: { level: { High: "عالية", Medium: "متوسطة", Low: "منخفضة" }, score: "درجة المخاطر {{score}} من 100", notAssessed: "لم تقيم بعد", namedScore: "{{name}}: {{score}} من 100" },
  commandCenter: {
    notScored: "لم تقيم المخاطر بعد",
    showAll: "عرض الكل ({{count}})",
    showFewer: "عرض أقل",
    otherSteps: "خطوات أخرى ({{count}})",
    andMore: "ملفات أخرى غير معروضة: {{count}}",
    openRegister: "فتح السجل",
    myWork: "عملي",
    myFiles: "ملفاتي",
    noFiles: "لا توجد ملفات مفتوحة مسندة إليك.",
    awaitingDecision: "بانتظار قراري",
    nothingAwaiting: "لا يوجد ما ينتظر قرارك.",
    approaching: "مواعيد تقترب",
    nothingApproaching: "لا توجد مواعيد نهائية خلال الأيام الخمسة القادمة.",
    workload: "توزيع العمل",
    workloadOpen: "الملفات المفتوحة",
    unassignedAlert: "ملفات عالية الأولوية غير مسندة إلى موظف: {{count}}",
    assign: "إسناد",
    pipeline: "مسار الطلبات",
    agentColumns: { agent: "الوكيل", runs: "التشغيلات", accepted: "المقبول" },
    ministerDecisions: "قرارات الوزير",
    proposalDecisions: "قرارات الموظفين على مقترحات الذكاء الاصطناعي",
    pendingProposals: "مقترحات بانتظار المراجعة",
    watchlistColumns: { case: "الملف", stage: "المرحلة", deadline: "الموعد", risk: "المخاطر" },
    title: "الرئيسية",
    subtitle: "عرض مباشر لحالات إدارة المنافسة والمدد القانونية ومساعدة الذكاء الاصطناعي.",
    kpis: "مؤشرات الأداء الرئيسية",
    funnel: "مسار الحالات",
    slaHeatmap: "خريطة المدد القانونية",
    aiAssist: "مساعدة الذكاء الاصطناعي",
    watchlist: "ملفات التركز الاقتصادي",
    proposalsApproved: "مقترحات معتمدة",
    proposalsEdited: "معدلة قبل الاعتماد",
    proposalsRejected: "مرفوضة",
    hoursSaved: "ساعات عمل موفرة تقديريا",
    target: "المستهدف",
    refreshed: "آخر تحديث",
    openCases: "الحالات المفتوحة",
    onTarget: "ضمن المستهدف",
    offTarget: "خارج المستهدف",
    kpi: {
      ecFormalReviewDays: "المراجعة الأولية للتركز (أيام)",
      ecDecisionDays: "من التقديم إلى قرار التركز (أيام)",
      complaintResolutionDays: "البت في الشكاوى (أيام)",
      slaCompliance: "الالتزام بالمدد القانونية",
      aiAcceptance: "قبول مسودات الذكاء الاصطناعي",
      hoursSaved: "ساعات العمل الموفرة",
      unnotifiedCandidates: "تركزات مرشحة غير مخطر بها",
    },
    dealWatchNew: "الجديد منذ الأمس: {{count}}",
    dealWatchOpen: "فتح مراقبة الصفقات",
    newsOpen: "فتح الأخبار",
    newsToday: "نشر اليوم: {{count}}",
    funnelHint: "التركزات الاقتصادية التي وصلت إلى كل مرحلة من مراحل الإجراء القانوني.",
    intake: "الطلبات المقدمة أسبوعيا",
    intakeHint: "الطلبات المقدمة أسبوعيا حسب الخدمة.",
    aiAssistHint: "القرارات البشرية على مقترحات الذكاء الاصطناعي، وقرارات الوزير، والتشغيلات لكل وكيل.",
    runs: "تشغيل",
    slaHeatmapHint: "المدد القانونية حسب النوع والحالة عبر جميع الملفات المتاحة لك.",
    health: { OnTrack: "ضمن المدة", AtRisk: "معرض", Breached: "متجاوز", Paused: "موقوف", CompletedOnTime: "في الموعد", CompletedLate: "متأخر" },
    watchlistHint: "الحالات المفتوحة مرتبة حسب مخاطر التركز وأقرب موعد قانوني.",
    recentActivity: "آخر النشاطات",
    liveAgents: "نشاط الوكلاء",
    liveAgentsHint: "بث مباشر لخطوات استدلال الوكلاء والاسترجاع واستدعاء الأدوات وضوابط الحماية.",
    liveAgentsIdle: "لا يعمل أي وكيل حاليا. تظهر الخطوات هنا مباشرة عندما يعمل وكيل على حالة.",
    recentRuns: "آخر التشغيلات",
    decisions: "القرارات",
    methodology: "المنهجية: تحسب المؤشرات من سجل الإجراءات والمدد القانونية للحالات المتاحة لك؛ وتقدر الساعات الموفرة بـ{{hours}} ساعة عمل لكل مسودة مقبولة. تتبع المستهدفات مستويات الخدمة في طلب تقديم العروض كما تنص عليها منهجية الحساب المنشورة.",
    nearestFirst: "الأقرب موعدا أولا",
    watchlistOrder: "الملفات المفتوحة · الأقرب موعدا أولا",
    byStage: "ملفات التركز حسب المرحلة",
    byStageMeta: "عدد الملفات في كل مرحلة اليوم",
    workloadAtRisk: "قرب الموعد",
    workloadPast: "بعد الموعد",
  },
  inbox: {
    focused: "يعرض المقترح الذي وصلت إليه عبر الرابط.",
    showAll: "عرض صندوق الوارد كاملا",
    focusMissing: "هذا المقترح غير متاح لك أو لم يعد موجودا.",
    avgEditDistance: "متوسط مسافة التعديل {{value}}%",
    activityHint: "يمكن اختيار نشاط الوكيل لأي مقترح لمتابعة كيفية إعداد مسودته: الاسترجاع، واستدعاء الأدوات، وضوابط الحماية.",
    title: "صندوق الذكاء الاصطناعي",
    subtitle: "كل مقترح ينتظر هنا قرارا بشريا. لا يرسل أو يغير شيء قبل اعتمادك.",
    empty: "لا توجد مقترحات قيد الانتظار. سيخطرك الوكلاء عند جاهزية مسودات جديدة.",
    filterAgent: "الوكيل",
    filterType: "نوع الملف",
    filterUrgency: "الأولوية",
    reviewNote: "ملاحظة المراجعة (اختياري)",
    diffTitle: "تعديلاتك مقابل مسودة الذكاء الاصطناعي",
  },
  cases: {
    title: "الحالات",
    registers: "السجلات",
    ecRegister: "سجل طلبات التركز الاقتصادي",
    complaintsRegister: "سجل شكاوى المنافسة",
    exemptionsRegister: "طلبات الإعفاء",
    appeals: "التظلمات",
    whistleblowing: "البلاغات وإشارات السوق",
    legalEnquiries: "الاستفسارات القانونية",
    columns: {
      number: "الملف",
      title: "العنوان",
      state: "المرحلة",
      assignee: "الموظف",
      clock: "الموعد",
      risk: "المخاطر",
      proposals: "ذ.ا",
      updated: "آخر تحديث",
    stage: "المرحلة",
    period: "المدة القانونية",
    lastChange: "آخر تغيير",
  },
    noPeriod: "لا مدة جارية",
    order: "الأقرب موعدا أولا ثم الأحدث",
    pausedBy: "أوقفها {{name}}",
    unattended: "لا موظف · المدة جارية",
  },
  sections: {
    ec: { requests: "الطلبات", screening: "فرز الطلبات الواردة", screeningPurpose: "الطلبات التي أشار اختبار السيطرة إلى أنها قد لا تستوجب الإخطار، ويلزم تأكيد إعادتها أو إحالتها إلى المراجعة الأولية." },
    investigations: { register: "سجل الشكاوى" },
  },
  parallelRun: {
    title: "التشغيل الموازي",
    hint: "الشرط 1.10: قبل الاعتماد تعمل المنصة إلى جانب الإجراء المعتاد للإدارة. ويسجل ما انتهى إليه الموظفون في كل نقطة بجانب نتيجة المنصة، ويقرر رئيس القسم في كل اختلاف. والملف ملف اختبار، فلا يصل إلى الأطراف شيء مما تفعله المنصة فيه.",
    liveFile: "ليس ملف اختبار",
    liveFileHint: "هذا الملف فعلي: ما تفعله المنصة فيه يصل إلى الأطراف. ولا ينضم ملف إلى التشغيل الموازي إلا بعد أن يصنفه المدير ملف اختبار.",
    platform: "ما تنتهي إليه المنصة الآن",
    platformResult: "المنصة",
    officers: "ما انتهى إليه الموظفون",
    points: "النقاط المقارنة",
    noPoints: "لم تقارن أي نقطة بعد.",
    record: "تسجيل نتيجة الموظفين في نقطة",
    point: "النقطة",
    customPoint: "نقطة أخرى يحددها الموظفون",
    customLabel: "النقطة",
    customPlatform: "ما تظهره المنصة فيها",
    source: "موضع تسجيل نتيجة الموظفين",
    sourceHint: "المذكرة أو قيد السجل أو الملاحظة في الإجراء المعتاد للإدارة.",
    recordAction: "تسجيل ومقارنة",
    recordedAgree: "سجلت النتيجة: تتفق نتيجة الموظفين مع نتيجة المنصة.",
    recordedDivergent: "سجلت النتيجة: تختلف النتيجتان، ويحال الاختلاف إلى رئيس القسم.",
    agree: "متفقتان",
    measure: "المدة المستغرقة",
    divergent: "اختلاف – لرئيس القسم",
    decide: "البت في الاختلاف",
    redecide: "البت مجددا",
    decided: "سجل القرار.",
    decidedBy: "بت فيه {{name}}",
    ruling: "النتيجة الصحيحة",
    rulings: { Platform: "نتيجة المنصة", Officers: "نتيجة الموظفين", Both: "النتيجتان مقبولتان", Neither: "لا هذه ولا تلك" },
    severity: "جسامة خطأ المنصة",
    severityHint: "جسيم أو رئيسي أو بسيط كما يعرفها الشرط 1.7، والاختلاف الجسيم يمنع اعتماد القسم.",
    severities: { Critical: "جسيم", Major: "رئيسي", Minor: "بسيط" },
    reasons: "الأسباب",
    rereadAction: "قراءة نتيجة المنصة مجددا",
    reread: "قرئت نتيجة المنصة مجددا.",
    none: "لا نتيجة بعد",
    notifiable: { Yes: "واجبة الإخطار", No: "غير واجبة الإخطار", Pending: "معلق: أرقام ناقصة" },
    register: {
      title: "التشغيل الموازي قبل الاعتماد",
      hint: "الشرط 1.10: ثمانية أسابيع على الأقل وخمسة عشر ملفا فعليا (أو كل الملفات الواردة في تلك المدة)، دون أي أثر على الأطراف. ولا يعتمد القسم إلا إذا كانت دقة المنصة تعادل دقة الموظفين على الأقل، ولم يبق اختلاف جسيم، وتحسن زمن التحليل أو عمقه.",
      empty: "لم ينضم أي ملف إلى التشغيل الموازي بعد. ويبدأ بأول نقطة تسجل على ملف اختبار.",
      weeks: "الأسابيع",
      files: "الملفات",
      platformAccuracy: "دقة المنصة",
      officersAccuracy: "دقة الموظفين",
      platformDays: "الوسيط الزمني للمنصة",
      officersDays: "الوسيط الزمني للموظفين",
      unmet: "ما ينقص الاعتماد",
      met: "يستوفي التشغيل الشروط التي يمكن قياسها",
      metHint: "يبقى الاعتماد قرار الإدارة، مع تقدير رئيس القسم لعمق التحليل.",
      file: "الملف",
      points: "النقاط",
      divergences: "حالات الاختلاف",
      undecided: "بانتظار القرار",
      critical: "الجسيمة القائمة",
      since: "في التشغيل منذ",
    },
  },
  statistics: {
    dataChecks: {
      title: "ملفات قد تشوه بياناتها هذه الأرقام: {{count}}",
      hint: "يراجع كل ملف قبل الاعتماد على الأرقام التي يؤثر فيها. والمدة التي لا يمكن أن تكون صحيحة تستبعد من متوسطها حتى يصحح الملف.",
      leftOut: "مستبعد حتى التصحيح",
      figures: { decisionOutcomes: "القرارات حسب النتيجة" },
    },
    sourcesHint: "تحتسب الأرقام التي تسجلها المنصة من ملفات القضايا، أما ما لا تسجله (الميزانية والموظفون والتفتيش المفاجئ والطعون القضائية والدعاوى الخاصة والتعاون الدولي والفعاليات) فتدخله الإدارة. يحل الرقم المدخل محل المحتسب ويحتفظ بكليهما. تشتق النسب المئوية من الأرقام المعروضة.",
    from: "من",
    to: "إلى",
    questionnairePeriod: "فترة الاستبيان",
    export: "تصدير جداول منظمة التعاون الاقتصادي والتنمية",
    year: "السنة",
    texts: {
      sectors: "القطاعات", otherMeans: "وسائل أخرى (ما هي)", withdrawalReasons: "أسباب السحب (7.4)", turnoverReasons: "أسباب الالتحاق والمغادرة",
      cooperationAuthorities: "الجهات والقضايا", refusalReasons: "أسباب رفض المزايا (8.3)", rejectionReasons: "أهم أسباب الرفض",
      exOfficioSources: "أهم مصادر المبادرة الذاتية", claimTypes: "أنواع الدعاوى ونتائجها",
    },
    value: "الرقم",
    note: "ملاحظة أو مصدر",
    clear: "استخدام الرقم المحتسب",
    saved: "تم حفظ الرقم",
    computedIs: "المحتسب من المنصة: {{value}}",
    computedWas: "المحتسب: {{value}}",
    editHint: "يرجى اختيار رقم لإدخاله أو تصحيحه",
    recordedFrom: "تحتفظ المنصة بالملفات منذ {{year}}؛ السنوات السابقة من الأرقام المدخلة فقط",
    noDecisions: "لا توجد قرارات في هذه السنوات",
    tabs: { operations: "العمليات", antitrust: "إنفاذ قواعد المنافسة", mergers: "التركز الاقتصادي", powers: "صلاحيات الإنفاذ", sanctions: "الجزاءات", advocacy: "السياسات", judicial: "المراجعة القضائية", institution: "المؤسسة" },
    legend: { Computed: "محتسب من الملفات", Entered: "أدخلته الإدارة", Derived: "مشتق من الأرقام المعروضة", Missing: "غير مسجل" },
    units: { days: "يوم", "%": "٪", h: "ساعة" },
    kpiBasis: {
      ecFormalReviewDays: "متوسط الأيام التقويمية من التقديم إلى القبول الشكلي ({{period.ec.initialReview}} أيام عمل قابلة للتمديد مرة واحدة).",
      ecDecisionDays: "متوسط الأيام التقويمية من التقديم إلى قرار معالي الوزير (مدة المراجعة النظامية {{period.ec.review}} يوما).",
      complaintResolutionDays: "متوسط الأيام التقويمية من تقديم الشكوى إلى إغلاقها (المستهدف: 3 أشهر).",
      slaCompliance: "نسبة المواعيد النظامية المكتملة التي أغلقت في موعدها أو قبله.",
      aiAcceptance: "نسبة مقترحات الذكاء الاصطناعي المراجعة التي اعتمدت كما هي أو بعد التعديل.",
      hoursSaved: "تقدير بواقع 2.5 ساعة عمل لكل مسودة معتمدة من الذكاء الاصطناعي.",
    },
    columns: {
      totalBudget: "إجمالي الميزانية (درهم)", competitionBudget: "ميزانية المنافسة (درهم)", totalStaff: "إجمالي الموظفين", competitionStaff: "موظفو المنافسة",
      lawyers: "قانونيون", economists: "اقتصاديون", other: "أخرى", mergersUnit: "التركز الاقتصادي", antitrustUnit: "التحقيقات", advocacyUnit: "السياسات والدراسات", otherUnits: "وحدات أخرى",
      joined: "الملتحقون", left: "المغادرون", turnoverRate: "معدل الدوران",
      mergerCooperation: "قضايا التركز", antitrustCooperation: "قضايا الممارسات", meetings: "الاجتماعات المحضورة", oecdSubmissions: "المساهمات في موائد المنظمة المستديرة",
      horizontalDays: "الأفقية", bidRiggingDays: "التواطؤ في العطاءات", verticalDays: "الرأسية", abuseDays: "إساءة استغلال الوضع المهيمن",
      total: "الإجمالي", complaint: "بناء على شكوى", leniency: "بناء على طلب تساهل", whistleblowing: "بناء على بلاغ", exOfficio: "بمبادرة من الوزارة", exOfficioShare: "نسبة المبادرة الذاتية",
      fine: "بفرض غرامة", noInfringement: "إغلاق دون مخالفة",
      simplified: "الإخطارات المبسطة", simplifiedShare: "نسبتها من الإخطارات",
      notifications: "الإخطارات", phase1: "موافقات بعد الدراسة المبسطة", phase1Remedies: "الدراسة المبسطة بشروط", phase2: "موافقات بعد الدراسة الموضوعية", phase2Remedies: "الدراسة الموضوعية بشروط", prohibitions: "حالات الرفض", withdrawn: "المسحوبة",
      reviewAverageDays: "حتى انتهاء الدراسة – المتوسط", reviewMaxDays: "حتى انتهاء الدراسة – الأقصى", decisionAverageDays: "من انتهاء الدراسة إلى القرار – المتوسط", decisionMaxDays: "من انتهاء الدراسة إلى القرار – الأقصى",
      averageDays: "الإجمالي – المتوسط", maxDays: "الإجمالي – الأقصى",
      applications: "قضايا بطلبات تساهل", multipleApplicants: "أكثر من مقدم طلب", benefitsGranted: "مزايا لجميع المتقدمين", benefitsRefused: "رفض المزايا",
      received: "الواردة", rejected: "المرفوضة", rejectionRate: "نسبة الرفض", rejectionsAppealed: "المرفوضة المتظلم منها", appealsSucceeded: "التظلمات الناجحة",
      cartel: "الاتفاقات المحظورة", abuse: "إساءة استغلال الوضع المهيمن",
      referrals: "الإحالة إلى النيابة", charged: "توجيه اتهامات جزائية", criminalSanctions: "عقوبات جزائية",
      horizontalSettlements: "أفقية – تسويات", horizontalCommitments: "أفقية – تعهدات", horizontalOther: "أفقية – أخرى",
      verticalSettlements: "رأسية – تسويات", verticalCommitments: "رأسية – تعهدات", verticalOther: "رأسية – أخرى",
      abuseSettlements: "إساءة استغلال – تسويات", abuseCommitments: "إساءة استغلال – تعهدات", abuseOther: "إساءة استغلال – أخرى",
      imprisonment: "قضايا بعقوبة الحبس", imprisonmentAppealed: "المطعون فيها", imprisonmentOverturned: "الملغاة في الطعن",
      studies: "دراسات السوق", opinions: "الآراء الرسمية", events: "الفعاليات المنظمة",
      studyRecommendations: "توصيات الدراسات", studyAdopted: "المعتمدة", studyAdoptionRate: "الدراسات – نسبة الاعتماد", opinionRecommendations: "توصيات الآراء", opinionAdopted: "المعتمدة", opinionAdoptionRate: "الآراء – نسبة الاعتماد",
      studyAverageDays: "متوسط المدة",
      decisions: "القرارات الصادرة", interimMeasures: "التدابير المؤقتة", grievances: "التظلمات", grievancesAccepted: "التظلمات المقبولة", courtAppeals: "الطعون القضائية", courtAppealRate: "نسبة الطعن",
      courtUpheld: "المؤيدة", courtPartial: "الملغاة جزئيا", courtAnnulled: "الملغاة",
      grievanceAverageDays: "التظلم", firstInstanceDays: "الدرجة الأولى", appealCourtDays: "الاستئناف", cassationDays: "النقض",
      claims: "الدعاوى المرفوعة", followOnClaims: "لاحقة لقرار", standaloneClaims: "مستقلة", claimsUpheld: "المقبولة", claimsDismissed: "المرفوضة",
    },
    tables: {
      q2_3_1: { title: "الميزانية والموظفون", hint: "إجمالي الميزانية بالدرهم والجزء المخصص للمنافسة وإجمالي الموظفين والعاملين في المنافسة." },
      q2_3_7: { title: "موظفو المنافسة حسب التخصص والقسم", hint: "العاملون في المنافسة حسب الخلفية الأكاديمية وحسب أقسام الإدارة." },
      q2_3_10: { title: "دوران الموظفين", hint: "موظفو المنافسة الملتحقون والمغادرون خلال السنة؛ معدل الدوران هو المغادرون منسوبين إلى موظفي المنافسة (2.3.1)." },
      q12_9: { title: "التعاون مع جهات المنافسة الأجنبية", hint: "القضايا التي تعاونت فيها الإدارة مع جهة منافسة أجنبية واحدة على الأقل، حسب النوع (تجيب أيضا عن 7.10 للتركز الاقتصادي)." },
      q3_7: { title: "متوسط مدة التحقيق", hint: "متوسط الأيام التقويمية من فتح التحقيق إلى القرار النهائي، حسب سنة القرار ونوع السلوك (3.7 و5.5 و6.6)." },
      q12_11: { title: "المحافل الدولية", hint: "اجتماعات منظمة التعاون الاقتصادي والتنمية والأونكتاد والشبكة الدولية للمنافسة المحضورة، والمساهمات في الموائد المستديرة." },
      q3_8: { title: "الاتفاقات الأفقية – التحقيقات المفتوحة", hint: "الملفات التي دخلت مرحلة التحقيق خلال السنة حسب طريقة فتحها." },
      q3_9: { title: "الاتفاقات الأفقية – القرارات النهائية", hint: "القرارات النهائية خلال السنة: بفرض غرامة (بما فيها التسويات) أو بالإغلاق دون مخالفة (بما فيها التعهدات)." },
      q4_7: { title: "التواطؤ في العطاءات – التحقيقات المفتوحة", hint: "الملفات التي دخلت مرحلة التحقيق خلال السنة حسب طريقة فتحها." },
      q4_8: { title: "التواطؤ في العطاءات – القرارات النهائية", hint: "القرارات النهائية خلال السنة." },
      q5_7: { title: "الاتفاقات الرأسية – التحقيقات المفتوحة", hint: "الملفات التي دخلت مرحلة التحقيق خلال السنة حسب طريقة فتحها." },
      q5_8: { title: "الاتفاقات الرأسية – القرارات النهائية", hint: "القرارات النهائية خلال السنة." },
      q6_8: { title: "إساءة استغلال الوضع المهيمن – التحقيقات المفتوحة", hint: "الملفات التي دخلت مرحلة التحقيق خلال السنة حسب طريقة فتحها." },
      q6_9: { title: "إساءة استغلال الوضع المهيمن – القرارات النهائية", hint: "القرارات النهائية خلال السنة." },
      q7_1_6: { title: "إجراء الإخطار المبسط", hint: "الإخطارات المنتهية بالإجراء المبسط (دون التصعيد إلى دراسة كاملة) حسب سنة الإخطار، ونسبتها من جميع الإخطارات (7.2)." },
      q7_2: { title: "قرارات التركز الاقتصادي", hint: "الإخطارات الواردة والقرارات الصادرة خلال السنة، بحسب الدراسة التي خضع لها كل ملف. أسباب السحب تجيب عن 7.4." },
      q7_3: { title: "مدة مراجعة التركز الاقتصادي", hint: "الأيام التقويمية من الإخطار الأولي إلى انتهاء الدراسة (رفع الملف إلى صاحب القرار)، ومن ثم إلى القرار، وإجمالا." },
      q8_2: { title: "طلبات التساهل ومزاياه", hint: "القضايا التي قدمت فيها طلبات تساهل، والقضايا بأكثر من مقدم طلب، ومنح المزايا لجميع المتقدمين، وأسباب رفضها (8.3)." },
      q8_10: { title: "الشكاوى الواردة والمرفوضة", hint: "الشكاوى الواردة والمرفوضة ونسبة الرفض وأهم أسبابه، والمرفوضة المتظلم منها ونتيجة التظلم." },
      q8_11: { title: "مصادر التحقيقات", hint: "جميع التحقيقات المفتوحة خلال السنة أيا كان السلوك حسب المصدر، ونسبة ما فتح بمبادرة من الوزارة وأهم مصادره." },
      q8_14: { title: "التفتيش المفاجئ", hint: "القضايا التي نفذ فيها تفتيش مفاجئ حسب نوع السلوك." },
      q8_19: { title: "الإنفاذ الجزائي", hint: "الملفات المحالة إلى النيابة العامة (المادة 33)، والقضايا التي وجهت فيها اتهامات جزائية، والقضايا التي فرضت فيها عقوبات جزائية." },
      q10_8: { title: "التسويات والتعهدات والإجراءات التفاوضية الأخرى", hint: "القضايا المنتهية بتسوية أو تعهدات أو إجراء تفاوضي آخر حسب نوع السلوك والأداة." },
      q10_9: { title: "عقوبة الحبس", hint: "القضايا التي فرضت فيها عقوبة الحبس، والمطعون فيها، والملغاة في الطعن." },
      q13_2: { title: "دراسات السوق", hint: "دراسات السوق التي أصدرتها الوزارة خلال السنة – ملفات دراسات السوق المعتمدة للنشر – وقطاعاتها." },
      q13_3: { title: "الآراء الرسمية في السياسات", hint: "الآراء المقدمة للجهات الحكومية المعتمدة خلال السنة وقطاعاتها." },
      q13_5: { title: "اعتماد التوصيات", hint: "التوصيات الواردة في دراسات السوق وفي آراء السياسات، ونسبة ما اعتمدته الجهات التنظيمية أو التشريعية." },
      q13_7: { title: "مدة دراسات السوق", hint: "متوسط الأيام التقويمية من التكليف إلى النشر لدراسات السوق المنجزة خلال السنة." },
      q13_9: { title: "فعاليات السياسات", hint: "ورش العمل والتدريب والحملات التي نظمتها الإدارة (دون الفعاليات التي شارك فيها الموظفون متحدثين فقط)." },
      q14_8: { title: "الطعون", hint: "القرارات الصادرة والتدابير المؤقتة، والتظلمات منها (المادة 34)، والطعون أمام المحاكم منسوبة إلى القرارات، ونتائجها." },
      q14_8d: { title: "مدة الطعون", hint: "متوسط الأيام التقويمية للتظلم ولكل درجة من درجات التقاضي." },
      q11_6: { title: "الإنفاذ الخاص", hint: "دعاوى المنافسة الخاصة المرفوعة أمام المحاكم، لاحقة لقرار أو مستقلة، ونتائجها." },
    },
    lists: {
      q3_10: "الاتفاقات الأفقية – قرارات المخالفة",
      q4_9: "التواطؤ في العطاءات – قرارات المخالفة",
      q5_9: "الاتفاقات الرأسية – قرارات المخالفة",
      q6_10: "إساءة استغلال الوضع المهيمن – قرارات المخالفة",
      q7_6: "عمليات التركز المرفوضة أو الموافق عليها بشروط",
      q10_8: "القضايا المنتهية بتسوية أو تعهدات",
      q10_10: "المخالفات الإجرائية (بما فيها رقابة التركز الاقتصادي)",
      q13_2: "دراسات السوق المنجزة",
      q13_3: "آراء السياسات الرسمية الصادرة",
    },
    noItems: "لا شيء في هذه السنوات",
    decisionColumns: { number: "رقم القرار", year: "السنة", conduct: "نوع السلوك", companies: "الشركات المعنية", sanction: "الجزاء", fine: "الغرامة (درهم)", outcome: "النتيجة", parties: "أطراف التركز", remedies: "الشروط", instrument: "الأداة" },
    itemColumns: { year: "السنة", title: "العنوان", sector: "القطاع", duration: "المدة", addressee: "الجهة الموجه إليها" },
    sanctions: { Fine: "غرامة", Warning: "إنذار", Referral: "إحالة إلى النيابة العامة", Orders: "أوامر", Finding: "ثبوت المخالفة", Settlement: "تسوية", Commitments: "تعهدات", Remedies: "موافقة بشروط", Prohibition: "رفض" },
    methodology: {
      antitrust: "مجموعات السلوك: الاتفاقات الأفقية تشمل الاتفاقات المحظورة وتقاسم الأسواق والاتفاقات المقيدة؛ والرأسية تشمل فرض أسعار إعادة البيع والتعامل الحصري؛ وإساءة استغلال الوضع المهيمن تشمل التسعير الافتراسي والتبعية الاقتصادية ورفض التعامل والربط. يحتسب التحقيق مفتوحا عند دخول الملف مرحلة التحقيق. الإنذار يثبت المخالفة دون غرامة، لذا يحتسب في الإجمالي فقط.",
      phasesAwaiting: "اعتبار الدراسة المبسطة المرحلة الأولى في الاستبيان والدراسة الموضوعية المرحلة الثانية بانتظار اعتماد الإدارة في سجل القواعد القانونية.",
      phasesApproved: "اعتمدت الإدارة اعتبار الدراسة المبسطة المرحلة الأولى في الاستبيان والدراسة الموضوعية المرحلة الثانية.",
      mergers: "تحسب الموافقات بحسب الدراسة التي خضع لها كل ملف: المبسطة أو الموضوعية. ويملأ التصدير عمودي المرحلة الأولى والثانية في الاستبيان منهما. لا تشمل الإخطارات الطلبات التي تبين في الفرز أنها غير خاضعة لوجوب الإخطار. وتحتسب مدة الدراسة من تاريخ الإخطار الأولي.",
    },
  },
  caseAssistant: {
    button: "سؤال المساعد",
    title: "المساعد · {{caseNumber}}",
    description: "تستند الإجابات إلى هذا الملف ومعارف الإدارة؛ وأي إجراء يغير الملف ينتظر موافقتك. وتستمر المحادثة نفسها في المساعد الذكي.",
    newConversation: "بدء محادثة جديدة",
    openInWorkspace: "فتح في المساعد الذكي",
    starting: "جار فتح محادثتك حول هذا الملف…",
    failed: "تعذر فتح المحادثة",
  },
  // S 2.4, S 6.3, S 6.4: where each filed value comes from, the amounts as filed in their currency, and the sales test that could not run.
  filedValues: {
    title: "مصدر كل قيمة في النموذج",
    hint: "كل قيمة مع مصدرها: مستند في الملف (الصفحة والنص) أو المحادثة أو من أدخلها. وتعلم القيمة المنقولة من خانة أخرى أو التي لا مصدر لها أو التي لا يذكرها نصها؛ ويظهر للمبلغ بعملة أخرى سعر صرفه ومصدره وتاريخه.",
    none: "لا توجد قيم في النموذج بعد.",
    value: "القيمة",
    source: "المصدر",
    kinds: { Document: "مستند في الملف", Applicant: "أدخلها مقدم الطلب", Conversation: "ذكرت في المحادثة", Officer: "أدخلها موظف" },
    noSource: "لا مصدر مسجل",
    page: "ص. {{page}}",
    passageNotFound: "لم يعثر على النص في المستند",
    passageDoesNotState: "النص لا يذكر هذه القيمة",
    market: "السوق: {{market}}",
    originalAmount: "مقدمة بمبلغ {{original}}",
    published: "سعر منشور",
    turnoverMissing: "المبيعات في السوق المعنية غير مسجلة",
    turnoverMissingHint: "حجم السوق غير مسجل: {{markets}}",
    noMarket: "لم تعلن أي سوق معنية",
  },
  workspace: {
    breadcrumb: "الموقع",
    testFile: "ملف اختبار – كما في {{date}}",
    testMode: {
      action: "تصنيف الملف ملف اختبار",
      hint: "إعادة نظر قضية سابقة أو تشغيل سيناريو معد",
      title: "تصنيف هذا الملف ملف اختبار",
      body: "ملف الاختبار لا ينشر ولا يدخل في الإحصاءات ولا يستشهد به سابقة، ولا ترسل منه رسائل بريد إلكتروني أو رسائل نصية، ولا يظهر في البوابة إلا للحساب الذي قدمه. لا يمكن إلغاء التصنيف، ويرفض بعد صدور قرار في الملف.",
      asOf: "إعادة النظر كما في",
      asOfHint: "لن يقرأ الذكاء الاصطناعي إلا النصوص القانونية والسوابق الصادرة في هذا التاريخ أو قبله.",
      reason: "الغرض من الاختبار وما ستقارن به نتيجته",
      submit: "تصنيفه ملف اختبار",
      done: "صنف الملف ملف اختبار.",
    },
    administrativeRecord: {
      action: "تصدير السجل الإداري",
      hint: "ملف ZIP للتظلم أو الطعن: المستندات والخطابات والمسودات والأرقام والاعتمادات والقرارات وسجل التدقيق",
      preparing: "جار تجميع السجل الإداري…",
      done: "تم تصدير السجل الإداري للملف {{caseNumber}}.",
      doneHint: "يفتح الملف المضغوط على صفحة فهرسه. وتسجل في سجل التدقيق بصمة رقمية لقائمة محتوياته، التي تبين بصمة كل ملف فيه، فيظهر أي تعديل لاحق.",
      failed: "تعذر تصدير السجل الإداري",
    },
    testFileHint: "ملف يشغل لاختبار المنصة. لا ينشر قراره ولا يدخل في الإحصاءات ولا يستشهد به سابقة، ولا يقرأ الذكاء الاصطناعي فيه إلا النصوص والسوابق الصادرة حتى هذا التاريخ.",
    fineAed: "مبلغ التصالح أو الغرامة القضائية (درهم)",
    settlementReached: "تم التصالح وفق المادة 33(2) (المبلغ هو مبلغ التصالح)",
    closedWithCommitments: "أغلق الملف بناء على تعهدات المنشآت",
    stepTaken: "تم تسجيل الخطوة",
    decisionNotYet: "لا يوجد قرار بعد",
    decisionNotYetHint: "يعد القرار ويصدر عند وصول الملف إلى مرحلة القرار.",
    stageOf: "المرحلة {{n}} من {{total}}",
    awaitingRole: "بانتظار {{role}}",
    procedureGuide: "دليل الإجراء",
    details: "التفاصيل",
    moreActions: "إجراءات أخرى",
    undo: {
      action: "التراجع عن الخطوة الأخيرة",
      nothing: "لا توجد خطوة يمكن التراجع عنها",
      confirmTitle: "التراجع عن الخطوة الأخيرة؟",
      confirmBody: "يعود الملف من «{{from}}» إلى «{{to}}». تعاد فتح المواعيد والمهام التي أغلقتها الخطوة، ويسجل التراجع في السجل.",
      note: "سبب التراجع",
      done: "تم التراجع عن الخطوة الأخيرة",
      failed: "تعذر التراجع عن الخطوة",
      fromToast: "تراجع مباشرة بعد اختتام المرحلة",
      reasons: {
        NoStep: "لا توجد خطوة للتراجع عنها",
        WorkflowChanged: "تغير الإجراء منذ هذه الخطوة",
        Submission: "لا يمكن التراجع عن تقديم الطلب",
        Automatic: "لا يمكن التراجع عن الخطوات التلقائية",
        NotActor: "يتراجع عن الخطوة من اتخذها أو المدير فقط",
        Window: "يمكن التراجع خلال 24 ساعة من الخطوة",
        DecisionIssued: "صدر قرار منذ هذه الخطوة",
        LetterSent: "أرسل خطاب إلى الأطراف منذ هذه الخطوة",
      },
    },
    refer: {
      action: "الإحالة إلى قسم آخر",
      hint: "يفتح ملفا مستقلا مرتبطا في ذلك القسم",
      title: "الإحالة إلى قسم آخر",
      body: "يعمل القسم الآخر على الموضوع في ملف مستقل مرتبط بمراحله الخاصة، ويحتفظ هذا الملف بمساره.",
      target: "القسم ونوع الملف",
      reason: "سبب الإحالة",
      submit: "فتح الملف المرتبط",
      done: "تم فتح الملف المرتبط {{caseNumber}}",
      sections: { EconomicConcentration: "التركز الاقتصادي", Investigations: "التحقيقات والشكاوى", Advocacy: "السياسات", Requests: "طلبات أخرى", Leadership: "القيادة" },
    },
    linked: {
      title: "الملفات المرتبطة",
      hint: "الملفات المحالة من هذا الملف أو إليه",
      relation: { Parent: "محال من", Referral: "محال إلى", Related: "مرتبط" },
    },
    guard: "شرط",
    guardSatisfied: "مستوفى",
    guardNotSatisfied: "غير مستوفى",
    guardUnmet: "شرط غير مستوفى: {{condition}}",
    fieldEn: "{{field}} بالإنجليزية",
    fieldAr: "{{field}} بالعربية",
    noticeDraft: "تلقت وزارة الاقتصاد والسياحة إخطارا بعملية التركز الاقتصادي «{{title}}». ولكل ذي مصلحة أن يقدم ملاحظاته أو اعتراضه خلال مدة النشر.",
    roles: "الأدوار",
    anyStaff: "أي موظف",
    overview: "نظرة عامة",
    workflow: "الإجراء",
    parties: "الأطراف والمنشآت",
    documents: "المستندات",
    analysis: "التحليل",
    investigationReport: "التقرير",
    correspondence: "المراسلات",
    tasks: "المهام والاجتماعات",
    reports: "التقارير",
    decision: "القرار",
    audit: "سجل التدقيق",
    agents: "نشاط الوكلاء",
    aiSummary: "ملخص الملف بالذكاء الاصطناعي",
    riskGauge: "مخاطر التركز",
    explainability: "قابلية التفسير",
    nextSteps: "الخطوات التالية",
    runAgent: "تشغيل الوكيل",
    transition: "المضي في الإجراء",
    publication: "النشر والاعتراضات",
    procedure: "الإجراء",
    remedies: "التعهدات",
    investigation: "لوحة التحقيق",
    report: "البلاغ",
    enforcement: "الإنفاذ",
    enquiry: "الاستفسار",
    exemption: "دراسة الاستثناء",
    amendment: "دراسة التعديل",
    appeal: "التظلم",
    noSummary: "لم يلخص وكيل الفرز هذا الملف بعد. ويمكن تشغيله من قائمة الوكلاء أو انتظار تشغيله الآلي.",
    formallyAccepted: "القبول الشكلي",
    clocks: "المدد القانونية",
    extend: "تمديد",
    extendHint: "يسمح بتمديد حتى {{max}} يوما إضافيا بموجب",
    extensionDays: "مدة التمديد (أيام)",
    justification: "المبررات",
    clockExtended: "تم تمديد المدة",
    paused: "موقوفة",
    pausedSince: "موقوفة منذ {{date}} – تستأنف عند استيفاء مقدم الطلب للمتطلبات",
    humanTasks: "المهام البشرية",
    taskCompleted: "تم إتمام المهمة",
    journal: "سجل الإجراء",
    noDocuments: "لا توجد مستندات في هذا الملف بعد.",
    docSize: "{{kb}} كيلوبايت",
    docPages_zero: "لا صفحات",
    docPages_one: "صفحة واحدة",
    docPages_two: "صفحتان",
    docPages_few: "{{count}} صفحات",
    docPages_many: "{{count}} صفحة",
    docPages_other: "{{count}} صفحة",
    docVersion: "الإصدار {{version}}",
    docPageOf: "الصفحة {{page}} · المقطع {{n}}",
    docInternal: "داخلي",
    docLanguage: { ar: "بالعربية", en: "بالإنجليزية", other: "بلغة أخرى" },
    // كيف قرئ نص المستند (الدراسة الموضوعية 6.12): الصفحات والنسخ الممسوحة والصفحات غير المقروءة والقيم الواجب التحقق منها.
    documentReading: {
      checks: "فحوص المستند",
      noChecks: "لا ملاحظات على هذا المستند.",
      pages: "عدد صفحات الملف: {{count}}",
      scanned: "نسخت من الصورة: {{count}}",
      unread: "لم تقرأ: {{count}}",
      partly: "قرئت جزئيا: {{count}}",
      notStored: "لم تحفظ نصا: {{count}}",
      scanTag: "نسخة ممسوحة",
      unreadTag: "صفحات لم تقرأ",
      toCheck: "للتحقق",
      onPage: "الصفحة {{page}}",
      scannedPage: "صفحة ممسوحة",
      notFound: "لم ترد في النص كما قرئ",
      lowConfidence: "ثقة منخفضة",
      toCheckNote: "القيم المعلمة للتحقق قرئت من صفحة ممسوحة أو بدرجة ثقة منخفضة، ويتحقق منها مقابل المستند.",
      aiReading: "قراءة آلية",
      aiReadingLegacy: "قرأ الذكاء الاصطناعي في المنصة هذا المستند قبل أن تسجل القراءة النموذج الذي أجراها؛ أعد معالجة المستند لتسجيله ثم التحقق منه.",
      instructionVersion: "إصدار التعليمات {{version}}",
      transcriptionVersion: "إصدار تعليمات نسخ الصفحات الممسوحة {{version}}",
      notChecked: "الملخص والحقول قراءة الذكاء الاصطناعي للمستند، ولم يتحقق منها أي موظف مقابل المستند بعد.",
      checkedBy: "تحقق منها {{name}} مقابل المستند بتاريخ {{date}}.",
      check: "تسجيل أنني تحققت منها",
      checkRecorded: "سجل تحققك من القراءة في الملف.",
    },
    // المستند وترجمته (الدراسة الموضوعية 6.13): النصان متجاوران والاختلافات معلمة وقرار الموظف المختص.
    documentTranslation: {
      tab: "الترجمة",
      none: "لا توجد ترجمة لهذا المستند في الملف.",
      linkLabel: "هذا المستند ترجمة لـ",
      choose: "اختيار الأصل…",
      link: "ربطه بوصفه ترجمة",
      unlink: "إلغاء وصفه ترجمة",
      linked: "تم ربط الترجمة، وتجري مقارنة النصين الآن.",
      original: "الأصل",
      translation: "الترجمة",
      arabic: "العربي",
      english: "الإنجليزي",
      inferred: "تبين أنهما بند واحد من قائمة المستندات مقدم بلغتين.",
      arabicPrevails: "تشريع أو مستند صادر عن الوزارة: يعتد بالنص العربي دون الترجمة.",
      referred: "مستند مقدم من طرف تختلف ترجمته عن أصله: أحيل إلى الموظف المختص ليقرر النص المعتمد مع بيان الأسباب.",
      decided: "قرر {{name}} بتاريخ {{date}}: {{decision}}.",
      reasons: "الأسباب",
      decisionLabel: "النص المعتمد",
      decision: { arabic: "النص العربي هو المعتمد", english: "النص الإنجليزي هو المعتمد", corrected: "لا هذا ولا ذاك: تقدم ترجمة مصححة" },
      reasonsHint: "تسجل في سجل الملف مع القرار.",
      decide: "تسجيل القرار",
      saved: "سجل القرار في سجل الملف.",
      differences_zero: "لا اختلافات",
      differences_one: "اختلاف واحد",
      differences_two: "اختلافان",
      differences_few: "{{count}} اختلافات",
      differences_many: "{{count}} اختلافا",
      differences_other: "{{count}} اختلاف",
      noDifferences: "تتطابق الأرقام والتواريخ والمواد والأسماء والمصطلحات المعرفة.",
      kind: { figure: "رقم", date: "تاريخ", article: "مادة", name: "اسم", term: "مصطلح معرف" },
      page: "الصفحة {{page}}",
      pages: "الصفحة {{ar}} من العربي · الصفحة {{en}} من الإنجليزي",
      article: "المادة {{article}}",
      whole: "النص كاملا",
      empty: "لا شيء بهذه اللغة هنا.",
    },
    reprocess: "إعادة قراءة المستند",
    reprocessQueued: "تمت جدولة إعادة تحليل المستند",
    extraction: "الحقول المستخرجة",
    fullText: "النص",
    redactedView: "عرض النص بعد إخفاء البيانات الشخصية (كما رآه النموذج)",
    processingDoc: "لا يزال تحليل المستند جاريا.",
    injectionFlags: "رصد فحص حقن التعليمات",
    thresholdTest: "اختبار الحدود (قرار مجلس الوزراء رقم 3 لسنة 2025)",
    thresholdBasis: "حاسبات حتمية – المبيعات، الحصة السوقية، مؤشر هيرفندال-هيرشمان، نسبة تركز أكبر أربع منشآت – محسوبة من بيانات الإخطار.",
    relevantMarkets: "الأسواق المعنية ومؤشرات التركز",
    band: "نطاق التركز",
    scorecard: "درجة الفرز البنيوية (إشارة لترتيب الأولويات)",
    scorecardHint: "إشارة فرز توزع جهد الفحص ولا تقوم دليلا على الضرر: تنسب كل نقطة إلى عامل بنيوي مع مبرر. أما التوصية فهي حكم الخبير على ضوء معيار الإثبات في التقرير.",
    noAnalysis: "لم يعمل وكيل تحديد السوق والتركز بعد.",
    noRisk: "لا توجد درجة فرز بعد",
    dominancePost: "وضع مسيطر بعد العملية",
    theoriesOfHarm: "نظريات الضرر",
    countervailing: "العوامل الموازنة",
    letters: "الخطابات والإشعارات",
    lettersHint: "تعد من قوالب ثنائية اللغة. ولا يرسل خطاب ذو أثر قانوني إلا بعد أن يؤكده موظف مخول، وما أعد دون هذا التأكيد ينتظر هنا.",
    held: {
      badge: "بانتظار تأكيدكم",
      title: "لم يرسل هذا الخطاب",
      hint: "للخطاب أثر قانوني وقد أعد دون تأكيد موظف مخول. يرجى قراءته كاملا ثم تأكيده لإرساله الآن، أو إلغاؤه.",
      confirm: "تأكيد وإرسال",
      cancel: "إلغاء الخطاب",
      reason: "سبب الإلغاء (اختياري)",
      confirmed: "تم تأكيد الخطاب وإرساله",
      cancelled: "ألغي الخطاب ولم يرسل",
      failed: "تعذر تأكيد الخطاب",
      openFailed: "لم يسجل اطلاعك على هذا الخطاب، فلا يمكن تأكيده بعد",
    },
    // Scenario 6.36: a letter the confidentiality screen held, with what it found, until the passages are removed or the basis for disclosure is recorded.
    screened: {
      badge: "أوقفه فحص السرية",
      title: "قد يكشف هذا الخطاب عن معلومات سرية",
      hint: "رصد فحص السرية المقاطع التالية في الخطاب أو مرفقاته. تحذف المقاطع ويحفظ الخطاب – فيعاد فحصه – أو يسجل موظف مخول سند الإفصاح عنها، ثم يمكن تأكيده.",
      kinds: { Passage: "مقطع من مستند سري", Figure: "رقم من مستند سري", Marked: "موسوم بالسرية", Email: "عنوان بريد إلكتروني", Phone: "رقم هاتف", Document: "مستند سري مرفق", Identity: "يكشف هوية الشاكي" },
      where: { letter: "في الخطاب", arabic: "في النص العربي", attachment: "في المرفق {{name}}" },
      edit: "تعديل الخطاب",
      bodyEn: "النص الإنجليزي",
      bodyAr: "النص العربي",
      save: "حفظ وإعادة الفحص",
      saved: "حفظ الخطاب وأعيد فحصه",
      stillFound_zero: "لم يعد الفحص يرصد أي مقطع",
      stillFound_one: "ما زال الفحص يرصد مقطعا واحدا",
      stillFound_two: "ما زال الفحص يرصد مقطعين",
      stillFound_few: "ما زال الفحص يرصد {{count}} مقاطع",
      stillFound_many: "ما زال الفحص يرصد {{count}} مقطعا",
      stillFound_other: "ما زال الفحص يرصد {{count}} مقطع",
      clear: "لم يعد فحص السرية يرصد شيئا في هذا الخطاب.",
      basis: "سند الإفصاح",
      basisPlaceholder: "الموافقة الكتابية لصاحب المعلومة وتاريخها، أو سبب عدم سرية المقطع",
      recordBasis: "تسجيل السند",
      basisRecorded: "سجل سند الإفصاح",
      recordedBy: "سجل السند {{name}}",
      basisOnlyAuthorised: "يسجل سند الإفصاح رئيس قسم الشكاوى أو المدير أو من يعلوهما.",
    },
    rfis: "طلبات استكمال المعلومات",
    rfisHint: "طلبات استكمال النواقص والاستيضاح مع مدد رد قانونية وتذكيرات دورية.",
    reminders: "التذكيرات",
    awaitingResponse: "بانتظار رد مقدم الطلب",
    satisfied: "مستوفى",
    notSatisfied: "غير مستوفى",
    publicRegister: "إشعار السجل العام",
    publicRegisterHint: "يفتح النشر في جدول الطلبات المستلمة مهلة المرئيات والاعتراضات التي تحددها المادتان 15 و16 من اللائحة التنفيذية، ويبين النشر يوم انتهائها. ولا يصدر أي قرار قبل انتهاء تلك المهلة.",
    publish: "نشر الإشعار",
    publishHint: "تنشر المعلومات غير السرية فقط. تستثنى بيانات الأطراف والأسواق السرية.",
    published: "تم نشر الإشعار",
    windowCloses: "تنتهي النافذة في {{date}}",
    windowClosesLabel: "انتهاء النافذة",
    windowOpen: "مفتوحة",
    publicLink: "السجل العام",
    copyPublicLink: "نسخ الرابط",
    previewPublicLink: "معاينة",
    publicLinkCopied: "تم نسخ رابط السجل العام",
    publishedAt: "تاريخ النشر",
    notPublished: "لم ينشر بعد. ويكون النشر بعد قيد الملف وموافقة المدير.",
    submissions: "المشاركات",
    interestedParties: "آراء الأطراف ذوي المصلحة",
    outsideWindow: "خارج النافذة",
    weighHint: "توزن المذكرات ولا تعد: سجل لكل مذكرة الأدلة التي تقدمها والمصلحة التي تخدمها. تنقل الأسباب إلى قسم آراء الأطراف الثالثة في التقرير.",
    weighPrinciple: "المنافس الذي يعترض على انخفاض الأسعار يحمي نفسه لا المنافسة. يتبع الوزن الأدلة – المستندات والبيانات والوقائع القابلة للتحقق – لا عدد المذكرات.",
    weighSubmission: "وزن المذكرة",
    reweighSubmission: "إعادة الوزن",
    recordWeight: "تسجيل الوزن",
    submissionWeighed: "تم وزن المذكرة",
    evidentialWeight: "الوزن الإثباتي",
    interestServed: "المصلحة التي تخدمها",
    whyAcceptedOrDiscounted: "أسباب القبول أو الاستبعاد",
    weighReasoningHint: "تذكر هنا الأدلة التي تقدمها المذكرة (أو تفتقر إليها) وسبب قبول الشاغل أو استبعاده. تنقل هذه العبارة نصا إلى التقرير.",
    weighedBy: "وزنها {{name}} · {{date}}",
    weights: { Unassessed: "غير مقيم", None: "لا وزن", Low: "منخفض", Medium: "متوسط", High: "عال" },
    decisionPublication: {
      title: "النسخة العلنية من القرار",
      hint: "تصاغ من السجل – النتيجة والإجراءات والأسباب والشروط ورأي اللجنة – مع حجب أرقام الإيرادات والحصص والأسعار الدقيقة والأطراف الثالثة. ثم تراجع المحذوفات، ويحرر النص، وينشر في سجل القرارات.",
      publishedBadge: "منشور", draftBadge: "مسودة",
      review: "مراجعة ونشر", revise: "تنقيح", publish: "نشر في السجل", republish: "نشر التنقيح", resetToDraft: "إعادة الصياغة من السجل",
      saved: "حفظت المسودة.", published: "نشر القرار في السجل.",
      staleTitle: "تغير السجل منذ إعداد هذه المسودة", stale: "تغير القرار أو شروطه أو رأي اللجنة بعد المسودة. وتلزم إعادة صياغتها قبل النشر.",
      outcome: "النتيجة", decidedOn: "صدر في", reviewDays: "المراجعة (أيام)", simplified: "إجراء مبسط", redactionCount: "المقاطع المحجوبة",
      redactions: "مواضع الحجب التي طبقتها المنصة: {{count}}",
      redactionKinds: { Turnover: "إيرادات", Share: "حصة سوقية", Amount: "مبلغ", Percentage: "نسبة", Contact: "بيانات اتصال", ThirdParty: "طرف ثالث", Party: "طرف", Name: "اسم" },
      caseHint: "أعدت المسودة من القرار – النتيجة والأسباب والأوامر والإجراءات المتبعة – مع حجب المبيعات وحدود الغرامة والمبالغ الأخرى. تنشر الغرامة المفروضة، ولا يسمى المشتكي ولا مصدر التحقيق أبدا.",
      fine: "الغرامة المفروضة", provision: "النص المطبق",
      notNamed: "المنشآت غير مسماة", notNamedHint: "ينشر خطاب الإنذار وطلب تحريك الدعوى الجزائية وإنهاء التحقيق الذي فتحته الإدارة من تلقاء نفسها دون تسمية المنشآت.",
      publisherOnly: "ينشر القرار رئيس قسم الشكاوى أو المدير؛ ويمكنك إعداد النص وحفظه.",
      reasons: "الأسباب", reasonsHint: "التسبيب العلني: تحديد السوق، والأدلة المعتمدة، وملاحظات الأطراف وكيف أجيب عليها، ولماذا تعد الشروط متناسبة. لا تدرج الأسرار التجارية.",
      conditions: "الشروط", conditionsHint: "شرط في كل سطر، كما ينبغي أن يقرأه الجمهور.",
      redactionNotes: "بيان الحجب", redactionNotesHint: "بيان ما حجب وعلى أي أساس؛ ينشر هذا البيان مع القرار.",
      preparedBy: "أعده {{name}} · {{date}}", publishedBy: "نشره {{name}} · {{date}}",
    },
    exPost: {
      tab: "التقييم اللاحق",
      title: "التقييم اللاحق للقرار",
      hint: "ما توقعه التقرير لكل سوق – البنية والأسعار والدخول – وما كان على الالتزامات تحقيقه، مقروءا مقابل بنك البيانات بعد القرار. ويستكمل بحكم ونتائج ودروس تأخذ بها سياسة التحليل.",
      none: "لا تقييم لاحق مجدول", noneHint: "يجدول التقييم عند إغلاق الملف المقررة.",
      status: { Scheduled: "مجدول", Read: "مقروء", Completed: "مكتمل" },
      verdicts: { AsPredicted: "كما هو متوقع", BetterThanPredicted: "أفضل من المتوقع", WorseThanPredicted: "أسوأ من المتوقع", Inconclusive: "غير حاسم", NoData: "لا بيانات" },
      entryReadings: { Likely: "محتمل", Unlikely: "غير محتمل", NotAssessed: "غير مقيم" },
      read: "قراءة بنك البيانات", reread: "إعادة قراءة بنك البيانات", readDone: "قرئ بنك البيانات مقابل توقعات التقرير.",
      complete: "تسجيل التقييم", completed: "سجل التقييم ودروسه على صفحة سياسة التحليل.", record: "تسجيل",
      decision: "القرار", dueAt: "الاستحقاق", inDays: "خلال {{count}} يوم", inDays_zero: "اليوم", inDays_one: "خلال يوم واحد", inDays_two: "خلال يومين", inDays_few: "خلال {{count}} أيام", inDays_many: "خلال {{count}} يوما", inDays_other: "خلال {{count}} يوم", overdueDays: "متأخر {{count}} يوم", overdueDays_zero: "مستحق اليوم", overdueDays_one: "متأخر يوما واحدا", overdueDays_two: "متأخر يومين", overdueDays_few: "متأخر {{count}} أيام", overdueDays_many: "متأخر {{count}} يوما", overdueDays_other: "متأخر {{count}} يوم", lastRead: "آخر قراءة", efficiencies: "مكاسب الكفاءة المدعاة", cognisable: "معتبرة",
      suggested: "الحكم المقترح", notRead: "لم يقرأ بنك البيانات لهذا القرار بعد. وتلزم قراءته لمقارنة ما حدث في السوق بما توقعه التقرير.",
      hhiPredicted: "مؤشر هيرفندال-هيرشمان متوقع", hhiObserved: "مؤشر هيرفندال-هيرشمان ملاحظ", sharePredicted: "حصة المندمجة المتوقعة", shareObserved: "حصة المندمجة الملاحظة", pricePredicted: "أثر السعر المحاكى", priceObserved: "تغير السعر الملاحظ", entry: "الدخول", verdict: "الحكم",
      commitments: "الالتزامات", suggestedLessons: "دروس مقترحة لسياسة التحليل",
      findings: "النتائج", findingsHint: "ما فعلته السوق مقابل ما توقعه التقرير، سوقا بسوق، وهل صمدت الالتزامات.",
      policyLessons: "دروس لسياسة التحليل", policyLessonsHint: "ما ينبغي أن تأخذه افتراضات الهامش والاستعادة والدخول والتدابير من هذا الملف – أو أن لا شيء يحتاج إلى تغيير، ولماذا.",
      completedBy: "أكمله {{name}} · {{date}}",
    },
    interests: { Unassessed: "لم تقرأ المصلحة", ProtectsCompetition: "حماية المنافسة", ProtectsCompetitor: "حماية منافس", Neutral: "محايد" },
    objections: "الاعتراضات",
    objectionsHint: "تتطلب الاعتراضات سداد الرسم وقرار قبول شكلي خلال {{period.ec.objectionAdmissibility}} أيام عمل؛ ويخطر المعترض بالنتيجة؛ ويرسل الاعتراض المقبول إلى كل طرف بصيغة غير سرية للرد خلال {{period.ec.objectionReply}} أيام عمل (المادة 16).",
    admissibilityDue: "موعد القبول الشكلي",
    partiesReplyDue: "موعد رد الأطراف",
    repliedInTime: "رد في الموعد",
    repliedLate: "رد متأخر",
    admissibility: "القبول الشكلي",
    partiesReply: "رد الأطراف",
    outcome: "النتيجة",
    decideAdmissibility: "البت في القبول الشكلي",
    resolveObjection: "الفصل في الاعتراض",
    objectionDecided: "تم تحديث الاعتراض وإخطار الأطراف عند الاقتضاء",
    reasoning: "الحيثيات",
    admissible: "مقبول شكلا",
    inadmissible: "غير مقبول شكلا",
    uphold: "قبول الاعتراض",
    dismiss: "رفض الاعتراض",
    nonConfidentialGrounds: "أسباب الاعتراض بصيغة غير سرية للأطراف",
    nonConfidentialGroundsAr: "الأسباب بصيغة غير سرية (بالعربية، اختياري)",
    nonConfidentialHint: "ترسل إلى كل طرف في التركز إذا قبل الاعتراض. حذفت من المسودة الأرقام والمبالغ وبيانات الاتصال؛ ويحذف كل ما قدم على سبيل السرية. ولا يرسل الاعتراض كما قدم.",
    objectorReasonsHint: "يخطر المعترض بالنتيجة، وبهذه الأسباب في حالة الرفض (المادة 16/3).",
    reasoningAr: "الأسباب (بالعربية، اختياري)",
    objectorTold: "أخطر المعترض {{date}}",
    partiesSent_zero: "لم يرسل إلى أي طرف",
    partiesSent_one: "أرسل إلى طرف واحد",
    partiesSent_two: "أرسل إلى طرفين",
    partiesSent_few: "أرسل إلى {{count}} أطراف",
    partiesSent_many: "أرسل إلى {{count}} طرفا",
    partiesSent_other: "أرسل إلى {{count}} طرف",
    recordSilence: "تسجيل عدم الرد",
    silenceFrom: "لا رد – الصمت اعتبارا من {{date}}",
    silenceRecorded: "سجل الصمت: تستأنف الدراسة على ما هو ثابت في الملف (المادة 16/4/ج).",
    noticeDraftLoaded: "معد من الإخطار: الأطراف والعملية وكل سوق معلنة، دون أرقام.",
    reportsHint: "التقرير مع التوصية، وموجز القرار، وتقارير الوكلاء الأخرى المعتمدة لهذا الملف.",
    noReports: "لا يوجد تقرير بعد.",
    conditions: "الشروط",
    conditionsHint: "شرط واحد في كل سطر.",
    decidedBy: "صادر عن",
    issuedAt: "تاريخ الإصدار",
    signature: "التوقيع الإلكتروني",
    appealDeadline: "آخر موعد للتظلم",
    notifiedAt: "تم إخطار الأطراف",
    noticesTitle: "الإخطار لكل طرف",
    noticesHint: "يتلقى كل طرف له الحق في الإخطار إخطارا خاصا به؛ ويسري موعد كل طرف من تاريخ إخطاره، وينتقل إلى يوم الاستلام متى ثبت.",
    deliveryStatus: { Delivered: "أرسل بالبريد الإلكتروني", Failed: "تعذر البريد الإلكتروني – المنصة فقط", PortalOnly: "المنصة فقط" },
    receivedOn: "استلم {{date}}",
    receiptNotProven: "الاستلام غير مثبت",
    deadlineOn: "الموعد {{date}}",
    fromSending: "محسوب من الإرسال",
    recordReceipt: "تسجيل الاستلام",
    receiptDate: "تاريخ الاستلام",
    receiptNote: "طريقة التسليم",
    receiptRecorded: "سجل الاستلام؛ ويسري موعد الطرف منه",
    grievanceEffect: "أثر القبول على القرار",
    effectWithdrawn: "سحب القرار",
    effectAmended: "تعديل القرار",
    amendedTerms: "الشروط المعدلة (بالإنجليزية)",
    amendedTermsAr: "الشروط المعدلة (بالعربية)",
    amendedTermsHint: "تقتبس في الإخطار الموجه إلى المتظلم وإلى الأطراف الأخرى في القرار الأصلي.",
    notifyParties: "إخطار الأطراف",
    partiesNotified: "تم إرسال إشعارات القرار",
    issueDecision: "إصدار القرار",
    issueDecisionHint: "يقيد التوقيع القرار في السجل ويبدأ مدة التظلم.",
    issueDecisionHintEc: "يصدر القرار خلال مدة الدراسة البالغة {{period.ec.review}} يوما، ويخطر الطرفان خلال {{period.ec.notifyDecision}} أيام.",
    deciderOnly: "يصدر القرار من الموقع المختص به: معالي الوزير للقرارات الوزارية، ووكيل الوزارة للقرار الإداري بعدم استيفاء شروط المادة (12).",
    notInDecisionState: "الملف في مرحلة {{state}}. تقدم في الإجراء إلى مرحلة القرار أولا.",
    prefillFromBrief: "تعبئة من موجز القرار",
    signAndIssue: "توقيع وإصدار",
    decisionIssued: "تم توقيع القرار وقيده",
    auditTrail: "سجل التدقيق",
    auditHint: "كل حدث مرتبط بتجزئة SHA-256 بالحدث السابق ويبث إلى نظام SIEM. يمكن إعادة التحقق في أي وقت.",
    verifyChain: "إعادة الفحص",
    chainValid: "السجل كامل ولم يعدل",
    chainValidHint: "فحص {{count}} قيد.",
    chainGap: "بعض الأحداث تنتمي إلى سلاسل متداخلة من حالات أخرى.",
    chainInvalid: "{{count}} قيد عدلت بعد تسجيلها",
    chainInvalidHint: "يبلغ مسؤول المنصة بذلك.",
    actor: "الفاعل",
    action: "الإجراء",
    entity: "الكيان",
    auditDetailsInConsole: "يطلع مسؤولو المنصة على كل قيد في إدارة المنصة.",
    extendedBy: "بتمديد {{days}}",
    warningSent: "أرسل إنذار", recipients: { applicant: "مقدم الطلب", department: "إدارة المنافسة", complainant: "المشتكي", whistleblower: "المبلغ" },
    auditSeverity: "الخطورة",
    agentRuns: "تشغيلات الوكلاء",
    piiRedacted: "رموز البيانات الشخصية المخفاة",
    piiHint: "تستبدل الأسماء والهويات وبيانات الاتصال برموز مستعارة قبل أي استدعاء للنموذج.",
    noRuns: "لم يعمل أي وكيل على هذا الملف بعد.",
    runTools_one: "استدعاء أداة واحد",
    runTools_two: "استدعاءان للأدوات",
    runTools_few: "{{count}} استدعاءات للأدوات",
    runTools_many: "{{count}} استدعاء للأدوات",
    runTools_other: "{{count}} استدعاء للأدوات",
    runSources_one: "مصدر واحد",
    runSources_two: "مصدران",
    runSources_few: "{{count}} مصادر",
    runSources_many: "{{count}} مصدرا",
    runSources_other: "{{count}} مصدر",
    runFailed: "لم يكتمل التشغيل",
    reviewStage: "مرحلة المراجعة",
    processPosition: "موقع الطلب في الإجراء",
    phaseOf: "المرحلة {{n}} من {{total}}",
    phases: {
      title: "مراحل الإجراء",
      intake: "الاستلام",
      review: "الدراسة",
      publication: "النشر",
      assessment: "التقييم",
      decision: "القرار",
      closure: "الإغلاق",
    },
    phaseStanding: { done: "منجزة", current: "جارية", ahead: "قادمة", skipped: "خارج مسار هذا الطلب" },
    stageStanding: { visited: "منجزة", current: "المرحلة الحالية", ahead: "قادمة", bypassed: "خارج مسار هذا الطلب" },
    stagesDone: "الخطوات المنجزة: {{done}} من {{total}}",
    extendable: "قابلة للتمديد {{days}} يوما",
    automaticTransition: "انتقال آلي",
    guardUnknown: "لم يقيم لدورك",
    primaryClock: "رئيسية",
    processMap: "خريطة الإجراء",
    processGuide: "دليل الإجراء",
    processGuideHint: "الوصف المكتوب لهذا الإجراء – المراحل والجهات والمهل والبوابات والمخططات (PDF، يفتح في علامة تبويب جديدة).",
    stepRequires: "متطلبات هذه الخطوة",
    responsibleRoles: "الأدوار المسؤولة",
    forms: "النماذج",
    openWorkflow: "عرض الإجراء",
    stepProposals: "مقترحات الذكاء الاصطناعي لهذه الخطوة",
    noStepProposals: "لا توجد مقترحات لهذه الخطوة بعد. ويمكن تشغيل وكيل أو المضي في الإجراء.",
    recommendedAction: "الإجراء الموصى به",
    noTransition: "لا يوجد انتقال متاح لدورك في هذه الخطوة.",
    thisStep: "هذه الخطوة",
    completeness: "اكتمال الملف",
    mandatoryPresent: "بنود إلزامية متوفرة",
    requiredDocumentsOnFile: "المستندات المطلوبة في الملف: {{present}} من {{total}}",
    atSubmission: "عند التقديم",
    readinessAtSubmission: "درجة الجاهزية عند التقديم",
    fileComplete: "الملف مكتمل",
    itemsToResolve: "{{count}} بند بحاجة إلى معالجة", itemsToResolve_zero: "لا بنود بحاجة إلى معالجة", itemsToResolve_one: "بند واحد بحاجة إلى معالجة", itemsToResolve_two: "بندان بحاجة إلى معالجة", itemsToResolve_few: "{{count}} بنود بحاجة إلى معالجة", itemsToResolve_many: "{{count}} بندا بحاجة إلى معالجة", itemsToResolve_other: "{{count}} بند بحاجة إلى معالجة",
    readinessOnly: "قائمة تحقق آلية — لم يفحص وكيل المراجعة الأولية هذا الملف بعد.",
    runFormalReview: "تشغيل المراجعة الأولية",
    formalReviewQueued: "تم إدراج وكيل المراجعة الأولية؛ تتحدث القائمة عند انتهائه.",
    reviewProposal: "مراجعة المقترح",
    consistencyFindings: "ملاحظات الاتساق",
    optional: "اختياري",
    requestText: "الطلب",
    openDocument: "عرض المستند",
    deficiencyDrafted: "تمت صياغة إخطار النواقص بهذه البنود وهو بانتظار اعتمادك.",
    completenessStatus: { Present: "متوفر", Deficient: "غير مستوف", Missing: "مفقود", ToFollow: "يستكمل لاحقا", NotRequired: "غير مطلوب" },
    overriddenCount: "بنود بقرار من موظف: {{count}}",
    overrideBadge: "قرار الموظف",
    resolveItem: "معالجة",
    resolveItemHint: "إرفاق مستند أو ربطه أو إصدار قرار بشأن هذا البند",
    resolve: {
      title: "معالجة بند من قائمة التحقق",
      platformSays: "رأي المنصة",
      notOnChecklist: "هذا البند غير موجود في قائمة التحقق الخاصة بالملف.",
      upload: "رفع",
      link: "ربط مستند",
      mark: "إصدار قرار",
      uploadHint: "يرفع المستند مباشرة في هذا البند. يصنف ضمن {{category}}، وتعاد حساب قائمة التحقق عند حفظ الملف.",
      uploadAction: "رفع في هذا البند",
      uploaded: "تم رفع المستند وتصنيفه ضمن البند",
      linkHint: "صنف مستندا موجودا في الملف ضمن هذا البند. يحتفظ المستند بسجله، ويدون النقل في الجدول الزمني.",
      alreadyHere: "مصنف ضمن هذا البند بالفعل",
      currentlyUnder: "مصنف حاليا ضمن: {{slot}}",
      notLinked: "غير مرتبط بأي بند في قائمة التحقق",
      linkAction: "تصنيف ضمن هذا البند",
      linked: "تم تصنيف المستند ضمن البند",
      ruling: "القرار",
      present: "اعتباره متوفرا",
      presentHint: "المتطلب مستوفى — في الملف، أو بصيغة لم تتعرف عليها المنصة.",
      notRequired: "غير مطلوب في هذا الملف",
      notRequiredHint: "لا ينطبق البند هنا. البنود الإلزامية تحتاج إلى سبب يصمد في السجل.",
      reason: "السبب",
      reasonHint: "الحد الأدنى {{min}} أحرف. ويدون في الجدول الزمني للحالة وفي سلسلة التدقيق باسم المستخدم.",
      reasonPlaceholder: "ما الذي تحققت منه، ولماذا لا يصمد ما وجدته المنصة…",
      auditNote: "قرار الموظف يعلو على نتيجة المنصة وعلى أي تقييم من الوكلاء. يبقى ظاهرا على البند ويمكن إلغاؤه في أي وقت؛ ويعيد الإلغاء رأي المنصة.",
      markAction: "تسجيل القرار",
      marked: "تم تسجيل القرار — أعيد حساب قائمة التحقق",
      revoke: "إلغاء القرار",
      revokeReason: "سبب سحب القرار (اختياري)",
      confirmRevoke: "تأكيد الإلغاء",
      revoked: "ألغي القرار — عاد رأي المنصة إلى السريان",
    },
    qualification: "هل تشكل الصفقة تركزا اقتصاديا؟",
    qualificationHint: "اختبار السيطرة الحتمي وفق المادة 1 من المرسوم بقانون اتحادي رقم 36 لسنة 2023، مع حدود الإخطار. القواعد تقرر؛ والوكلاء يقدمون الأدلة فقط.",
    verdict: { Constitutes: "تشكل تركزا", DoesNotConstitute: "لا تشكل تركزا", Borderline: "حالة حدية – تستلزم الفحص", Unassessed: "لم تقيم" },
    notificationRequired: "الإخطار مطلوب",
    notificationNotRequired: "الإخطار غير مطلوب",
    flaggedForScreening: "محالة إلى الفحص",
    controlTest: "اختبار السيطرة",
    noControlTest: "لم يقدم مقدم الطلب مدخلات اختبار السيطرة بعد.",
    missingInputs: "معلومات ما زالت مطلوبة من مقدم الطلب",
    changeOfControl: "انتقال السيطرة",
    turnoverThreshold: "المبيعات في السوق المعنية فوق {{threshold}}",
    turnoverThresholdHint: "المبيعات المجمعة للأطراف في كل سوق معلنة (الحصة × حجم السوق). حسابات المجموعة السنوية هي أساس الرسم فقط.",
    marketSizeMissing: "حجم السوق غير مسجل",
    shareThreshold: "حصة الأطراف فوق {{threshold}}%",
    met: "متحقق",
    notMet: "غير متحقق",
    notificationPending: {
      short: "قيد الانتظار",
      hint: "لا يجرى اختبار المادة 3 دون رقمه: تبقى الحالة قيد الانتظار حتى يسجل لكل سوق معلنة حجمها وحصص الأطراف فيها. ولا يقرأ الرقم الناقص أبدا على أنه دون الحد.",
    },
    applicantNotice: "الإشعار المعروض لمقدم الطلب",
    acknowledgedBy: "أقر به {{name}} بتاريخ {{date}}",
    acknowledgedHint: "أكد مقدم الطلب فهمه بأن الإخطار لا يبدو مطلوبا وأن الرسم غير قابل للاسترداد، واختار التقديم على أي حال.",
    applicantContests: "يعترض مقدم الطلب على التصنيف الآلي ({{date}}) ويطلب مراجعة بشرية",
    declaredInputs: "المدخلات المصرح بها",
    dataCompleteness: { Full: "مدخلات كاملة", Partial: "مدخلات جزئية", Minimal: "مدخلات محدودة" },
    acquiredStake: "الحصة المستحوذ عليها",
    preStake: "الحصة قبل الصفقة",
    postStake: "الحصة بعد الصفقة",
    largestOther: "أكبر مساهم آخر",
    controlType: "السيطرة المصرح بها",
    controlTypes: { Unknown: "غير مصرح", None: "لا سيطرة", Sole: "سيطرة منفردة", Joint: "سيطرة مشتركة" },
    rights: "الحقوق الممنوحة",
    controlRights: { BoardMajority: "أغلبية مجلس الإدارة", VetoStrategic: "حق النقض على القرارات الاستراتيجية", CastingVote: "الصوت المرجح", ManagementContract: "عقد إدارة", OptionToAcquireControl: "خيار اكتساب السيطرة", MinorityProtectionOnly: "حقوق حماية الأقلية فقط" },
    fullFunctionJv: "مشروع مشترك كامل الوظائف",
    assetsAreBusiness: "الأصول تشكل نشاطا تجاريا",
    intraGroup: "إعادة هيكلة داخل المجموعة",
    controlStructureNote: "وصف مقدم الطلب لهيكل السيطرة",
    aiDraftBy: "مسودة الذكاء الاصطناعي · {{agent}}",
    extractionNote: "قرئت آليا من المستند، ويتحقق منها مقابل النص قبل الاعتماد عليها.",
    noneFound: "لم يرد شيء",
    notRecorded: "غير مسجل",
    chainValidHint_zero: "لا قيود",
    chainValidHint_one: "فحص قيد واحد.",
    chainValidHint_two: "فحص قيدان.",
    chainValidHint_few: "فحص {{count}} قيود.",
    chainValidHint_many: "فحص {{count}} قيدا.",
    chainValidHint_other: "فحص {{count}} قيد.",
    chainInvalid_zero: "لا قيود معدلة",
    chainInvalid_one: "قيد واحد عدل بعد تسجيله",
    chainInvalid_two: "قيدان عدلا بعد تسجيلهما",
    chainInvalid_few: "{{count}} قيود عدلت بعد تسجيلها",
    chainInvalid_many: "{{count}} قيدا عدلت بعد تسجيلها",
    chainInvalid_other: "{{count}} قيد عدلت بعد تسجيلها",
  },
  stage: {
    steps: "مراحل الملف",
    stepOf: "المرحلة {{n}} من {{total}}",
    owner: "المسؤول",
    notReached: "لم يبلغها الملف بعد",
    notOnPath: "خارج مسار هذا الملف",
    panels: { file: "ملف الطلب", history: "السجل" },
    recordOf: "سجل مرحلة {{phase}}",
    recordHint: "ما انتهت إليه هذه المرحلة، ومن أنهاها، ومتى.",
    recordEmpty: "لم تسجل في هذه المرحلة مراجعة موقعة.",
    recordExits: "كيف انتهت المرحلة",
    actor: { Human: "موظف", Agent: "أحد مساعدي المنصة", System: "سجل المنصة" },
    left: {
      title: "ما بقي",
      count_zero: "لا بنود",
      count_one: "بند واحد",
      count_two: "بندان",
      count_few: "{{count}} بنود",
      count_many: "{{count}} بندا",
      count_other: "{{count}} بند",
      done_zero: "لا بنود منجزة",
      done_one: "بند واحد منجز",
      done_two: "بندان منجزان",
      done_few: "{{count}} بنود منجزة",
      done_many: "{{count}} بندا منجزا",
      done_other: "{{count}} بند منجز",
      none: "لا شيء متبق عليك في هذه المرحلة.",
      noneCanConclude: "أنجزت البنود كلها، ويمكن إنهاء المرحلة.",
      blocking: "لازم",
      criteria_zero: "لا معايير للبت فيها",
      criteria_one: "معيار واحد للبت فيه",
      criteria_two: "معياران للبت فيهما",
      criteria_few: "{{count}} معايير للبت فيها",
      criteria_many: "{{count}} معيارا للبت فيها",
      criteria_other: "{{count}} معيار للبت فيها",
      showAll: "عرض الكل ({{count}})",
      showFewer: "عرض أقل",
      taskFailed: "تعذر إنجاز المهمة",
      kinds: { Criterion: "معيار للبت فيه", Issue: "مسألة للمعالجة", Document: "مستند ناقص أو معيب", Task: "مهمة", Draft: "مسودة للمراجعة", Condition: "شرط من شروط الإجراء" },
      actions: { Criterion: "البت", Issue: "فتح", Document: "معالجة", Task: "إنجاز", Draft: "مراجعة" },
    },
    primary: {
      go: "التالي: {{title}}",
      here: "الخطوة التالية: {{title}}",
      issue: "معالجة المسألة: {{title}}",
      conclude: "إنهاء المرحلة",
      waiting: "بانتظار الخطوة التالية من الإجراء",
      waitingFor: "بانتظار: {{condition}}",
      awaiting: "بانتظار: {{role}}",
      anotherRole: "موظف آخر",
      closed: "هذا الملف مغلق",
      held: {
        declare: "الخطوة التالية: التصريح بالمصالح في هذا الملف",
        pending: "بانتظار قرار المدير في المصلحة المصرح بها",
        recused: "تمت التنحية عن هذا الملف، فلا يمكن اتخاذ أي إجراء فيه",
      },
    },
  },
  review: {
    sourceKinds: { Document: "مستند", Proposal: "مسودة الذكاء الاصطناعي", Tool: "فحص المنصة", Clock: "مدة قانونية", Task: "مهمة", Citation: "مرجع قانوني", Letter: "خطاب", Meeting: "اجتماع", Rfi: "طلب معلومات", Submission: "مرئيات" },
    tab: "المراجعة",
    fileTab: "ملف الطلب",
    historyTab: "السجل",
    stageReviews: "مراجعات المراحل",
    visit: "الزيارة {{n}}",
    criteria: "معايير المراجعة",
    required: "إلزامي",
    recommended: "مستحسن",
    optional: "اختياري",
    undecided: "لم يبت فيه بعد",
    decidedOf: "تم البت في {{done}} من {{total}}",
    overrodeAi: "حالات تجاوز تقييم الذكاء الاصطناعي: {{count}}",
    surfaces: { file: "فتح ملف الطلب", analysis: "فتح التحليل", decision: "فتح القرار", history: "فتح الإجراء" },
    aiAssessment: "ما وجدته المنصة",
    basis: { Deterministic: "محرك القواعد", Agent: "مسودة الوكيل", Mixed: "قواعد + وكيل", label: "صفة مقدم الطلب", Member: "عضو في المنشأة", Mandate: "ممثل بموجب تفويض" },
    references_one: "المرجع",
    references_other: "المراجع ({{count}})",
    discuss: {
      title: "ناقش مع الوكيل",
      subtitle: "أسئلة عن أي جانب من هذا المقترح",
      why: "ما سبب هذا الحكم؟",
      raise: "ما الذي يرفع درجة ثقتك؟",
      weakest: "ما أضعف الأدلة؟",
      address: "ما المسائل التي يتناولها الاستنتاج؟",
      placeholder: "سؤال عن هذا المقترح…",
      send: "إرسال",
      stop: "إيقاف",
      agent: "وكيل المراجعة",
      thinking: "جارٍ الاطلاع على الملف",
      copy: "نسخ",
      copied: "تم النسخ",
      restart: "محادثة جديدة",
      failed: "تعذر على الوكيل الإجابة",
      none: "لم تُطرح أسئلة حول هذا المقترح.",
    },
    confidence: "الثقة",
    confidenceHint: "درجة ثقة المنصة في هذا الحكم: {{pct}}%",
    readingBasis: "هذه القراءة {{meaning}}: {{pct}}%",
    manualOnly: "لا يوجد تقييم آلي لهذا المعيار.",
    proposalAwaiting: "مسودة بانتظار قرارك",
    acceptAi: "قبول التقييم",
    acceptedAi: "تم قبول تقييم الذكاء الاصطناعي",
    acceptAiHint: "تسجيل حكم المنصة كما هو كنتيجة لك",
    aiAgreesHint: "هذا هو حكم المنصة",
    yourFinding: "نتيجتك",
    verdicts: { Satisfied: "مستوف", Deficient: "غير مستوف", NeedsInformation: "يحتاج إلى معلومات", NotApplicable: "غير منطبق", Unknown: "لا رأي", Pending: "لم يبت فيه" },
    hotkeyVerdicts: "للبت",
    hotkeyNote: "ملاحظة",
    hotkeyRecord: "تسجيل",
    disagreeTitle: "وجدت المنصة \"{{ai}}\" — يذكر سبب الاختلاف.",
    note: "ملاحظة المراجع",
    response: "ردك",
    noResponse: "لم يكتب رد — بقي تقييم المنصة كما سجل.",
    startFromPlatform: "البدء من نص المنصة",
    startFromPlatformHint: "ينسخ ما وجدته المنصة إلى ردك لتحتفظ به أو تصححه أو تعيد كتابته",
    notePlaceholder: "نتيجتك بكلماتك…",
    notePlaceholderDisagree: "لماذا تخالف نتيجتك تقييم المنصة…",
    resetRationale: "إعادة الحيثيات المعبأة مسبقا",
    evidence: "الأدلة",
    pinEvidence: "تثبيت كدليل لنتيجتك",
    unpinEvidence: "إلغاء التثبيت",
    attachDocument: "إرفاق مستند",
    recordFinding: "تسجيل النتيجة",
    recordNewVersion: "تسجيل كالإصدار v{{version}}",
    findingRecorded: "تم تسجيل النتيجة كالإصدار v{{version}}",
    findingCleared: "تم مسح النتيجة",
    clearFinding: "مسح النتيجة",
    unsavedChanges: "تغييرات غير مسجلة",
    moreActions: "إجراءات أخرى",
    copyLink: "نسخ رابط هذا المعيار",
    linkCopied: "تم نسخ الرابط",
    refresh: "تحديث التقييم",
    refreshHint: "تشغيل {{agent}} مجددا على هذا الطلب؛ يتحدث التقييم عند الانتهاء",
    refreshQueued: "تم إدراج الوكيل — يتحدث التقييم عند الانتهاء",
    draftOf: "مسودة بتاريخ {{date}}",
    liveFromFile: "محسوب مباشرة من ملف الطلب",
    sources: "المصادر ({{count}})",
    pinHint: "تثبيت مصدر للاستشهاد به في النتيجة",
    showAll: "عرض جميع المصادر ({{count}})",
    showFewer: "عرض أقل",
    sourceStatus: { Linked: "مرتبطة", FailureToNotifyCandidate: "احتمال عدم الإخطار", StandstillBreachCandidate: "احتمال الإتمام قبل الموافقة", NotYetDue: "لم يحن موعدها", OutOfScope: "خارج النطاق", Constitutes: "تشكل تركزا", DoesNotConstitute: "لا تشكل تركزا", Borderline: "حالة حدية", Met: "مستوفاة", NotMet: "غير مستوفاة", HighlyConcentrated: "شديدة التركز", ModeratelyConcentrated: "متوسطة التركز", Unconcentrated: "غير مركزة", Concern: "مصدر قلق", Watch: "تحت المراقبة", BelowWatch: "دون حد المراقبة", Complete: "مكتمل", Gaps: "ثغرات", Declared: "معلن", Evidenced: "مثبت", Cognisable: "معتبرة", NotCognisable: "غير معتبرة", ViewIssued: "صدر الرأي", Pending: "قيد الانتظار", Requested: "مطلوب", Qualifies: "مؤهلة", Fails: "غير مؤهلة", Consulted: "تم التشاور", NotConsulted: "لم يتم التشاور", Planned: "مخطط", Filed: "مقدم", UnderReview: "قيد المراجعة", OpinionRequested: "طلب الرأي", Cleared: "موافقة", ClearedWithConditions: "موافقة بشروط", Prohibited: "رفض", NotRequired: "غير مطلوب", Replied: "ورد الرد", Elapsed: "انقضت المدة", Awaiting: "بانتظار الرد", Proposed: "مقترح", Approved: "معتمد", Edited: "عدل واعتمد", Rejected: "مرفوض", Superseded: "مستبدل", Expired: "منتهي", Concur: "يوافق", ConcurWithReservations: "يوافق مع تحفظات", Dissent: "يخالف", Draft: "مسودة", Published: "منشور", Horizontal: "أفقي", Vertical: "رأسي", Conglomerate: "تكتلي", Paid: "مدفوع", Unpaid: "غير مدفوع", Applicant: "مقدم الطلب", AcquiringParty: "الطرف المستحوذ", TargetParty: "الطرف المستحوذ عليه", Representative: "الممثل", Complainant: "المشتكي", Respondent: "المشتكى عليه", OnTrack: "ضمن المهلة", AtRisk: "معرضة للتأخر", Breached: "تجاوزت المهلة", Supports: "يؤيد", Neutral: "محايد", Contradicts: "يعارض", Sent: "مرسل", Delivered: "مسلم", Read: "مقروء", Open: "مفتوحة", Closed: "مغلقة", Responded: "ورد الرد", RepliedLate: "ورد الرد متأخرا", MinutesApproved: "المحضر معتمد", NoMinutes: "بلا محضر", Admissible: "مقبول شكلا", Inadmissible: "غير مقبول شكلا", Held: "عقد", Notified: "أخطر", NotNotified: "لم يخطر", Completed: "مكتملة", Lapsed: "منقضية", Match: "مطابقة للسجل", NameMismatch: "اختلاف في الاسم", Inactive: "غير سارية", NotFound: "غير موجودة في السجل", Unavailable: "تعذر الوصول إلى السجل", Overdue: "انقضت المهلة", Paused: "متوقفة", Submitted: "مقدم", AwaitingApplicant: "بانتظار مقدم الطلب", Accepted: "مقبول", Investigation: "قيد التحقيق", Decision: "بانتظار القرار", Decided: "صدر فيه قرار", Suspended: "معلق", Withdrawn: "مسحوب" },
    verdictHints: { Satisfied: "المتطلب مستوف في الملف", Deficient: "المتطلب غير مستوف", NeedsInformation: "لا يمكن البت بما في الملف", NotApplicable: "لا ينطبق على هذا الطلب" },
    filter: { label: "تصفية المعايير", all: "الكل", awaiting: "تنتظرك", disagree: "تخالف الذكاء الاصطناعي", empty: "لا يوجد معيار يطابق هذه التصفية." },
    hotkeys: "لوحة المفاتيح",
    voice: { ai: "المنصة", officer: "أنت" },
    sourcesToResolve: "بحاجة إلى معالجة: {{count}}",
    resolveFromHere: "معالجة هذا البند — رفع مستند أو ربطه أو إصدار قرار",
    versions: {
      title: "سجل الإصدارات",
      view: "سجل الإصدارات",
      count: "{{count}} إصدار", count_zero: "لا إصدارات", count_one: "إصدار واحد", count_two: "إصداران", count_few: "{{count}} إصدارات", count_many: "{{count}} إصدارا", count_other: "{{count}} إصدار",
      currentIs: "الحالي v{{version}}",
      current: "الحالي",
      versionN: "الإصدار {{version}}",
      empty: "لم تسجل نتيجة على هذا المعيار بعد.",
      kind: { Recorded: "مسجل", Updated: "محدث", Cleared: "ممسوح", Restored: "مستعاد" },
      fields: { Verdict: "الحكم", Note: "الرد", Evidence: "الأدلة" },
      changed: "تغير",
      compareWith: "مقارنة مع",
      previous: "الإصدار السابق",
      currentVersion: "الإصدار الحالي",
      aiAtTime: "الذكاء الاصطناعي حينها",
      recordedBy: "تسجيله",
      restoredFrom: "مستعاد من v{{version}}",
      noEvidence: "لا أدلة مثبتة.",
      immutableHint: "لا تحرر الإصدارات ولا تحذف. الاستعادة تكتب إصدارا جديدا؛ ويبقى السجل كاملا.",
      restore: "استعادة v{{version}}",
      restoreHint: "جعل الإصدار {{version}} النتيجة الحالية مجددا — يسجل كإصدار جديد مع تقييم الذكاء الاصطناعي الحالي.",
      restored: "تمت الاستعادة — سجلت كالإصدار v{{version}}",
    },
    previous: "السابق",
    next: "التالي",
    move: "تنقل",
    raiseIssue: "تسجيل مسألة",
    raiseIssueHint: "متابعة نقص أو سؤال أو مخاطرة أو إجراء لاحق على هذا المعيار أو على المرحلة كاملة. والمسائل المانعة توقف إنهاء المرحلة.",
    issues: "المسائل",
    noIssues: "لا مسائل مفتوحة على هذا الطلب.",
    issueKindLabel: "النوع",
    issueKind: { Deficiency: "نقص", Question: "سؤال", Risk: "مخاطرة", FollowUp: "متابعة" },
    severityLabel: "الخطورة",
    severity: { Low: "منخفضة", Medium: "متوسطة", High: "عالية", Blocking: "مانعة" },
    blockingHint: "لا يمكن إنهاء المرحلة ما دامت هذه المسألة مفتوحة.",
    criterion: "المعيار",
    wholeStage: "المرحلة كاملة",
    detail: "التفاصيل",
    issueRaised: "تم تسجيل المسألة",
    issueUpdated: "تم تحديث المسألة",
    issueStatus: { Open: "مفتوحة", Resolved: "محلولة", Dismissed: "مستبعدة" },
    resolve: "حل",
    dismiss: "استبعاد",
    reopen: "إعادة فتح",
    resolution: "الحل",
    resolveHint: "كيف سويت النقطة (اختياري).",
    dismissHint: "لماذا لا تستوجب النقطة المتابعة.",
    showClosed: "عرض المغلقة ({{count}})",
    hideClosed: "إخفاء المغلقة ({{count}})",
    concludeStage: "إنهاء المرحلة",
    concludeReady: "لكل معيار إلزامي نتيجة، ولا مسألة مانعة مفتوحة.",
    concludeHint: "يحدد هنا مسار انتقال الطلب وتوقع المسوغات.",
    cannotConclude: "لا يمكن إنهاء المرحلة بعد",
    blockers: {
      RequiredUndecided: "معايير إلزامية بلا نتيجة بعد ({{count}}): {{detail}}",
      BlockingIssues: "مسائل مانعة مفتوحة ({{count}}): {{detail}}",
      NoTransition: "لا يتوفر لدورك حاليا أي انتقال من هذه المرحلة.",
      Terminal: "الطلب في حالة نهائية.",
    },
    recommendation: "التوصية",
    aiRecommended: "يوصي {{agent}}",
    departsFromAi: "توصيتك تخالف توصية الوكلاء. يسجل ذلك — وينبغي أن تبين المسوغات السبب.",
    completionPreview: {
      title: "ما سيطلب من مقدم الطلب",
      hint: "يرسل إنهاء المرحلة طلب الاستيفاء هذا إلى مقدم الطلب، ومهلة الرد {{days}} يوم عمل. يستأنف الملف عند الرد على الطلب.",
      fromRationale: "لا توجد في هذه المرحلة نتيجة ناقصة أو نقص مفتوح، لذا ترسل المسوغات أدناه بوصفها الطلب. يمكن تحديد المعايير الناقصة لإرسال طلبات مفصلة بدلا من ذلك.",
    },
    rationale: "المسوغات",
    preview: "معاينة",
    signAndConclude: "توقيع وإنهاء",
    concluded: "تم إنهاء المرحلة",
    agentsDispatched: "الوكلاء الذين بدأوا العمل: {{agents}}",
    tally: { decided: "معايير مبتوت فيها", satisfied: "مستوف / غير منطبق", deficient: "غير مستوف / يحتاج معلومات", overrode: "تجاوز للذكاء الاصطناعي" },
    readOnlyTitle: "مراجعة هذه المرحلة مغلقة",
    terminalTitle: "{{stage}} — لا شيء متبق للمراجعة",
    terminalHint: "بلغ الطلب حالة نهائية. مراجعات المراحل التي أدت إلى ذلك محفوظة في السجل.",
    status: { Open: "مفتوحة", Concluded: "منتهية", Superseded: "متجاوزة" },
    noHistory: "لم تفتح أي مراجعة مرحلة على هذا الطلب بعد.",
    followedAi: "وفق توصية الذكاء الاصطناعي",
    departedFromAi: "خالف الذكاء الاصطناعي",
    concludedBy: "أنهاها {{name}} ({{role}}) في {{date}}",
    openedBy: "فتحها {{name}} في {{date}}",
    findings: "النتائج",
    noFindings: "لم تسجل نتائج.",
    notConcludedYet: "لم تنه بعد.",
    noRationale: "لم تسجل مسوغات.",
    submitter: "مقدم الطلب",
    firm: "المكتب",
    verifiedBy: "التحقق منه",
    openMandateQueue: "فتح قائمة الممثلين",
    linkedCase: "طلب مرتبط",
  },
  // الشرط 3.6: يذكر التحليل إلى جانب نتيجته تفسيرا بديلا والأدلة التي تميز بينه وبينها.
  alternativeExplanation: {
    title: "التفسير البديل والأدلة التي تميز بينه وبين النتيجة",
    evidence: "ما يميز بينهما",
    none: "لا يذكر التحليل تفسيرا بديلا؛ ولا يعتمد قبل ذكره.",
  },
  analysisSections: {
    percentagePoints: "{{value}} نقطة مئوية",
    isic: "رمز النشاط {{code}}",
    concentration: "مؤشرات التركز",
    cr4Post: "حصة أكبر أربع منشآت بعد الصفقة",
    variancePp: "الفرق (نقاط مئوية)",
    sections: { qualification: "التصنيف القانوني", definition: "تحديد السوق", structure: "هيكل السوق", assessment: "التقييم التنافسي", evidence: "الأدلة والسيناريوهات", precedents: "السوابق" },
    qualificationHint: "هل تنقل الصفقة السيطرة، وهل تستوفي الأطراف حدود الإخطار؟",
    definitionHint: "البعدان المنتجي والجغرافي لكل سوق معنية، مع مبررات تحديد الحدود.",
    structureHint: "الحصص ومؤشر هيرفندال-هيرشمان ونسب التركز – أرقام مقدم الطلب جنبا إلى جنب مع ما يمكن لبنك البيانات التحقق منه.",
    assessmentHint: "نظريات الضرر والقيود المثبتة والهيمنة والكفاءات – التقييم القائم على الآثار؛ ودرجة الفرز البنيوية إشارة لترتيب الأولويات فقط.",
    evidenceHint: "سيناريوهات الحساسية وتاريخ تركز السوق ونشاط ميرجرماركت لهذه الأطراف والقرارات المماثلة والتزامات ما بعد القرار.",
    precedentsHint: "قرارات الوزارة السابقة والسوابق المنشورة التي استند إليها التحليل.",
    linkedToDataBank: "سوق بنك البيانات",
    notLinked: "غير مرتبطة بسوق في بنك البيانات",
    definitionRationale: "مبررات التحديد (وكيل تحديد السوق والتركز)",
    noDefinitionRationale: "لم يوثق الوكيل بعد سبب اختيار حدود هذه السوق.",
    ssnipNote: "تتبع حدود السوق المعنية إرشادات الوزارة لتحديد السوق المعنية: إحلال جانبي الطلب والعرض المختبر بزيادة سعرية صغيرة لكن معتبرة وغير مؤقتة (SSNIP).",
    marketSize: "حجم السوق",
    parties: "الأطراف في هذه السوق",
    competitorsListed: "المنافسون المدرجون",
    declaredSharesHint: "كما صرح بها مقدم الطلب، مع تمييز الطرفين.",
    declaredVsVerified: "الحصص المصرح بها مقابل المتحقق منها",
    declaredVsVerifiedHint: "ممارسة منظمة التعاون الاقتصادي والتنمية: تصرح المنشأة بحصتها وتتحقق السلطة منها مقابل بياناتها. يحمل كل رقم متحقق منه مصدره ودرجة الثقة.",
    declaredCombined: "المجمعة المصرح بها",
    verifiedCombined: "المجمعة المتحقق منها",
    undertaking: "المنشأة",
    declared: "مصرح",
    verified: "متحقق",
    source: "المصدر",
    party: "طرف",
    curated: "معتمد",
    applicantOnly: "رقم مقدم الطلب فقط",
    verification: { Verified: "متحقق", Discrepancy: "تباين", Unverified: "غير متحقق", DataBankOnly: "بنك البيانات فقط" },
    sourcesUsed: "المصادر المستخدمة",
    noneIdentified: "لم يحدد شيء.",
    noneClaimed: "لم يدع شيء.",
    efficiencies: "الكفاءات المدعاة",
    efficienciesTest: "اختبار الكفاءات",
    efficienciesTestHint: "كل ادعاء على أركانه الثلاثة – قابل للتحقق، خاص بالتركز، منقول إلى العملاء – كما أجراه محرك القواعد. لا تقابل بـ CMCR إلا وفورات التكلفة الحدية المحتسبة؛ والادعاء السردي لا يختبر ولا وزن له.",
    narrativeOnlyEfficiencies: "ادعيت الكفاءات وصفا سرديا فقط. لا يمكن اختبارها من حيث قابلية التحقق أو الخصوصية بالتركز أو النقل ولا تحتسب – يطلب من الأطراف كل وفر بنوعه ومقداره وتوقيته ودليله وخصوصيته وآلية نقله.",
    claim: "الادعاء",
    verifiable: "قابل للتحقق",
    mergerSpecific: "خاص بالتركز",
    passedOn: "منقول",
    credited: "المحتسب",
    creditedTotal: "وفورات التكلفة الحدية المحتسبة",
    requiredCmcr: "الخفض التعويضي المطلوب في التكلفة الحدية",
    shortfall: "العجز",
    cmcrComparison: "مقابل الخفض التعويضي في التكلفة الحدية",
    capabilitiesReading: "قراءة القدرات",
    stillNeeded: "ما يزال مطلوبا من الأطراف",
    cognisable: "معتبر",
    notCognisable: "غير معتبر",
    notTimely: "يتجاوز 24 شهرا",
    ofMarginalCost: "من التكلفة الحدية",
    months: "{{count}} شهر", months_zero: "أقل من شهر", months_one: "شهر واحد", months_two: "شهران", months_few: "{{count}} أشهر", months_many: "{{count}} شهرا", months_other: "{{count}} شهر",
    verdicts: { Yes: "نعم", Partial: "جزئيا", No: "لا" },
    cmcrVerdicts: { ReachesCmcr: "يبلغ CMCR", ShortOfCmcr: "يقصر عن CMCR", NoQuantifiedMarginalClaim: "لا ادعاء مقدر في التكلفة الحدية", NoCmcr: "لا اختبار ضغط سعري" },
    efficiencyTypes: { MarginalCost: "التكلفة الحدية", FixedCost: "التكلفة الثابتة", Procurement: "المشتريات", Distribution: "التوزيع", Quality: "الجودة", Innovation: "الابتكار", Other: "أخرى" },
    dominance: "اختبار الهيمنة",
    dominanceHint: "يتحقق الوضع المسيطر عند تجاوز الحصة {{share}} (المادة 6(2)(أ) من المرسوم بقانون؛ قرار مجلس الوزراء رقم 3 لسنة 2025، المادة 2).",
    overall: "التقييم العام",
    precedentsSource: "قرارات معقمة في قاعدة المعرفة القانونية (منشورة من الوزارة أو صادرة في المنصة) مرتبة حسب صلتها بأسواق هذا الملف، إضافة إلى السوابق التي استشهد بها التحليل.",
    precedentCorpus: "السوابق في القاعدة: {{count}}",
    citedByAgent: "استشهد بها التحليل",
    noPrecedents: "لا سوابق بعد. استورد قرارات الوزارة المنشورة إلى قاعدة المعرفة القانونية؛ وتضاف القرارات الصادرة هنا آليا.",
    dealIntelligence: {
      title: "نشاط ميرجرماركت ذو الصلة",
      hint: "استخبارات صفقات مرخصة عن هذه الأطراف — صفقات مماثلة والقيمة المعلنة وأحداث الجهات الأخرى. تتحقق من الإخطار وليست إثبات ضرر.",
      empty: "لا توجد صفقة في النسخة المحلية مربوطة أو مقترحة لهذه الأطراف بعد. ويمكن البحث في الفهرس إن كان إعلان عام متوقعا.",
      onFile: "في هذا الملف",
      suggested: "مقترحة من النسخة المحلية",
      news: "أخبار ذات صلة",
      overlap: "التطابق",
      link: "ربط بهذا الملف",
      linked: "ربطت الصفقة بهذا الإخطار",
      open: "فتح في مراقبة الصفقات",
      relation: { Linked: "مربوطة", Escalated: "مصدر الدعوى التلقائية", Suggested: "مقترحة" },
    },
  },
  investigations: {
    dataPoints: {
      bid_spread_pct: "الفارق بين العطاءات %", repeat_pattern_count: "تكرار النمط في مناقصات سابقة", combined_share_pct: "الحصة المجتمعة %", days_between: "الأيام بين التغييرات",
      delta_pct: "تغير السعر %", composite_score: "الدرجة المركبة", flagged_tenders: "المناقصات المؤشر عليها", rotation_index: "مؤشر التناوب %",
      forensic_overlaps: "تطابقات المستندات", divergence_points: "التباعد (نقاط مئوية)", change_correlation: "ارتباط التغيرات", acquisitions: "عمليات الاستحواذ",
      cumulative_share_points: "الحصة المضافة (نقاط مئوية)", not_notified: "غير المخطر بها",
    },
    title: "التحقيقات",
    subtitle: "الشكاوى حسب المرحلة الإجرائية، وصندوق البلاغات، وإشارات مراقب السوق. وتفتح البطاقة لوحة التحقيق: الخطة، ومصفوفة الأدلة، وطلبات المعلومات، والزيارات الميدانية.",
    board: "لوحة الشكاوى",
    whistleblowing: "صندوق البلاغات",
    signals: "إشارات السوق",
    source: "المصدر",
    sources: { Individual: "بلاغ من فرد", Ministry: "الوزارة", MarketMonitor: "مراقب السوق", Mergermarket: "مراقبة الصفقات (ميرجرماركت)", Regulator: "جهة رقابية أخرى", Referral: "إحالة من قسم آخر" },
    anonymous: "مجهول",
    noReports: "لا توجد بلاغات",
    noReportsHint: "تظهر هنا البلاغات المقدمة عبر المنصة (مجهولة أو معرفة) والإشارات المصعدة من مراقب السوق.",
    signalsHint: "أنماط مرصودة في تدفق بيانات السوق والمناقصات وبنك البيانات – تواز سعري، فروق عطاءات مشبوهة، قفزات في الحصص، مؤشرات فرض أسعار إعادة البيع. الإشارات أدوات لترتيب الأولويات وليست نتائج.",
    lastScan: "آخر مسح",
    pendingSignals: "مسوح بانتظار المراجعة في صندوق الذكاء الاصطناعي: {{count}}",
    runScan: "تشغيل المسح الآن",
    scanQueued: "تمت جدولة مسح مراقب السوق",
    scanQueuedHint: "تصل الإشارات المرصودة كمقترح لرئيس قسم الشكاوى في صندوق الذكاء الاصطناعي.",
    signalFilters: { active: "نشطة", Escalated: "مصعدة", Dismissed: "مرفوضة", all: "الكل" },
    signalMethod: { score: "درجة الإشارة {{score}} من 100", unscored: "غير مقيسة – لا يقيس هذه الإشارة فحص ولا قاعدة" },
    signalTypes: { PriceParallelism: "تواز سعري", TenderPattern: "نمط مناقصات", ShareJump: "تركز الحصص", ResalePriceSignal: "مؤشر سعر إعادة البيع", ComplaintCluster: "تجمع شكاوى", NewsMention: "إعلان صفقة", UnnotifiedConcentration: "تركز غير مخطر به", StandstillBreach: "تنفيذ قبل الموافقة", ExpectedConcentration: "إخطار متوقع" },
    signalLead: { lead: "مؤشر يستدعي التحري، وليس دليلا.", alternative: "التفسير البديل" },
    signalStatuses: { New: "جديدة", UnderReview: "قيد المراجعة", Escalated: "مصعدة", Dismissed: "مرفوضة" },
    noSignals: "لا إشارات",
    noSignalsHint: "يمكن تشغيل مسح أو انتظار مهمة مراقب السوق اليومية.",
    markReview: "وضع قيد المراجعة",
    openReport: "فتح حالة بلاغ",
    openReportHint: "تنشئ حالة وفق المسار 6 بمصدر رصد «مراقب السوق» وتربط الإشارة بها.",
    officerNote: "ملاحظة الموظف",
    dismiss: "رفض",
    dismissTitle: "استبعاد الإشارة",
    dismissReasons: "سبب استبعاد الإشارة",
    dismissReasonsHint: "بأسلوبك، 20 حرفا على الأقل: ما الذي تحققت منه ولماذا لا تثير الإشارة شبهة تتعلق بالمنافسة. وتحفظ الأسباب مع الإشارة وفي سجل التدقيق.",
    dismissConfirm: "الاستبعاد بهذه الأسباب",
    reopen: "إعادة فتح",
    escalated: "تم فتح حالة البلاغ {{caseNumber}}",
    stages: { Received: "الاستلام", FormalCheck: "الفحص الشكلي", AwaitingComplainant: "بانتظار الشاكي", PartiesNotification: "إخطار الأطراف ({{period.complaint.response}} يوم عمل)", Investigation: "قيد التحقيق", PreliminaryReportShared: "إحالة التقرير الأولي", FinalReport: "التقرير النهائي", Escalation: "الرفع", MinisterDecision: "قرار الوزير", Decided: "صدور القرار", RejectOrSuspend: "الرفض أو التعليق", Suspended: "معلقة" },
  },
  dealWatch: {
    dealTypeNames: { Acquisition: "استحواذ", Merger: "اندماج", "In talks": "قيد التفاوض", "Minority stake": "حصة أقلية", "Mandated advisers": "تعيين مستشارين", "Private equity buyout": "استحواذ بملكية خاصة", "For sale": "معروض للبيع", "Private placement": "طرح خاص", "Private equity exit": "تخارج ملكية خاصة", Intelligence: "معلومات سوقية" },
    countryNames: { ARE: "الإمارات", SAU: "السعودية", DEU: "ألمانيا", CYM: "جزر كايمان", EUR: "أوروبا", CAN: "كندا", USA: "الولايات المتحدة", GBR: "المملكة المتحدة", FRA: "فرنسا", IND: "الهند", CHN: "الصين", JPN: "اليابان", KWT: "الكويت", QAT: "قطر", OMN: "عمان", BHR: "البحرين", EGY: "مصر", NLD: "هولندا", CHE: "سويسرا", SGP: "سنغافورة" },
    currencyNames: { USD: "دولار أمريكي", EUR: "يورو", GBP: "جنيه إسترليني", SAR: "ريال سعودي" },
    amountInMillions: "{{amount}} مليون {{currency}}",
    tab: "مراقبة الصفقات",
    enter: {
      action: "إدخال صفقة",
      title: "إدخال صفقة يدويا",
      hint: "صفقة لا يتضمنها فهرس معلومات الصفقات: خبر صحفي، أو بلاغ، أو إشعار من جهة أخرى، أو صفقة مرجعية تحددها الإدارة. وتفحص كما تفحص صفقات الفهرس – الارتباط بالدولة وانتقال السيطرة وحدود المادة 12 ووجود إخطار مطابق – وتراجع بالطريقة نفسها.",
      dealTitle: "العنوان",
      type: "النوع",
      types: { Acquisition: "استحواذ", Merger: "اندماج", JointVenture: "مشروع مشترك", MinorityStake: "حصة أقلية", AssetPurchase: "شراء أصول" },
      status: "الحالة",
      statuses: { Live: "قائمة", Completed: "مكتملة", Lapsed: "منقضية" },
      announcedOn: "تاريخ الإعلان",
      expectedCloseOn: "التاريخ المتوقع للإتمام",
      completedOn: "تاريخ الإتمام",
      valueAed: "القيمة (درهم)",
      stake: "الحصة (%)",
      parties: "الأطراف",
      role: "الدور",
      roles: { Bidder: "المستحوذ", Target: "المستحوذ عليه", Seller: "البائع", Party: "طرف آخر" },
      partyName: "اسم المنشأة",
      country: "الدولة (رمز ISO)",
      addParty: "إضافة طرف",
      removeParty: "حذف الطرف",
      source: "مصدر العلم بالصفقة",
      sourceHint: "خبر صحفي، أو شكوى، أو جهة أخرى، أو ملف مرجعي.",
      sourceReference: "المرجع",
      description: "الوصف",
      submit: "إدخال الصفقة وفحصها",
      saved: "أدخلت الصفقة {{id}} وفحصت.",
      entered: "أدخلها موظف",
    },
    deal: "الصفقة",
    hint: "صفقات الاندماج والاستحواذ الواردة من ميرجرماركت (آيون أناليتكس)، فحصتها المنصة للتحقق من الارتباط بالدولة، وانتقال السيطرة (المادة 1)، وحدود المادة 12، ووجود إخطار مطابق في السجل. الصفقات المرشحة أدوات لترتيب الأولويات: تتحقق الوزارة من الإيرادات لدى الهيئة الاتحادية للضرائب ومن السيطرة لدى السجل قبل مخاطبة أي طرف.",
    mode: { Simulator: "تدفق الاختبار – لم يربط Mergermarket بعد", Live: "تدفق مباشر", Web: "تدفق مباشر (الويب)" },
    lastSync: "آخر مزامنة",
    neverSynced: "لم تتم المزامنة بعد",
    pendingProposals: "تقييمات مراقبة الصفقات بانتظار المراجعة في صندوق الذكاء الاصطناعي: {{count}}",
    syncNow: "مزامنة الآن",
    syncQueued: "تمت جدولة مزامنة ميرجرماركت في الخلفية",
    syncDone: "اكتملت المزامنة. الصفقات: {{fetched}}، المرشحة: {{candidates}}، الجديدة: {{fresh}}",
    syncPartial: "انتهت المزامنة مع أخطاء. الصفقات: {{fetched}}، المرشحة: {{candidates}}، الجديدة: {{fresh}}؛ وما تعذر مبين أدناه",
    stats: { total: "الصفقات المسجلة", intelligence: "مواد استخباراتية: {{count}}" },
    closingSoon: "إتمام قريب",
    closingSoonHint: "إتمام متوقع خلال {{days}} يوما، أو فات موعده",
    closingInDays: "خلال {{count}} يوم", closingInDays_zero: "اليوم", closingInDays_one: "خلال يوم واحد", closingInDays_two: "خلال يومين", closingInDays_few: "خلال {{count}} أيام", closingInDays_many: "خلال {{count}} يوما", closingInDays_other: "خلال {{count}} يوم",
    closedDaysAgo: "متأخر {{count}} يوم", closedDaysAgo_zero: "مستحق اليوم", closedDaysAgo_one: "متأخر يوما واحدا", closedDaysAgo_two: "متأخر يومين", closedDaysAgo_few: "متأخر {{count}} أيام", closedDaysAgo_many: "متأخر {{count}} يوما", closedDaysAgo_other: "متأخر {{count}} يوم",
    overdueClose: "فات تاريخ الإتمام المتوقع؛ والمزود ما زال يصنف الصفقة قائمة.",
    statusFilter: "أي مرحلة",
    nexusFilter: "أي ارتباط",
    sortBy: "الترتيب",
    sort: { score: "أعلى درجة", value: "أكبر قيمة", announced: "أحدث إعلان", expectedClose: "أقرب إتمام", completed: "أحدث إتمام" },
    minScore: "أي درجة",
    minScoreValue: "{{count}}+",
    estimatedOnly: "قيم تقديرية",
    clearFilters: "مسح عوامل التصفية الإضافية",
    filterBy: "تصفية بحسب التقنية",
    keyboardHint: "↑ ↓ للتنقل بين الصفقات",
    prevDeal: "الصفقة السابقة",
    nextDeal: "الصفقة التالية",
    copyId: "نسخ المعرف",
    copied: "تم نسخ معرف ميرجرماركت",
    techniques: "التقنيات",
    providerStatus: "حالة المزود",
    latestActivity: "آخر نشاط",
    domestic: "محلية",
    unsolicited: "غير مرغوب فيها",
    timeline: "الجدول الزمني",
    economics: "المقابل",
    equityValue: "قيمة حقوق الملكية",
    drawer: { overview: "الوقائع", parties: "الأطراف", screening: "الفحص", provider: "ميرجرماركت" },
    filing: { FailureToNotifyCandidate: "عدم الإخطار", StandstillBreachCandidate: "تنفيذ قبل الموافقة", NotYetDue: "مراقبة – لم يستحق بعد", Filed: "مخطر به", OutOfScope: "خارج النطاق" },
    review: { New: "جديدة", UnderReview: "قيد المراجعة", Dismissed: "مرفوضة", Linked: "مرتبطة بإخطار", Escalated: "مصعدة" },
    filingFilters: { candidates: "المرشحة", FailureToNotifyCandidate: "عدم الإخطار", StandstillBreachCandidate: "التنفيذ المبكر", NotYetDue: "مراقبة", Filed: "مخطر بها", OutOfScope: "خارج النطاق", all: "كل الصفقات" },
    reviewFilters: { active: "مفتوحة", Escalated: "مصعدة", Linked: "مرتبطة", Dismissed: "مرفوضة", all: "أي حالة" },
    searchPlaceholder: "البحث عن صفقة أو طرف أو معرف ميرجرماركت أو رقم حالة…",
    catalogEmpty: "لا توجد نتائج في النسخة المحلية أو الجلسة المباشرة. ويمكن البحث باسم شركة أو بمعرف ميرجرماركت الرقمي.",
    catalogLive: "مباشر",
    catalogKind: { Deal: "صفقة", News: "خبر", Company: "شركة", Intelligence: "استخبارات" },
    refreshFromProvider: "تحديث من ميرجرماركت",
    refreshed: "تم تحديث الصفقة من جلسة الويب المرخصة",
    companyLookup: "شركة في ميرجرماركت",
    companyIntel: "مقالات مرتبطة: {{count}}",
    relatedDeals: "صفقات مرتبطة في السجل",
    relatedNews: "أخبار مرتبطة في السجل",
    empty: "لا توجد صفقات مطابقة",
    emptyHint: "يمكن تشغيل المزامنة لجلب أحدث الصفقات المرتبطة بالدولة من ميرجرماركت، أو توسيع عوامل التصفية.",
    columns: { score: "درجة الفرز", deal: "الصفقة", filing: "السجل", screen: "الفحص", dates: "التواريخ", value: "القيمة", review: "المراجعة" },
    rowCount: "الصفقات المعروضة: {{count}}",
    recordCount: "سجلات المزود: {{count, number}}",
    roles: { Acquirer: "المستحوذ", Bidder: "المستحوذ", Target: "الشركة المستهدفة", Vendor: "البائع", Seller: "البائع" },
    verdictGroups: { nexus: "الارتباط", control: "السيطرة", threshold: "الحدود" },
    nexus: { UaeParty: "طرف إماراتي", UaeSubsidiary: "شركة تابعة إماراتية", UaeTurnover: "إيرادات في الدولة", UaeGeography: "نطاق جغرافي إماراتي", None: "لا يوجد" },
    control: { Acquired: "انتقلت", Possible: "محتملة", NotAcquired: "لم تنتقل", Unknown: "غير معلوم" },
    threshold: { Met: "مستوفاة", NotMet: "غير مستوفاة", Unknown: "قيد الانتظار – أرقام ناقصة" },
    noFigures: "لا تتوفر بعد أرقام إيرادات أو حصص سوقية في الدولة لأطراف الصفقة.",
    announced: "الإعلان",
    completed: "الإتمام",
    expectedClose: "الإتمام المتوقع",
    estimated: "تقديرية",
    openOnMergermarket: "فتح في ميرجرماركت",
    enterpriseValue: "قيمة المنشأة",
    stakeExact: "حصة دقيقة",
    settlement: "التسوية",
    status: { Live: "معلنة", Completed: "متمة", Lapsed: "ملغاة", Intelligence: "استخبارات" },
    undisclosed: "غير معلنة",
    openCase: "فتح الملف",
    agentAssessed: "قيمها الوكيل",
    screenedAt: "فحصت",
    escalate: "فتح حالة بمبادرة الوزارة",
    escalateHint: "تنشئ حالة وفق المسار 6 بمصدر رصد «مراقبة الصفقات (ميرجرماركت)»، وتسجل إشارة السوق وتربط الصفقة بها. يحمل الملف نوع السلوك ونطاق الغرامة وفق المادتين 25/26.",
    escalateBasis: { unnotified: "تركز متم يبدو أنه يستوفي حدود المادة 12 دون إخطار في السجل (المادة 25: غرامة من 2% إلى 10% من المبيعات في الدولة، أو من 500,000 إلى 5,000,000 درهم إذا تعذر تحديد المبيعات).", standstill: "تركز نفذ قبل قرار الوزير أو بالمخالفة له (المادة 26: من 50,000 إلى 500,000 درهم؛ وقد يكون التركز ذاته غير مخطر به)." },
    link: "ربط بإخطار",
    linkHint: "يحدد إخطار التركز الاقتصادي المقابل لهذه الصفقة؛ يعاد حساب نتيجة الفحص من ذلك الملف وتستبعد الصفقة من قائمة المرشحة إذا كان الإخطار صحيحا.",
    linkConfirm: "ربط",
    registerSearch: "البحث في السجل برقم الملف أو العنوان…",
    linked: "ربطت الصفقة بالملف {{caseNumber}}",
    escalated: "تم فتح الملف {{caseNumber}} بمبادرة الوزارة",
    dismissTitle: "رفض الصفقة",
    dismissHint: "تبقى الصفقة مسجلة مع ملاحظتك؛ ويمكن إعادة فتحها إذا ظهرت وقائع جديدة.",
    dismissPlaceholder: "لماذا لا تشكل هذه الصفقة تركزا واجب الإخطار (لا ارتباط، لا انتقال سيطرة، الحدود غير مستوفاة، مخطر بها بالفعل في الملف …)",
    dismissReasonsHint: "تبين الأسباب في جملة على الأقل، وتسجل مع الاستبعاد.",
    dismissOverrideTitle: "استبعاد هذه الصفقة تجاوز لنتيجة من نتائج المنصة",
    dismissOverrideBody: "يصنف الفحص هذه الصفقة مرشحة. ولا يستبعدها إلا موظف مخول بتجاوز نتائج المنصة؛ وتسجل أسبابك بوصفها هذا التجاوز في سجل الصفقة وسجل التدقيق.",
    openProposal: "تقييم الوكيل",
    reviewerRole: "لا يجوز مراجعة مرشحات مراقبة الصفقات إلا لرئيس قسم الشكاوى أو المدير أو الباحث الاقتصادي.",
    reviewNote: "ملاحظة الموظف",
    stake: "الحصة المستحوذ عليها",
    combinedTurnover: "الإيرادات المجمعة في الدولة",
    combinedShare: "الحصة السوقية المجمعة",
    countries: "الدول",
    party: "الطرف",
    role: "الدور",
    country: "الدولة",
    resolvedEntity: "المنشأة في بنك البيانات",
    uaeTurnover: "الإيرادات في الدولة",
    unresolved: "غير مطابقة",
    viaSubsidiary: "عبر",
    registerCheck: "التحقق من السجل",
    noFilingFound: "لا يوجد إخطار تركز اقتصادي مطابق لهذه الصفقة في سجل الوزارة.",
    noFilingNeeded: "لا يلزم إخطار وفق نتيجة الفحص الحالية (أو لم تتم الصفقة بعد).",
    otherJurisdictions: "مراجعة الاندماج في دول أخرى",
    reviewPhases: { PhaseI: "المرحلة الأولى", PhaseII: "المرحلة الثانية", Substantivereview: "الدراسة الموضوعية" },
    reviewOutcomes: { Cleared: "أجيزت", ClearedWithConditions: "أجيزت بشروط", Prohibited: "حظرت", Withdrawn: "سحبت", Pending: "قيد الدراسة" },
    factors: "عوامل الدرجة",
    signal: "إشارة السوق",
    proposal: "مقترح الذكاء الاصطناعي",
    exOfficioCase: "حالة بمبادرة الوزارة",
    rawRecord: "سجل المزود (JSON)",
    openNews: "فتح الأخبار",
    lastMonth: "آخر 31 يوما",
    scoreName: "درجة الفرز {{score}} من 100",
    scoreMeaning: "نقاط تمنحها قواعد الفرز للصفقة بحسب مؤشرات الإخطار وحجمها والتداخل؛ وهي مؤشر للمراجعة لا نتيجة.",
  },
  news: {
    subtitle: "معلومات السوق والصفقات لإدارة المنافسة. كل بطاقة تعرض الناشر ورابط الأصل. ميرجرماركت مصدر واحد؛ والصحافة الإماراتية (ذا ناشيونال، خليج تايمز، زاوية) وتغذية السوق مصادر أخرى.",
    item: "المادة",
    lastSync: "آخر مزامنة",
    neverSynced: "لم تتم المزامنة بعد",
    syncNow: "مزامنة الآن",
    syncQueued: "أدرجت مزامنة الأخبار في الخلفية",
    syncDone: "اكتملت المزامنة. المواد: {{fetched}}، الجديدة: {{inserted}}، المحدثة: {{updated}}",
    syncPartial: "انتهت المزامنة مع أخطاء. المواد: {{fetched}}، الجديدة: {{inserted}}، المحدثة: {{updated}}؛ وما تعذر مبين أدناه",
    allSources: "كل المصادر",
    searchPlaceholder: "البحث في العنوان أو معرف ميرجرماركت…",
    empty: "لا مواد مطابقة",
    emptyHint: "يمكن تشغيل المزامنة لجلب معلومات ميرجرماركت وتغذية السوق، أو توسيع عوامل التصفية.",
    companies: "الشركات",
    relatedDeal: "صفقة مرتبطة في مراقبة الصفقات",
    openDealWatch: "فتح في مراقبة الصفقات",
    openSource: "فتح الأصل",
    noUrl: "لا رابط ناشر على هذا السجل",
    hasDeal: "مرتبطة بصفقة مفحوصة",
    lastMonth: "آخر 31 يوما",
    lastMonthMergers: "اندماجات الإمارات في الشهر الماضي (صفحات الناشر)",
    topCompanies: "أكبر المنشآت في الدولة",
    topCompaniesHint: "الملف الموحد (سوق أبوظبي وسوق دبي والمعرف العالمي للكيانات القانونية وفوربس وويكيبيديا) — المصدر والرابط على كل بطاقة",
    stats: {
      total: "المواد",
      today: "نشر اليوم: {{count}}",
      lastMonth: "آخر 31 يوما: {{count}}",
      intelligence: "مواد استخباراتية: {{count}}",
      market: "مواد تغذية السوق: {{count}}",
      deals: "إعلانات الصفقات",
      dealsHint: "صفقات ميرجرماركت المعلنة والمكتملة كبطاقات أخبار",
    },
    sources: {
      mergermarket: "ميرجرماركت",
      marketfeed: "تغذية السوق",
      news: "الأخبار",
      wam: "وام",
      thenational: "ذا ناشيونال",
      gulfnews: "غلف نيوز",
      khaleejtimes: "خليج تايمز",
      zawya: "زاوية",
      bbc: "بي بي سي",
      wikipedia: "ويكيبيديا",
      wikidata: "ويكيداتا",
      "forbes-me": "فوربس الشرق الأوسط",
      adx: "سوق أبوظبي",
      dfm: "سوق دبي",
      gleif: "المعرف العالمي للكيانات القانونية",
      "open-data-master": "ملف الشركات الإماراتي",
    },
    kinds: {
      Intelligence: "استخبارات",
      Deal: "صفقة",
      Market: "سوق",
      Press: "صحافة",
    },
  },
  enforcement: {
    title: "تقييم الإنفاذ",
    hint: "المواد 25–27 من المرسوم بقانون اتحادي رقم 36 لسنة 2023: يحسب نطاق الغرامة القانوني من أساس المبيعات والمادة؛ ويوازن فريق الملف الظروف ويقترح النتيجة؛ ويعتمدها ويوقعها المسؤول الذي تعود إليه. أما الإحالة إلى النيابة العامة فهي للوزير وحده (المادة 33).",
    prefilled: "معبأ مسبقا – غير محفوظ",
    saveDraft: "حفظ المسودة",
    saved: "تم حفظ التقييم",
    articleShort: "المادة {{n}}",
    openDeal: "فتح في مراقبة الصفقات",
    facts: "التركز",
    factsHint: "من استحوذ على السيطرة على من، ومتى نفذ، ومتى اكتشفته الوزارة؛ وأساس الحدود الذي يستند إليه التزام المادة 12.",
    article: "المخالفة",
    undertaking: "المنشأة المسؤولة",
    undertakingAr: "المنشأة (بالعربية)",
    target: "المنشأة المستهدفة / الطرف الآخر",
    thresholdBasis: "أساس الحدود",
    thresholdBasisHint: "مثال: إجمالي المبيعات المجمعة في الدولة 1.2 مليار درهم (المادة 12/1/أ) أو حصة مجمعة 45% في تجزئة الأدوية (المادة 12/1/ب).",
    implementedOn: "تاريخ التنفيذ",
    discoveredOn: "تاريخ الاكتشاف",
    salesBasis: "أساس المبيعات (المادة 25)",
    salesBasisOnFile: "أساس المبيعات المسجل",
    salesBasisHint: "مبيعات السلع أو الخدمات المعنية في الدولة خلال السنة المالية الأخيرة، من إقرار الهيئة الاتحادية للضرائب أو الحسابات المدققة المطلوبة في طلب المعلومات. أما إجمالي المبيعات في بنك المعلومات فمقياس مختلف: يذكر في الملاحظة للاطلاع ولا يدرج هنا أبدا. ودون رقم يطبق النطاق الثابت من 500,000 إلى 5,000,000 درهم.",
    salesAed: "مبيعات السلع المعنية في الدولة (درهم)",
    salesUnknown: "غير محدد – يطبق النطاق الثابت",
    salesYear: "السنة المالية",
    salesSource: "المصدر",
    salesSources: { FTA: "الهيئة الاتحادية للضرائب (إقرار ضريبة الشركات)", Filing: "إفادة الأطراف (الإخطار / الرد على طلب المعلومات)", DataBank: "بنك البيانات", Estimate: "تقدير الوزارة" },
    salesNote: "ملاحظة على الأساس",
    fallbackTitle: "النطاق الثابت ساري",
    fallback: "نطاق ثابت",
    circumstances: "الظروف",
    circumstancesHint: "يحدد هنا ما يثبته الملف. كل ظرف يحرك مبلغ التصالح الإرشادي بين الحد الأدنى للتصالح وفق المادة 33(2) والحد الأقصى القانوني؛ والأوزان إرشادية ولا تقيد القرار حسابيا.",
    aggravating: "مشددة",
    mitigating: "مخففة",
    aggravatingNote: "الظروف المشددة – ملاحظة",
    mitigatingNote: "الظروف المخففة – ملاحظة",
    outcome: "النتيجة المقترحة",
    outcomeHint: "الغرامات الواردة في المواد (24) إلى (27) عقوبات جزائية توقعها المحكمة. ويختص المدير بخطاب الإنذار (دون جزاء)؛ ويحتاج اقتراح التصالح وفق المادة 33(2) إلى وكيل الوزارة؛ ويحصر طلب تحريك الدعوى الجزائية في الوزير، وإن أجازت المادة 33(1) التفويض فيه. وتعرض إشارة القواعد للمقارنة.",
    indicated: "مشار إليها",
    approvedBy: "يعتمدها {{role}}",
    proposedFine: "مبلغ التصالح المقترح (درهم)",
    indicativeFine: "تشير القواعد إلى {{amount}}",
    useIndicative: "استخدام الإرشادية",
    fineOutOfRange: "خارج نطاق التصالح {{min}} (ضعف الحد الأدنى للغرامة) – {{max}}",
    settlementFloor: "الحد الأدنى للتصالح (المادة 33(2))",
    settlementFloorHint: "لا يجوز التصالح بأقل من ضعف الحد الأدنى للغرامة؛ وتقضي المحكمة بالغرامة ضمن النطاق أعلاه.",
    referralTitle: "طلب تحريك الدعوى الجزائية (المادة 33(1))",
    referralHint: "تعرض المذكرة الوقائع وأدلة الإخفاء أو التكرار ومبلغ التصالح الذي كان سيقترح لولا ذلك؛ ولا يقترح التصالح إلى جانبها.",
    rationale: "الحيثيات",
    rationaleHint: "لماذا هذه النتيجة: التركز وحدوده، وكيف وزنت الظروف، والتناسب. تصبح حيثيات أداة القرار.",
    rationaleAr: "الحيثيات (بالعربية)",
    range: "النطاق القانوني (غرامة المحكمة)",
    min: "الحد الأدنى",
    max: "الحد الأقصى",
    indicative: "إرشادي",
    proposed: "مقترح",
    indicativeAmount: "مبلغ التصالح الإرشادي",
    netWeight: "الوزن الصافي",
    rulesIndicate: "تشير القواعد إلى: {{outcome}}",
    referralWorth: "الظروف (التكرار، الإخفاء) تجعل طلب تحريك الدعوى الجزائية بدلا من التصالح جديرا بالعرض على الوزير.",
    chain: {
      title: "التوصية والاعتماد",
      hint: "يعد فريق الملف المسودة ← يوصي رئيس قسم الشكاوى ← يعتمد {{role}} ← يوقع المعتمد أداة القرار.",
      draft: "أعدت مسودة التقييم",
      recommended: "تمت التوصية",
      approved: "اعتمده {{role}}",
      issued: "صدر القرار",
    },
    decision: "أداة القرار",
    notified: "أخطر الأطراف",
    unsaved: "يلزم حفظ التقييم قبل التوصية به أو اعتماده.",
    saveFirst: "يلزم حفظ المسودة لفتح التوصية.",
    awaitingApprover: "بانتظار اعتماد {{roles}}.",
    awaitingSigner: "معتمد – يوقع القرار {{roles}}.",
    // Scenario 6.40: the penalty calculation stays off until the Department records the legal confirmation of its basis; a second officer approves.
    penaltyBasis: {
      offTitle: "حساب الجزاء غير مفعل",
      onTitle: "الأساس القانوني للحساب مؤكد",
      confirmedBy: "{{reference}} – سجله {{name}} في {{date}} (المنهجية {{version}})",
      covers: "ما يشمله التأكيد",
      reference: "مرجع الرأي القانوني أو المذكرة وتاريخها",
      referenceAr: "المرجع بالعربية",
      note: "ملاحظة (اختيارية)",
      confirm: "تسجيل التأكيد القانوني",
      confirmed: "سجل التأكيد القانوني",
      secondOfficer: "أعددت هذا التقييم أو أوصيت به: يعتمده موظف مخول آخر.",
    },
    recommend: "توصية",
    recommendHint: "يرفع التقييم ({{outcome}}) إلى {{role}} للاعتماد. تقفل المسودة أثناء الاعتماد.",
    recommended: "تمت التوصية بالتقييم",
    approve: "اعتماد",
    approveHint: "يعتمد النتيجة المقترحة ({{outcome}})؛ ويمكن بعدها إصدار أداة القرار.",
    approved: "تم اعتماد التقييم",
    return: "إعادة للمراجعة",
    returnHint: "يعيد التقييم إلى فريق الملف مع ملاحظتك؛ وتفتح المسودة.",
    returned: "أعيد التقييم",
    issue: "إصدار القرار",
    issueHint: "يصدر {{outcome}} بوصفه {{instrument}} على هذا الملف، مع حيثيات التقييم بوصفها أسباب القرار، وينقل المسار 6 إلى «مقرر».",
    issued: "صدر القرار {{number}}",
    decisionTitle: "عنوان القرار",
    decisionTitleHint: "يترك فارغا لاستخدام العنوان المعياري للنتيجة.",
    decisionTitleAr: "عنوان القرار (بالعربية)",
    notifyParties: "إخطار الأطراف عند الإصدار",
    issueReferralHint: "قرار الإحالة داخلي: تبلغ النيابة العامة الأطراف، وليس إخطار الوزارة.",
    note: "ملاحظة",
    letters: {
      title: "الخطابات",
      hint: "يفتح طلب المعلومات الملف مع الأطراف (وتنطبق المادة 27 على الرد)؛ ويتبع خطاب الإنذار واقتراح التصالح ومذكرة الدعوى الجزائية النتيجة المعتمدة.",
      none: "لا خطابات إنفاذ على هذا الملف بعد.",
      kinds: { rfi: "طلب معلومات", warning: "خطاب إنذار", fine: "اقتراح التصالح", referral: "مذكرة الدعوى الجزائية" },
      kindHints: {
        rfi: "يطلب من المنشأة تأكيد الصفقة، والسيطرة المكتسبة، وإجمالي المبيعات في الدولة ومبيعات السلع المعنية، وأي إخطارات لدى جهات أخرى.",
        warning: "خطاب إنذار – لا يعد جزاء – مع التوجيه بالإخطار (أو إلغاء الصفقة) خلال المدة المحددة (المادة 18(1) من اللائحة التنفيذية).",
        fine: "يقترح التصالح وفق المادة 33(2): المبلغ، والإقرار بالمخالفة، والسداد خلال {{period.enforcement.settlementPayment}} يوم عمل، وما يترتب على الرفض (المادة 27 من اللائحة التنفيذية).",
        referral: "مذكرة داخلية توصي بأن يطلب الوزير تحريك الدعوى الجزائية (المادة 33(1))؛ تحفظ في الملف ولا ترسل إلى الأطراف.",
      },
      responseDays: "مهلة الرد بالأيام: {{n}}",
      subject: "الموضوع",
      preview: "معاينة",
      send: "إرسال",
      file: "حفظ في الملف",
      sent: "أرسل الخطاب",
      filed: "حفظت المذكرة في الملف",
      due: "الرد مستحق {{date}}",
      letterStatus: { Draft: "مسودة", PendingApproval: "بانتظار التأكيد", Approved: "معتمد", Sent: "أرسل", Delivered: "سلم", Failed: "فشل", Cancelled: "ألغي" },
      rfiStatus: "طلب المعلومات",
      rfiDue: "الرد مستحق {{date}}",
      rfiAnswered: "تم الرد {{date}}",
      rfiItems: "البنود المجاب عنها: {{answered}} من {{total}}",
      outcomeLettersAfterRecommendation: "يتاح الإنذار وإشعار الغرامة ومذكرة الإحالة بعد التوصية بالتقييم.",
    },
  },
  representatives: {
    tab: "الممثلون",
    subtitle: "التفويضات التي أودعتها مكاتب المحاماة والمستشارون للتصرف نيابة عن المنشآت. وتفتح الوكالة ويطابق الموقع مع الرخصة، ثم يتحقق منها أو ترفض أو تلغى. قد يكون الممثل قدم طلبا بالفعل بموجب تفويض معلق.",
    filters: { pending: "بانتظار التحقق", verified: "تم التحقق" },
    searchPlaceholder: "البحث عن ممثل أو مكتب أو منشأة أو رخصة…",
    empty: "لا توجد تفويضات",
    emptyHint: "لا شيء مودع ضمن هذا التصفية.",
    representative: "الممثل",
    undertaking: "يتصرف نيابة عن",
    noFirm: "لا يوجد مكتب مسجل",
    lowerAssurance: "دخول بالبريد الإلكتروني",
    lodgedOn: "أودع {{date}}",
    grantor: "صادر عن",
    validity: "الصلاحية",
    openEnded: "غير محددة",
    poa: "الوكالة القانونية",
    noPoa: "لا توجد وثيقة مرفقة",
    casesFiled: "الطلبات المقدمة بموجب هذا التفويض: {{count}}",
    verify: "التحقق",
    verifyHint: "يؤكد أن الوكالة أصلية وأن الموقع مفوض؛ ويخطر الممثل.",
    verifyAnyway: "التحقق بناء على دليل جديد",
    reject: "غير مقبول",
    rejectHint: "يوقف الطلبات الجديدة من هذا الممثل عن المنشأة حتى إيداع وكالة مصححة؛ ويرسل السبب إلى الممثل.",
    revoke: "إلغاء",
    revokeHint: "ينهي التفويض. تبقى الطلبات المقدمة مسجلة؛ وتستلزم الطلبات الجديدة تفويضا جديدا.",
    revoked: "ملغى",
    reason: "السبب (يرسل إلى الممثل)",
    validUntil: "صالح حتى",
    validUntilHint: "يترك فارغا للإبقاء على تاريخ الوكالة أو دون تاريخ.",
    badge: "ممثل",
    badgeHint: "قدمه {{name}} ({{firm}}) بموجب تفويض: {{status}}. وتفتح قائمة الممثلين بالنقر هنا.",
    badgeNoMandateHint: "قدمه {{name}}، وهو ليس عضوا في المنشأة ولا يحمل تفويضا عنها.",
    status: { Pending: "بانتظار التحقق", Verified: "تم التحقق", Rejected: "غير مقبول", Revoked: "ملغى", Expired: "منتهي الصلاحية", None: "لا تفويض" },
  },
  screening: {
    tab: "الفحص",
    subtitle: "الطلبات التي وجد اختبار السيطرة أن الإخطار لا يبدو مطلوبا فيها – والطلبات التي اعترض مقدموها على تلك النتيجة. ويلزم تأكيدها وإعادتها، أو إدخالها إلى المراجعة الأولية.",
    empty: "لا شيء للفحص",
    emptyHint: "اجتازت جميع الطلبات اختبار السيطرة ودخلت المسار الاعتيادي.",
    contested: "معترض عليه من مقدم الطلب",
    acknowledged: "تم الإقرار بالإشعار",
    thresholdsNotMet: "لم يتحقق حد المبيعات في السوق المعنية ({{turnover}}) ولا حد الحصة السوقية ({{share}}).",
    applicantSays: "أسباب مقدم الطلب",
    confirmReturn: "تأكيد: غير واجب الإخطار – إعادة",
    returnHint: "يصوغ وكيل المراسلات خطاب \"إعادة الطلب\" لاعتمادك؛ ويبقى الرسم غير قابل للاسترداد.",
    proceed: "واجب الإخطار – متابعة",
    proceedHint: "يدخل الطلب المراجعة الأولية (قائمة المادة 10) كأي طلب آخر.",
    badge: "الفحص",
    returned: "معاد",
    badgeHint: "وجد اختبار السيطرة الحتمي أن الإخطار لا يبدو مطلوبا (أو اعترض مقدم الطلب على النتيجة). والتفاصيل في لوحة التصنيف القانوني.",
  },
  briefSafeguards: {
    title: "الضمانات أمام معالي الوزير",
    hint: "ما تحمله المذكرة إلى معالي الوزير من السجلات: رأي المراجع المستقل في التقرير المعتمد، وكل تحفظ أو مخالفة مع رد الإدارة عليه، ورأي اللجنة كما اعتمد. ولا تعتمد المذكرة إلا بعد أن يكون لكل نقطة رد بكلمات فريق القضية.",
    peer: "المراجعة المستقلة",
    noPeer: "لا توجد في الملف مراجعة مستقلة للتقرير المعتمد.",
    notIndependent: "سجلت مع تعارض مصالح معلن",
    verdicts: { Concur: "يوافق", ConcurWithReservations: "يوافق مع تحفظات", Dissent: "يخالف" },
    points: "النقاط التي يجب أن ترد عليها الإدارة ({{count}})",
    answer: "رد الإدارة",
    answerHint: "أجب عن النقطة نفسها – بقبولها أو ببيان سبب بقاء التقرير على حاله أو بما يتغير – بما لا يقل عن {{min}} حرفا. والإشارة إلى النقطة ليست ردا عليها.",
    suggested: "اقتراح المسودة",
    useSuggestion: "البدء منه",
    unanswered: "لا رد بعد – ولا ترفع المذكرة إلى معالي الوزير.",
    committee: "رأي لجنة تنظيم المنافسة",
    outcomes: { Approve: "الموافقة", ApproveWithConditions: "الموافقة المشروطة", Prohibit: "عدم الموافقة", RequestMoreInfo: "طلب مزيد من التحليل" },
    tally: "{{votesFor}} من أصل {{total}} أصوات",
    differs: "يختلف عن توصية الإدارة",
    committeePending: "أحيل الملف – ولم يعتمد رأي اللجنة بعد.",
    notReferred: "لم يحل الملف إلى اللجنة، وتبين المذكرة ذلك.",
    notReferredRequired: "لم يحل الملف رغم أن التوصية تتضمن شروطا أو عدم موافقة – وتنبه المذكرة معالي الوزير إلى ذلك.",
    approve: "اعتماد المذكرة بهذه الردود",
    approved: "اعتمدت المذكرة مع ردود الإدارة.",
  },
  investigationReport: {
    title: "تقرير الدراسة",
    intro: "تقرير الوزارة في عملية التركز الاقتصادي. يصوغ الوكيل كل فقرة بالعربية مع نسخة إنجليزية مطابقة، وتقوم أنت بقبولها أو تعديلها أو تجاوزها فقرة بفقرة قبل الاعتماد.",
    none: "لم تصغ بعد أي مسودة للتقرير",
    noneHint: "يختار هنا تقرير الدراسة الموضوعية الكامل، أو التقرير المختصر للعملية التي لا تستوفي حدود الإخطار.",
    draftSubstantive: "صياغة تقرير الدراسة الموضوعية",
    draftThresholds: "صياغة تقرير عدم استيفاء الحدود",
    drafting: "جار صياغة التقرير",
    draftingHint: "تبث خطوات الوكيل في تبويب الوكلاء، وتظهر المسودة هنا عند انتهائه.",
    redraft: "إعادة الصياغة بالوكيل",
    recommendation: "التوصية",
    recommendations: { Approve: "الموافقة", ApproveWithConditions: "الموافقة بشروط", Prohibit: "عدم الموافقة", NotNotifiable: "عدم استيفاء الحدود – غير واجب الإخطار" },
    variants: { SubstantiveReview: "دراسة موضوعية (المادة 13/2)", ThresholdsUnmet: "عدم استيفاء الحدود (المادة 13/3)" },
    reviewTitle: "مراجعة بشرية مع الذكاء الاصطناعي",
    reviewHint: "فقرات تنتظر القرار قبل اعتماد التقرير: {{count}}.",
    reviewHintDone: "تم قبول أو تجاوز كل الفقرات المصاغة؛ ويمكن اعتماد التقرير.",
    accepted: "مقبولة",
    override: "تعديل / تجاوز",
    writePassage: "كتابة الفقرة",
    applyOverride: "تطبيق التعديل",
    emptySection: "ترك الوكيل هذه الفقرة فارغة – يرجى كتابتها قبل الاعتماد.",
    finalise: "اعتماد التقرير",
    finaliseHint: "يعتمد المسودة ويرفق نسخة PDF بالعربية ونسخة وورد قابلة للتحرير بالملف.",
    finaliseBlocked: "يرجى قبول أو تجاوز الفقرات المتبقية ({{count}}) أولا.",
    finalised: "تم اعتماد التقرير وإرفاقه بالملف",
    finalisedBadge: "معتمد",
    finalisedHint: "النسخة المعتمدة مرفقة في تبويب المستندات بالعربية مع نسختها الإنجليزية، بصيغتي PDF ووورد. وتنتج إعادة الصياغة بالوكيل إصدارا جديدا.",
    attachRenderings: "إرفاق الصيغ",
    renderingsAttached: "أرفق بالملف: {{files}}",
    renderingsUpToDate: "النسخة المعتمدة مرفقة بالملف بالعربية والإنجليزية بصيغتي PDF ووورد.",
    versionAr: "النسخة العربية", versionEn: "النسخة الإنجليزية", word: "وورد",
    stateHint: "الملف في مرحلة {{state}}؛ ويكتب التقرير عادة في مرحلة التقييم أو قبل قرار وكيل الوزارة.",
    modeGuided: "مراجعة موجهة",
    modeList: "كل الفقرات",
    progress: "الفقرات التي تمت مراجعتها: {{done}} من {{total}}",
    overriddenCount: "فقرات معدلة: {{count}}",
    move: "التنقل",
    stepOf: "الخطوة {{n}} من {{total}}",
    loadBearing: "تستند إليها التوصية",
    overridden: "معدلة",
    acceptAgain: "إبقاؤها مقبولة",
    previous: "السابق",
    skip: "تخط مؤقت",
    emptySectionHint: "تكتب هذه الفقرة يدويا، أو يعاد صوغ التقرير بالوكيل.",
    evidenceTitle: "ما تقوله الأرقام",
    evidenceCaption: "محسوبة بالقواعد الحتمية للمنصة من الملف، ولا يستطيع الوكيل تغييرها؛ فإن بدا رقم غير صحيح فالخلل في الطلب أو في بيانات السوق.",
    noEvidence: "لا يستند هذا النص إلى رقم محسوب، وإنما إلى المستندات وقراءة الموظف.",
    summaryTitle: "كل شيء في مكان واحد قبل التوقيع",
    summaryHint: "توصية الإدارة، والفقرات التي قبلتها أو أعدت كتابتها، والأرقام التي سيستند إليها القرار.",
    awaitingYou: "بانتظارك",
    stillOpen: "فقرات ما زالت تنتظر قرارك",
    allReviewed: "تمت مراجعة كل الفقرات",
    allReviewedHint: "يعتمد الاعتماد التقرير ويرفق نسخة PDF بالعربية ونسخة وورد قابلة للتحرير بالملف.",
    blanksTitle: "فقرات تركها الوكيل فارغة: {{count}}",
    blanksHint: "تحذف الفقرة الفارغة من التقرير المطبوع. وتكتب إن كان قالب الإدارة يقتضيها.",
    keyFigures: "الأرقام الرئيسية",
    variantLabel: "نوع التقرير",
    reasons: "أسبابك لهذه التوصية",
    reasonsPlaceholder: "ما تحققت منه وما تستند إليه التوصية، بعباراتك.",
    reasonsHint: "تسجل مع اعتمادك وتظهر في سجل الملف – بما لا يقل عن {{count}} من الأحرف (كتبت {{typed}})؛ ولا يقبل نص منقول من المسودة.",
    reasonsBlocked: "تذكر أسباب الموظف للتوصية أولا.",
    chapters: {
      header: "الترويسة",
      parties: "1 – المنشآت",
      jurisdiction: "2 – الاختصاص",
      test1: "2.1 – الاختبار (1)",
      test2: "2.2 – الاختبار (2)",
      nature: "3 – طبيعة العملية",
      assessment: "4 – التقييم",
      harm: "4 – نظرية الضرر",
      observations: "5 – الملاحظات",
      summary: "الاعتماد",
    },
    capacities: { Acquirer: "المستحوذ", Target: "المستهدف", JointVenturePartner: "شريك في مشروع مشترك", Notifying: "الطرف المخطر", Other: "طرف آخر" },
    natures: { Horizontal: "أفقي", Vertical: "رأسي", Conglomerate: "تكتلي", None: "لا يوجد تداخل" },
    evidence: {
      sales: "المبيعات / الحد",
      share: "الحصة / الحد",
      controlVerdict: "اختبار السيطرة",
      changeOfControl: "تغير السيطرة",
      acquiredStake: "الحصة المستحوذ عليها",
      preStake: "الحصة قبل الصفقة",
      controlRights: "الحقوق الممنوحة",
      consideration: "قيمة الصفقة",
      capacity: "الصفة",
      acceptedOn: "تاريخ القبول",
      legalBasis: "السند القانوني",
      combinedShare: "الحصة الموحدة بعد التركز",
      dominanceBefore: "هيمنة سابقة للصفقة",
      band: "درجة التركيز",
      hhi: "مؤشر التركز قبل الصفقة وبعدها",
      deltaHhi: "التغير في مؤشر التركز",
      worstMarket: "أكثر الأسواق تأثرا",
      competitors: "عدد المنافسين المتبقين",
      nature: "طبيعة التركز",
      recommendation: "التوصية",
    },
    hints: {
      headerParties: "جملة واحدة تبين من يستحوذ على ماذا ومن من، كما ترد في جدول الترويسة.",
      headerSector: "القطاع والنشاط الاقتصادي للسوق المعنية.",
      partyDescription: "الشكل القانوني والتسجيل والأنشطة وهيكل الملكية وحضور المنشأة في الدولة.",
      competence: "لماذا تنظر الوزارة في الملف دون جهة محلية أو قطاعية.",
      requestStatus: "موقف الطلب والسند الذي قبل بموجبه.",
      test1Intro: "مدى استجابة العملية لتعريف التركز الاقتصادي في المادة الأولى.",
      ownershipTransfer: "بند الاتفاقية الذي ينقل الملكية، وما الذي ينقله.",
      transactionValue: "قيمة الصفقة كما أفصح عنها في الطلب.",
      ownershipConclusion: "هل يتحقق شرط نقل الملكية؟ تستند التوصية إلى هذه النتيجة.",
      control: "هل يكتسب المستحوذ تأثيرا حاسما، ومن أي نوع.",
      structure: "سلسلة الملكية والحوكمة قبل الصفقة وبعدها.",
      test1Conclusion: "النتيجة بشأن المادة الأولى، وتقرأ مقابل اختبار السيطرة المجاور.",
      productMarket: "التداخل بين أنشطة الأطراف وتصنيف المنتجات وهيكلة السوق.",
      geographicMarket: "النطاق الذي تتجانس فيه ظروف المنافسة.",
      substitution: "قابلية الاستبدال من ناحيتي الطلب والعرض.",
      marketDefinitionResult: "السوق أو الأسواق المعنية بالتسمية. ويتوقف عليها كل ما يليها.",
      thresholdConclusion: "أي الأسواق يتجاوز {{turnover}} من المبيعات أو {{share}} من الحصة الموحدة.",
      nonNotification: "لماذا لا تخضع العملية لوجوب الإخطار، بالصيغة المعتمدة لدى الإدارة.",
      nature: "لماذا يعد التركز أفقيا أو رأسيا أو تكتليا.",
      competitiveSituation: "حال المنافسة في السوق المعنية قبل التركز وبعده.",
      marketCommentary: "ماذا يعني تحرك مؤشر هيرفندال-هيرشمان في هذه السوق.",
      harmIntro: "ما الآثار التي قد تترتب على التركز بحكم طبيعته.",
      harmNonCoordinated: "هل يستطيع الكيان الناتج رفع الأسعار منفردا؟",
      harmCoordinated: "هل يسهل التنسيق بين المتدخلين المتبقين؟",
      harmVertical: "هل يمكن غلق السوق أمام المنافسين في المدخلات أو العملاء؟",
      harmConglomerate: "هل يمكن ربط المبيعات أو تجميعها عبر المحفظة الموحدة؟",
      riskConclusion: "النتيجة بشأن المخاطر، وتقرأ مقابل الفارق المجاور.",
      rebuttal: "ما الذي يجعل تلك المخاطر أقل احتمالا.",
      finalObservation: "ملاحظة مرقمة تحمل التوصية إلى معالي الوزير.",
    },
    header: {
      title: "بيانات الطلب",
      hint: "تملأ من الملف ومن المدد القانونية، ولا يكتبها الوكيل.",
      caseNumber: "رقم الطلب",
      receivedOn: "الاستلام",
      reviewStartedOn: "بدء الفحص الموضوعي",
      suspension: "تعليق الآجال",
      transactionType: "نوع التركز",
      nature: "طبيعة التركز",
      reportDate: "تاريخ رفع التقرير",
    },
    thresholds: { title: "حدود الإخطار", hint: "تحسبها القواعد الحتمية لكل سوق معنية مقابل {{turnover}} من إجمالي المبيعات و{{share}} من الحصة الموحدة (المادة 3 من قرار مجلس الوزراء رقم 3 لسنة 2025).", market: "السوق المعنية", sales: "المبيعات / الحد (درهم)", share: "الحصة / الحد", notifiable: "واجب الإخطار" },
    sections: {
      headerParties: "أطراف التركز (سطر الترويسة)",
      headerSector: "القطاع والنشاط الاقتصادي",
      partyDescription: "وصف المنشأة",
      competence: "اختصاص الوزارة",
      requestStatus: "حالة الطلب",
      test1Intro: "الاختبار القانوني (1) – المقدمة",
      ownershipTransfer: "نقل الملكية أو الحقوق أو الحصص أو الأصول",
      transactionValue: "قيمة الصفقة",
      ownershipConclusion: "نتيجة شرط نقل الملكية",
      control: "مدى توفر السيطرة",
      structureBefore: "الهيكلة والحوكمة قبل التركز",
      structureAfter: "الهيكلة والحوكمة بعد التركز",
      test1Conclusion: "نتيجة الاختبار القانوني (1)",
      productMarket: "سوق المنتج والخدمات",
      geographicMarket: "السوق الجغرافية",
      substitution: "اختبار قابلية الاستبدال",
      marketDefinitionResult: "نتيجة تحديد السوق المعنية",
      thresholdConclusion: "نتيجة اختبار حدود الإخطار",
      nonNotification: "مبررات عدم وجوب الإخطار",
      nature: "طبيعة العملية",
      competitiveSituation: "الوضعية التنافسية بالسوق المعنية",
      harmIntro: "نظرية الضرر – المقدمة",
      harmNonCoordinated: "الآثار الأفقية غير المنسقة",
      harmCoordinated: "الآثار الأفقية المنسقة",
      harmVertical: "الآثار الرأسية",
      harmConglomerate: "الآثار التكتلية",
      riskConclusion: "نتيجة احتمالات وجود المخاطر",
      rebuttal: "مدى قابلية دحض المخاطر",
      finalObservation: "ملاحظة نهائية",
    },
  },
  procedure: {
    clock: {
      title: "مدة الدراسة القانونية",
      hint: "{{period.ec.review}} يوما من تاريخ استلام الطلب مستوفيا، قابلة للتمديد مرة واحدة بما لا يتجاوز {{period.ec.reviewExtension}} يوما (المادة 13(2)). تنقطع المدة أثناء انتظار معلومات من الأطراف، أو رأي جهة قطاعية، أو أثناء دراسة اعتراض (المادة 14). ويعد انقضاء المدة دون قرار رفضا للطلب.",
      running: "جارية",
      interrupted: "منقطعة",
      breached: "انقضت المدة",
      completed: "صدر القرار في المدة",
      notStarted: "لم تبدأ",
      notStartedHint: "تبدأ المدة عند تسجيل الطلب مستوفيا. ولا تحتسب مناقشات ما قبل الإخطار.",
      startedAt: "البداية",
      startedAtHint: "استلام الطلب مستوفيا",
      dueAt: "تاريخ الاستحقاق",
      days: "يوما",
      remaining: "الأيام المتبقية",
      frozen: "متوقفة أثناء الانقطاع",
      extension: "التمديد المستعمل",
      extensionHint: "مرة واحدة، حتى {{period.ec.reviewExtension}} يوما، بمبرر",
      interruptedDays: "أيام الانقطاع",
      interruptedDaysHint: "الانقطاعات المسجلة: {{count}}",
      activeGrounds: "المدة منقطعة حاليا للأسباب التالية",
      since: "منذ {{date}}",
      history: "سجل الانقطاعات ({{n}})",
      ongoing: "مستمر",
      silence: "أثر السكوت",
      sync: "إعادة المزامنة",
      syncedPaused: "أعيدت مزامنة المدة: منقطعة بموجب المادة 14",
      syncedRunning: "أعيدت مزامنة المدة: جارية",
      extend: "تمديد (حتى {{days}} يوما)",
      extendTitle: "تمديد مدة الدراسة — المادة 13(2)",
      extendHint: "يتاح تمديد واحد بما لا يتجاوز {{max}} يوما إضافيا. ويخطر الأطراف بالتاريخ الجديد وبالأسباب.",
      justificationHint: "يذكر السبب (تعقد الملف، تحليل إضافي، مناقشة الالتزامات)، ويقتبس في الرسالة إلى الأطراف وفي التقرير.",
    },
    simplified: {
      title: "الإجراء المبسط",
      hint: "كل سوق معلنة داخل الملاذات الآمنة الهيكلية للسياسة (الحصة المجمعة والتغير في المؤشر في السوق الأفقية، وحصة الأطراف في السوق الرأسية أو التكتلية) يمكن أن تسلك المسار القصير بعد مهلة المرئيات: تقرير مختصر ثم التقييم. سوق واحدة خارجها تحيل الملف كله إلى الدراسة الكاملة. وتأخذ كل سوق حد نوعها كما صنفه الموظف المختص، أو كما تظهره الأرقام إن لم يصنف – أفقية فقط حيث يملك الطرفان معا حصة فيها – ولا يعتد بوصف مقدم الطلب وحده.",
      eligible: "مؤهل",
      notEligible: "دراسة كاملة",
      start: "بدء الدراسة المبسطة",
      escalate: "الإحالة إلى الدراسة الكاملة",
      publish: "التأكيد والنشر",
      conclude: "الإنهاء – تقرير مختصر",
      overlap: "التداخل",
      combinedShare: "الحصة المجمعة",
      // 6.6 و6.10: في السوق غير الأفقية تكون الحصة حصة الأطراف، ويؤخذ النوع من التصنيف أو من الأرقام.
      partiesShare: "حصة الأطراف",
      type: "النوع",
      nonHorizontal: "غير أفقي",
      byOfficer: "تصنيف الموظف",
      byFigures: "من الأرقام",
      applicantLabel: "وصف مقدم الطلب: {{type}}",
      contradicted: "وصف تخالفه القراءة",
      reading: "القراءة",
      inside: "داخل الملاذ الآمن",
      outside: "خارجه",
    },
    coordination: {
      title: "التنسيق مع الجهات الأخرى",
      hint: "الجهات التنظيمية القطاعية التي يجب التشاور معها والسلطات الأجنبية التي تراجع الصفقة نفسها: الجدول الزمني والتنازل عن السرية ومواءمة التدابير. يقتبس في القسم 3.1 من التقرير.",
      saved: "تم حفظ سجل التنسيق.",
      regulatorsToConsult: "الجهات التنظيمية القطاعية التي تشير إليها رموز التصنيف الصناعي الدولي المعلنة – لم تتم استشارتها بعد",
      addConsultation: "تسجيل الاستشارة",
      empty: "لم تسجل أي جهة. وتضاف هنا الجهات التنظيمية التي تشير إليها رموز التصنيف الصناعي الدولي وأي إخطار أجنبي أفصحت عنه الأطراف.",
      entry: "المدخل {{n}}",
      kinds: { ForeignAuthority: "سلطة منافسة أجنبية", SectorRegulator: "جهة تنظيم قطاعية" },
      statuses: { Planned: "مخطط", Filed: "تم الإخطار", UnderReview: "قيد الدراسة", OpinionRequested: "طلب رأي فني", Consulted: "تمت الاستشارة", Cleared: "تمت الموافقة", ClearedWithConditions: "موافقة مشروطة", Prohibited: "منع", NotRequired: "غير مطلوب" },
      sources: { Applicant: "سجلته الأطراف", Ministry: "سجلته الوزارة" },
      authority: "الجهة",
      jurisdiction: "الاختصاص (رمز الدولة)",
      sector: "القطاع المنظم",
      status: "الحالة",
      filedOn: "تاريخ الإخطار",
      expectedDecisionOn: "القرار متوقع بحلول",
      decidedOn: "تاريخ القرار",
      waiver: "تنازلت الأطراف عن السرية",
      notes: "ملاحظات",
      remedyAlignment: "مواءمة التدابير",
      recorded: "المسجل من طلب الرأي",
      remedyAlignmentHint: "كيفية مواءمة التدابير مع السلطة الأخرى – التصفية نفسها، استثناء الإمارات، التسلسل – أو سبب الاختلاف.",
      addForeign: "إضافة سلطة أجنبية",
      addRegulator: "إضافة جهة تنظيم قطاعية",
    },
    preNotification: {
      title: "مناقشة ما قبل الإخطار",
      hint: "أسئلة الأطراف غير الرسمية قبل التقديم ورأي الإدارة غير الملزم. تغذي النقاط الواجب معالجتها فحص جاهزيتهم؛ ولا تحتسب المدة هنا ضمن الـ{{period.ec.review}} يوما.",
      open: "المناقشة مفتوحة",
      none: "لم تطلب الأطراف مناقشة ما قبل الإخطار.",
      questions: "أسئلة الأطراف",
      proposedMarket: "تعريف السوق المقترح",
      view: "الرأي غير الملزم",
      viewLabel: "رأي الإدارة غير الملزم",
      viewHint: "تفاعل مع تعريف السوق المقترح وبين ما يجب أن يتضمنه الإخطار. يرسل الرأي إلى الأطراف في خطاب.",
      viewLabelAr: "النسخة العربية (اختياري)",
      points: "نقاط يجب معالجتها في الإخطار",
      pointsHint: "نقطة في كل سطر؛ تصبح كل منها توصية في فحص جاهزية مقدم الطلب.",
      sendKeepOpen: "إرسال وإبقاء المناقشة مفتوحة",
      sendConclude: "إرسال وإنهاء المناقشة",
      viewSent: "أرسل الرأي غير الملزم إلى الأطراف.",
      concluded: "انتهت المناقشة بتاريخ {{date}}",
    },
    findings: {
      title: "بيان النتائج الأولية",
      hint: "قبل أن تصل توصية سلبية إلى الوزير، تطلع الأطراف على نظرية الضرر والشروط المقترحة وترد خلال المهلة. وتجيب الإدارة نقطة بنقطة؛ ويقتبس الاثنان في القسم 4.6 من التقرير، ويشترط ذلك للانتقال من التقييم إلى الرفع.",
      compose: "عرض النتائج على الأطراف",
      reportRecommends: "توصي آخر مسودة للتقرير بـ: {{code}}",
      noReport: "لا يوجد تقرير دراسة في الملف بعد",
      adverseNote: "توصية سلبية: يجب الاستماع إلى الأطراف قبل إنجاز التقرير. المسودة أدناه معبأة مسبقا من التقرير.",
      notAdverseNote: "لا يعفى من البيان إلا بعد أن يعتمد موظف تقريرا يوصي بالموافقة غير المشروطة أو بعدم وجوب الإخطار؛ ولا تعفي منه مسودة.",
      proposedOutcome: "تعتزم التوصية بـ",
      outcomes: { ApproveWithConditions: "الموافقة المشروطة", Prohibit: "المنع", Approve: "الموافقة", NotNotifiable: "عدم وجوب الإخطار" },
      replyDays: "مهلة الرد (أيام عمل)",
      theoryOfHarm: "نظرية الضرر المعروضة على الأطراف",
      theoryHint: "معبأة مسبقا من نظرية الضرر في التقرير؛ وتحرر ليتمكن الأطراف من الرد على الاستدلال الفعلي لا على ملخص.",
      theoryOfHarmAr: "النسخة العربية (اختياري)",
      markets: "الأسواق المعنية", marketsHint: "سوق في كل سطر (المنتج والنطاق الجغرافي). معبأة مسبقا من التقرير أو الإخطار.",
      evidence: "الأدلة التي يستند إليها الرأي الأولي", evidenceHint: "بند في كل سطر – ليعرف الأطراف ما يردون عليه. معبأة مسبقا من الأدلة المعتمدة في التقرير.",
      evidenceAr: "الأدلة بالعربية (اختياري)", marketsLabel: "الأسواق", evidenceLabel: "الأدلة المعتمدة",
      conditions: "الشروط التي تعتزم الإدارة اقتراحها",
      conditionsHint: "شرط في كل سطر. مطلوب للموافقة المشروطة؛ يترك فارغا في حالة المنع.",
      issue: "إصدار البيان",
      issued: "صدر بيان النتائج الأولية وأرسل إلى الأطراف.",
      none: "لم يصدر أي بيان.",
      issuedOn: "صدر {{date}} بواسطة {{by}}",
      periodElapsed: "انتهت مهلة الرد",
      replied: "تم الرد",
      late: "متأخر",
      replyDue: "الملاحظات مطلوبة بحلول {{date}}",
      partiesReply: "ملاحظات الأطراف",
      recordReply: "تسجيل ملاحظات وردت خارج المنصة",
      recordReplyHint: "خطاب أو محضر جلسة؛ ويمكن للأطراف أيضا الرد مباشرة من البوابة.",
      recordReplyAction: "تسجيل الملاحظات",
      replyRecorded: "ملاحظات الأطراف في الملف.",
      departmentReply: "رد الإدارة",
      departmentReplyHint: "نقطة بنقطة: ما قبل وما لم يقبل ولماذا. يقتبس نصا في التقرير.",
      departmentReplyAr: "النسخة العربية (اختياري)",
      recordAnswer: "تسجيل الرد",
      answerRecorded: "رد الإدارة في الملف.",
      answeredOn: "تم الرد {{date}} بواسطة {{by}}",
      notAnswered: "لم يرد بعد – وسيذكر القسم 4.6 من التقرير ذلك.",
    },
    peerReview: {
      title: "المراجعة المستقلة من خبير",
      hint: "خبير اقتصادي ثان لم يشارك في الملف يقرأ التقرير المعتمد ويسجل ما إذا كانت الأدلة تحمل التوصية. لا يخرج التقرير من مرحلة التقييم إلا بوجود المراجعة في الملف؛ وتنتقل أي مخالفة إلى مذكرة الوزير.",
      verdicts: { Concur: "موافقة", ConcurWithReservations: "موافقة مع تحفظات", Dissent: "رأي مخالف" },
      staleBadge: "عدل التقرير – تلزم مراجعة جديدة",
      missing: "بانتظار مراجعة الخبير",
      waiting: "لم يعتمد التقرير بعد",
      record: "تسجيل المراجعة",
      reportApproved: "اعتمد التقرير بتاريخ {{date}} – ويوصي بـ{{code}}",
      reportNotApproved: "لم يعتمد تقرير الدراسة بعد؛ تقرأ المراجعة النص المعتمد.",
      notIndependent: "لقد شاركت في هذا الملف",
      notIndependentHint: "المراجعة لخبير من خارج فريق الملف. صلاتك بالملف: {{ties}}. تمكن إحالة المهمة إلى زميل، أو يمكن لمسؤول النظام تسجيل مراجعة مع إثبات التعارض.",
      roleRequired: "يلزم خبير اقتصادي",
      roleRequiredHint: "يسجل المراجعة خبير اقتصادي (أو مدير ينوب عنه) لم يشارك في الملف.",
      overrideTitle: "تعارض مثبت",
      overrideHint: "لك صلة بهذا الملف ({{ties}}). بصفتك مسؤول النظام يمكنك تسجيل المراجعة؛ ويدون التعارض في المراجعة والمذكرة.",
      overrideCheck: "تسجيل المراجعة رغم التعارض مع إثباته",
      verdict: "الرأي",
      recommendedOutcome: "النتيجة التي توصي بها",
      opinion: "الرأي المسبب",
      opinionHint: "هل تحمل الأدلة التوصية؟ تعريف السوق، نظرية الضرر، السيناريو المقابل، الكفاءات، التعهدات – بين أين يصمد التقرير وأين لا يصمد (40 حرفا على الأقل).",
      opinionAr: "النسخة العربية (اختياري)",
      pointsField: "نقاط التحفظ أو المخالفة",
      pointsHint: "نقطة في كل سطر. مطلوبة إلا عند الموافقة دون تحفظ؛ ويرد على كل نقطة في مذكرة القرار.",
      submit: "توقيع المراجعة",
      recorded: "سجلت المراجعة.",
      none: "لا توجد مراجعة في الملف.",
      onReport: "على تقرير يوصي بـ{{code}}",
      wouldRecommend: "يوصي بـ{{code}}",
      stale: "مراجعة نسخة سابقة من التقرير",
      points: "النقاط المثارة",
      conflictRecorded: "المراجع لم يكن مستقلا",
      history: "مراجعات سابقة ({{n}})",
      team: "فريق الملف الذي يجب أن يكون المراجع من خارجه ({{n}})",
      conduct: {
        hint: "خبير قانوني أو اقتصادي لم يشارك في الملف يقرأ التقرير النهائي الذي اعتمده الموظفون ويسجل ما إذا كانت الأدلة تحمل النتيجة التي اختاروها. لا يرفع التقرير إلى الوزير إلا بوجود المراجعة في الملف؛ وتنتقل التحفظات والمخالفة إلى المذكرة.",
        reportNotApproved: "لم يعتمد التقرير النهائي بعد؛ تقرأ المراجعة النص المعتمد بالنتيجة التي اختارها الموظفون وأسبابهم.",
        roleRequired: "يلزم خبير قانوني أو اقتصادي",
        roleRequiredHint: "يسجل المراجعة خبير قانوني أو اقتصادي (أو مدير ينوب عنه) لم يشارك في الملف.",
        opinionHint: "هل تحمل الأدلة النتيجة؟ تناول كل سؤال من أسئلة الاختبار القانوني، والأدلة المؤيدة والمعارضة لكل نتيجة، ومبررات المدعى عليهم، وملاحظات الأطراف – وبين أين يصمد التقرير وأين لا يصمد (40 حرفا على الأقل).",
      },
    },
    committee: {
      title: "لجنة تنظيم المنافسة",
      hint: "رأي اللجنة في توصية الإدارة قبل عرض الملف على الوزير (المادة 17). مطلوب لتوصية بالموافقة المشروطة أو بالرفض؛ اختياري فيما عدا ذلك. ويقتبس الرأي في حيثيات القرار.",
      recommendations: { Approve: "الموافقة", ApproveWithConditions: "الموافقة المشروطة", Prohibit: "الرفض", RequestMoreInfo: "طلب مزيد من التحليل" },
      required: "الرأي مطلوب",
      optional: "اختياري",
      pending: "معروض على اللجنة",
      overdue: "تأخر الرأي",
      refer: "الإحالة إلى اللجنة",
      referBlocked: "يلزم أولا وجود التقرير المعتمد ومراجعة الخبير في الملف.",
      departmentRecommends: "توصي الإدارة بـ{{code}}",
      requiredHint: "التوصية غير المواتية تعرض على اللجنة قبل الرفع",
      optionalHint: "يمكن إحالتها بحسب تقدير الإدارة",
      none: "لم تحل إلى اللجنة.",
      referredOn: "أحيلت بتاريخ {{date}}",
      dueOn: "الرأي مستحق بتاريخ {{date}}",
      votes: "أصوات الأعضاء ({{n}})",
      noVotes: "لم تسجل أصوات بعد.",
      member: "العضو",
      memberPlaceholder: "الاسم الكامل",
      vote: "الصوت",
      note: "ملاحظة (اختياري)",
      castVote: "تسجيل الصوت",
      voteRecorded: "سجل الصوت.",
      adopt: "اعتماد الرأي",
      recommendation: "التوصية",
      session: "مرجع الجلسة",
      reasoning: "الأسباب",
      reasoningHint: "لماذا توصي اللجنة بهذه النتيجة، بعبارات يتابعها الوزير والمحكمة المختصة (40 حرفا على الأقل). يقتبس في المذكرة.",
      reasoningAr: "النسخة العربية (اختياري)",
      conditions: "الشروط التي توصي بها اللجنة",
      conditionsHint: "شرط في كل سطر.",
      adoptSubmit: "اعتماد وإصدار",
      opinionRecorded: "سجل رأي اللجنة.",
      opinion: "الرأي المعتمد",
      diverges: "يختلف رأي اللجنة عن توصية الإدارة",
      divergesHint: "تعرض المذكرة الرأيين على الوزير مع أسباب كل منهما.",
      recitalNote: "تقتبس حيثيات القرار هذا الرأي وتاريخه وجلسته.",
    },
  },
  decisions: {
    validity: {
      period: "مدة الصلاحية", from: "يسري الاستثناء من", fromHint: "اتركه فارغا ليسري من تاريخ التوقيع.", until: "ينتهي الاستثناء في",
      untilHint: "يذكر القرار مدة الصلاحية (المادة 9(1) من اللائحة التنفيذية).", untilAmendmentHint: "فقط إذا غير التعديل المدة؛ وإلا بقيت المدة الأصلية.",
    },
    title: "قائمة القرارات",
    subtitle: "الملفات بانتظار معالي الوزير أو الوكيل، وسلسلة الرفع، وقائمة مراجعة لجنة تنظيم المنافسة.",
    minister: "بانتظار الوزير", escalation: "سلسلة الرفع", committee: "مراجعة اللجنة",
    undersecretary: "بانتظار وكيل الوزارة",
    undersecretarySubtitle: "عمليات التركز التي لا تستوفي حدود الإخطار: يصدر وكيل الوزارة القرار الإداري بموجب التفويض الوارد في القرار الوزاري رقم (142) لسنة 2026.",
    noMinister: "لا شيء بانتظار توقيع الوزير.", noEscalation: "لا تقارير في سلسلة الرفع.", noCommittee: "لا ملفات لدى اللجنة.",
    noUndersecretary: "لا شيء بانتظار توقيع وكيل الوزارة.",
    states: { ComplianceReview: "دراسة التوافق", CommitteeReview: "لدى اللجنة", CommitteeOpinion: "مطلوب رأي اللجنة في التوصية", MinisterDecision: "بانتظار الوزير", UndersecretaryDecision: "بانتظار وكيل الوزارة" },
    instruments: { Ministerial: "قرار وزاري", Administrative: "قرار إداري" },
    instrumentHints: {
      Ministerial: "قرار وزاري يوقعه معالي الوزير استنادا إلى المادة (15) من المرسوم بقانون اتحادي رقم (36) لسنة 2023.",
      Administrative: "قرار إداري بعدم استيفاء شروط المادة (12)، يوقعه وكيل الوزارة بموجب القرار الوزاري رقم (142) لسنة 2026.",
    },
    signerRequired: "يوقع هذا القرار: {{roles}}.",
    notOpenYet: "لا يمكن توقيع القرار بعد",
    preview: "معاينة القرار",
    previewHint: "ينزل القرار غير الموقع بالصيغة التي سيصدر بها، لقراءته قبل التوقيع.",
    downloadInstrument: "تنزيل القرار الموقع",
    reasonArHint: "الصياغة العربية المستخدمة في المادة (1) من القرار؛ وتترك فارغة لاستخدام الصيغة القياسية.",
    remedyAccepted: "قبلت حزمة التعهدات رقم {{v}} (عدد التعهدات: {{count}})",
    remedyAcceptedHint: "ستطبع المادة (1) التعهدات المقبولة بوصفها شروط الموافقة، ويبنى سجل التعهدات منها (الوصي، الدورية، أجل الانتهاء، المؤشر). ويعرض الملء المسبق الصياغة، ولا تعدل إلا إذا غيرها الوزير.",
    noAcceptedRemedy: "لا توجد حزمة تعهدات مقبولة في الملف",
    noAcceptedRemedyHint: "ينبغي أن تستند الموافقة المشروطة إلى حزمة قيمتها الإدارة بأنها كافية بعد اختبار السوق. الشروط المكتوبة هنا ستلزم الأطراف دون ذلك السجل؛ لذا يلزم فتح عرض التعهدات وتمرير الحزمة عبر المرحلة أولا.",
  },
  remedies: {
    title: "تصميم التعهدات",
    hint: "ما تعرضه الأطراف لإبقاء الصفقة قابلة للموافقة، محسوبا أثره ومختبرا في السوق قبل أن تعطي الإدارة حكمها. عرض حزمة يفتح مرحلة تصميم التعهدات؛ والحكم يعيد الملف إلى التقييم فيعاد صوغ التقرير على أساس السجل.",
    legalBasis: "المادتان 14 و15 من المرسوم بقانون اتحادي رقم (36) لسنة 2023 – الموافقة المشروطة؛ واللائحة التنفيذية بشأن التعهدات التي يجوز للوزارة قبولها.",
    status: { settled: "مستقرة", needed: "التعهدات مطلوبة", notNeeded: "غير مطلوبة", offered: "عرضت حزمة", testing: "اختبار السوق جار", awaitingVerdict: "بانتظار حكم الإدارة" },
    stageOpen: "مرحلة تصميم التعهدات",
    reportRecommends: "يوصي تقرير الدراسة بـ: {{code}}.",
    noReportYet: "لا يوجد تقرير دراسة معتمد بعد – مسألة التعهدات تلي التقييم التنافسي.",
    marketsOnFile: "الأسواق المعنية في الملف لحساب الأثر: {{count}}",
    notPublished: "لم ينشر الملف بعد، فلا يمكن فتح اختبار سوق",
    neededTitle: "يوصي التقرير بشروط أو بالرفض ولا توجد حزمة مطروحة",
    neededHint: "لا يمكن إنهاء التقييم حتى تعرض التعهدات وتقيم، أو يسجل غيابها. ويمكن دعوة الأطراف إلى عرض تعهدات عبر البوابة، أو اقتراح حزمة من الإدارة، أو تسجيل أنه لم يعرض شيء.",
    acceptedTitle: "قبلت الحزمة رقم {{v}}",
    acceptedHint: "ستطبع التعهدات ({{count}}) بوصفها شروط القرار وتنقل إلى سجل التعهدات مع شروط رقابتها.",
    none: "لا توجد حزمة تعهدات في الملف",
    noneHint: "تظهر هنا الحزم التي تعرضها الأطراف عبر البوابة، مع الأثر المحسوب لكل تعهد.",
    propose: "اقتراح حزمة",
    proposeHint: "حزمة من الإدارة (ما تقبله الوزارة) أو حزمة مسجلة باسم الأطراف. يحسب أثر كل تعهد عند الحفظ: التنازل يعيد تشغيل سوق الملف مع بيع الجزء المتنازل عنه؛ والتعهد السلوكي يقرأ بعبء رقابته.",
    proposeSubmit: "تسجيل الحزمة",
    proposed: "سجلت حزمة التعهدات؛ وأثرها المحسوب في الملف.",
    source: "مقدمة من",
    sources: { Parties: "الأطراف", Department: "الإدارة" },
    proposedByName: "سجلت باسم",
    proposedByNameHint: "اسم الموقع عندما تكون الحزمة من الأطراف.",
    rationale: "لماذا تعالج هذه الحزمة الضرر",
    rationaleHint: "أي نتيجة من نتائج التقرير يعالجها كل تعهد؛ وبكلمات الأطراف نفسها عند التسجيل باسمها.",
    rationaleAr: "المبررات (بالعربية)",
    commitment: "التعهد",
    addCommitment: "إضافة تعهد",
    package: "الحزمة رقم {{v}}",
    proposedOn: "عرضت في {{date}} من {{by}}",
    statuses: { Proposed: "مقترحة", UnderMarketTest: "قيد اختبار السوق", MarketTested: "اختبرت في السوق", Accepted: "مقبولة", Rejected: "مرفوضة", Withdrawn: "مسحوبة", Superseded: "مستبدلة", NoneOffered: "لم تعرض تعهدات" },
    kinds: { Structural: "هيكلي", Behavioural: "سلوكي", Reporting: "إبلاغي", Performance: "أدائي" },
    verdicts: { Sufficient: "كافية", InsufficientScope: "غير كافية في نطاقها", InsufficientEffectiveness: "غير كافية في فعاليتها", NotMonitorable: "غير قابلة للرقابة", Disproportionate: "غير متناسبة" },
    verdictHints: {
      Sufficient: "تزيل الحزمة الضرر الذي وجده التقرير، على الأثر المحسوب ونتائج اختبار السوق؛ والتعهدات الهيكلية ممثلة في الحساب وتعيد هيكلا قابلا للعمل.",
      InsufficientScope: "تترك الحزمة جزءا من الضرر دون معالجة – سوق أو منتج أو فئة من العملاء وجد التقرير أنها متضررة وليست مشمولة.",
      InsufficientEffectiveness: "تغطي الحزمة الضرر ولكنها لا تزيله – الجزء المتنازل عنه صغير جدا، أو المشتري غير مثبت، أو الوعد غير مرجح الأثر.",
      NotMonitorable: "لا يمكن التحقق من الحزمة عمليا – لا وصي، ولا مؤشر، والتزام لا تستطيع الوزارة ملاحظته.",
      Disproportionate: "تتجاوز الحزمة ما يقتضيه الضرر؛ وتقبل الإدارة حزمة أضيق.",
    },
    purchaserRequirements: { None: "لا شرط للمشتري", UpfrontBuyer: "مشتر مسبق", FixItFirst: "إصلاح أولا", ApprovedPurchaser: "مشتر معتمد" },
    fields: {
      kind: "النوع", obligor: "الملتزم", relevantMarket: "السوق المعنية", title: "العنوان", description: "التعهد",
      descriptionHint: "الالتزام كما سيطبع في المادة (1) من القرار – من يفعل ماذا، ولمن، وبأي شروط.",
      divestedBusiness: "الأعمال المتنازل عنها", divestedFrom: "متنازل عنها من", divestedSharePoints: "نقاط الحصة", divestedSharePointsHint: "حصة السوق المعنية التي يحملها الجزء المتنازل عنه؛ يحتاجها حساب الأثر.",
      purchaserRequirement: "شرط المشتري", purchaserName: "المشتري (إن حدد)", divestitureDeadlineMonths: "التنازل في غضون (أشهر)",
      monitoringTrustee: "وصي المراقبة", monitoringTrusteeHint: "وصي مستقل يرفع تقاريره إلى الوزارة؛ وبدونه تتحمل الوزارة عبء الرقابة كله.",
      verificationMethod: "كيفية التحقق من الالتزام", reviewIntervalDays: "الدورية (أيام)", durationMonths: "المدة (أشهر)", durationMonthsHint: "أجل الانتهاء؛ يترك فارغا للالتزام غير محدد المدة فقط.",
      reviewClause: "شرط المراجعة", effectivenessIndicator: "مؤشر الفعالية", effectivenessIndicatorHint: "ما سيقرأه التقييم اللاحق ليقول إن كان التعهد قد أفلح.",
    },
    sharePoints: "نقطة حصة",
    withinMonths: "مهلة التنازل بالأشهر: {{n}}",
    forMonths: "المدة بالأشهر: {{n}}",
    noSunset: "بلا أجل انتهاء",
    everyDays: "دورية التحقق بالأيام: {{n}}",
    sandbox: "أثر التعهدات",
    sandboxSummary: "الأثر المحسوب للحزمة",
    sandboxPending: "لم يحسب أثر هذه الحزمة بعد.",
    sandboxRun: "أعيد حساب الأثر على أسواق الملف الحالية.",
    rerunSandbox: "إعادة حساب أثر الالتزامات",
    combinedShare: "الحصة المجمعة بعد التعهد",
    restoresSafeHarbour: "يعيد الملاذ الآمن",
    aboveSafeHarbour: "تبقى الزيادة فوق الملاذ الآمن",
    verificationRounds: "جولات التحقق المتوقعة: {{n}} تقريبا",
    notModelled: "غير ممثل",
    assumptions: "الافتراضات: {{count}}",
    structuralUnmodelled: "تعذر تمثيل تعهد هيكلي في النموذج",
    structuralUnmodelledHint: "لم يعبر عن التنازل بنقاط حصة في سوق معنية في الملف. لا يمكن للإدارة أن تجد مثل هذه الحزمة كافية حتى يقاس الجزء المتنازل عنه ، لذا تطلب من الأطراف إيرادات الأعمال المتنازل عنها أو حجمها.",
    priceEffect: "أثر السعر",
    marketTest: {
      stances: { Support: "مؤيد", Oppose: "معارض", Neutral: "محايد", Mixed: "مختلط" },
      title: "اختبار السوق",
      open: "فتح اختبار سوق",
      publish: "نشر اختبار السوق",
      opened: "فتح اختبار السوق في السجل العام.",
      requiresPublication: "يجب نشر الملف قبل اختبار تعهداتها في السوق.",
      summaryPublic: "ملخص غير سري للتعهدات",
      summaryPublicHint: "ما سيقرأه الغير في السجل. لا أسرار تجارية؛ وبما يكفي ليقول العميل أو المنافس إن كان التعهد سيفلح.",
      summaryPublicAr: "الملخص (بالعربية)",
      workingDays: "أيام العمل",
      openDays: "مفتوح · الأيام المتبقية: {{count}}",
      closedOn: "أغلق في {{date}}",
      responses: "الردود: {{count}}",
      notOpened: "لا اختبار سوق بعد. ويتيح فتح اختبار أن يبدي العملاء والمنافسون والمنظم القطاعي رأيهم في فعالية الحزمة.",
      waitStage: "يمكن فتح اختبار السوق حين يكون الملف في مرحلة تصميم التعهدات (تنتقل إليها عند عرض حزمة).",
      recordView: "تسجيل رأي طرف ثالث",
      submitter: "الطرف الثالث",
      submitterType: "النوع",
      submitterTypes: { Competitor: "منافس", Customer: "عميل", Supplier: "مورد", Association: "جمعية تجارية", Regulator: "منظم قطاعي", Other: "أخرى" },
      email: "البريد الإلكتروني",
      content: "رأيه",
      viewRecorded: "سجل الرأي في اختبار السوق.",
      close: "إغلاق اختبار السوق",
      closeSubmit: "الإغلاق وتسجيل النتائج",
      closed: "أغلق اختبار السوق؛ والنتائج في الملف.",
      findings: "نتائج اختبار السوق",
      findingsHint: "ما تقوله الردود عن الحزمة – موزونة بوزنها الإثباتي ومصلحتها كما مع الاعتراضات – وما ينبغي تغييره إن وجد.",
      findingsAr: "النتائج (بالعربية)",
    },
    assess: "إعطاء حكم الإدارة",
    assessSubmit: "تسجيل الحكم",
    assessed: "سجل الحكم؛ ويعود الملف إلى التقييم ويعاد صوغ التقرير.",
    assessorRole: "يعطي الحكم خبير اقتصادي أو قانوني أو المدير.",
    verdict: "الحكم",
    reasoning: "التسبيب",
    reasoningAr: "التسبيب (بالعربية)",
    reasoningHint: "بضع جمل على الأقل؛ يقتبس في البند 4.7 من التقرير.",
    assessReasoningHint: "تقيم الحزمة على أثرها المحسوب ونتائج اختبار السوق: أي ضرر تزيله، وما تتركه، ولماذا. ويقتبس ذلك في البند 4.7 من التقرير وفي القرار.",
    departmentVerdict: "حكم الإدارة",
    acceptedNote: "تصير التعهدات المقبولة شروط الموافقة؛ ويبنى سجل التعهدات منها عند القرار.",
    noneOffered: "تسجيل أنه لم تعرض تعهدات",
    noneOfferedHint: "حين لا تعرض الأطراف شيئا ولا تجد الإدارة ما تقترحه، يجب أن يقول السجل ذلك – فالرفض يجب أن يظهر أن التعهدات قد استكشفت.",
    recordNone: "تسجيل",
    noneRecorded: "سجل: لم تعرض تعهدات.",
    withdraw: "سحب",
    withdrawHint: "ستعلم الحزمة رقم {{v}} مسحوبة. وإذا كان الملف في تصميم التعهدات فتعود إلى التقييم؛ ويجوز للأطراف عرض حزمة جديدة.",
    withdrawConfirm: "سحب الحزمة",
    withdrawn: "سحبت الحزمة.",
  },
  exemption: {
    bases: { Article5RestrictiveAgreement: "المادة (5) – اتفاق مقيد", Article6DominantPosition: "المادة (6) – ممارسة منشأة مسيطرة", Article7EconomicDependency: "المادة (7) – التبعية الاقتصادية", Article8PriceReduction: "المادة (8) – التسعير الافتراسي" },
    externalMember: "عضو من خارج الوزارة",
    agreement: "الاتفاق المخطر به", essential: "سلع أساسية", scorecard: "شروط المادة (9) التراكمية", applicant: "المخطر", validator: "المدقق", selfOnly: "الفحص الذاتي للمخطر فقط", assessmentPending: "تقييم المدقق بانتظار الاعتماد",
    suggestedConditions: "شروط تجعل الاتفاق مقبولا", redTeam: "الأسئلة التي ستطرحها اللجنة", validUntil: "ساري حتى",
    undertaking: "التعهد بعدم التنفيذ", undertakingHint: "لا يجوز للأطراف التنفيذ حتى صدور قرار الوزير المسبب (المادة 4(2)(و) من اللائحة التنفيذية). ويعد عدم صدور القرار خلال المدة رفضا للطلب (المادة 10(1) من المرسوم بقانون).", signed: "موقع", lapses: "موعد القرار", lapsed: "انقضت مدة البت – يعد الطلب مرفوضا ولا يجوز تنفيذ الاتفاق", committeeReceived: "استلمته اللجنة", clockStarts: "يسجل موعد القرار عند إحالة الملف إلى اللجنة.", undertakingText: "نص التعهد", notSigned: "التعهد غير موقع", notSignedHint: "يجب أن يطلب الفحص الشكلي التعهد الموقع قبل المضي في الملف.", perEstablishment: "تعهد كل منشأة", noUndertaking: "لا يوجد تعهد بعد", authorityOwn: "موقع المنشأة نفسها", authorityPowerOfAttorney: "بموجب وكالة خاصة", authorityVerified: "تحقق {{name}} من الصلاحية", verifyAuthority: "تأكيد صلاحية الموقع في إلزامها", authorityVerifiedToast: "تم التحقق من صلاحية الموقع",
    committee: "لجنة تنظيم المنافسة", committeeHint: "{{period.exemption.committeeReview}} يوم عمل لدراسة التقرير المفصل والتوصية للوزير.", committeeStatus: { Pending: "معلق", InReview: "قيد الدراسة", RecommendationIssued: "صدرت التوصية" }, notReferred: "لم تتم الإحالة بعد، وتتم باعتماد تقرير اللجنة.", reportPending: "أعد تقرير اللجنة، ويحال الملف باعتماده من النظرة العامة.", referredOn: "أحيل", votes: "الأصوات", castVote: "تصويت", voteNote: "ملاحظة (اختياري)", issueRecommendation: "إصدار توصية اللجنة", reasoning: "الحيثيات", submitRecommendation: "رفع التوصية", recommendation: "التوصية", recommendationIssued: "صدرت التوصية – ينتقل الملف إلى الوزير", voteRecorded: "تم تسجيل الصوت", referred: "تمت الإحالة إلى اللجنة",
    recommendations: { Grant: "منح", GrantWithConditions: "منح بشروط", Reject: "رفض", RequestMoreInfo: "طلب معلومات إضافية" },
    views: "مرئيات الأطراف المعنية", noViews: "لم تطلب مرئيات.", requestViews: "طلب مرئيات الأطراف المعنية ({{period.exemption.views}} يوم عمل)", requestViewsHint: "العملاء أو المنافسون أو الموردون المتأثرون بالاتفاق؛ {{period.exemption.views}} يوم عمل للرد.", viewsRequested: "تم طلب المرئيات", addInterestedParty: "يلزم إضافة الطرف في النظرة العامة أولا.",
    refer: "إحالة إلى اللجنة", runValidator: "تشغيل مدقق التوافق", runBrief: "تشغيل كاتب مذكرة اللجنة",
    combinedShare: "الحصة السوقية المجمعة للأطراف",
  },
  amendment: {
    missing: "تعذر تحميل نموذج التعديل.", changes: "التعديلات المطلوبة", changesHint: "كل تعديل تطلبه الأطراف مقابل الاستثناء كما منح.", noChanges: "لم يدرج أي تعديل.",
    asGranted: "كما منح", requested: "المطلوب", rationale: "أسباب طلب التعديل", original: "الاستثناء كما منح", noOriginal: "الاستثناء الأصلي غير مرتبط بهذا التعديل.",
    validUntil: "ساري حتى {{date}}", agreement: "الاتفاق", share: "الحصة السوقية المجمعة", term: "المدة", openOriginal: "فتح الاستثناء الأصلي", statedFigures: "الأرقام الواردة في التعديل",
    filing: "التقديم", declared: "قبل مقدم الطلب الإقرار", notDeclared: "لم يقبل الإقرار بعد", mandatory: "مطلوب", optional: "اختياري",
    fields: { agreementEnd: "تاريخ الانتهاء / المدة", agreementScope: "نطاق الاتفاق", parties: "الأطراف", relevantMarket: "السوق المعنية", conditions: "شروط الاستثناء", pricing: "آلية التسعير", territory: "الإقليم", other: "أخرى" },
  },
  appeal: {
    grounds: "أسباب التظلم", groundsHint: "كما وردت من المتظلم وفق المادة (34).", newEvidence: "أدلة جديدة مقدمة", review: "تقييم مراجع التظلمات", reviewPending: "يتحقق مراجع التظلمات من المقبولية والأسباب مقابل القرار الأصلي.",
    recommendations: { Accept: "التوصية بالقبول", PartiallyAccept: "التوصية بالقبول الجزئي", Reject: "التوصية بالرفض" },
    clocks: "المدد القانونية", inTime: "قدم خلال {{period.grievance.filing}} يوم عمل", late: "قدم بعد مهلة الـ{{period.grievance.filing}} يوم عمل", notified: "الإخطار بالقرار", filingDeadline: "مهلة التقديم", ministerDue: "موعد قرار الوزير", daysToImplicit: "الأيام المتبقية قبل الرفض الضمني: {{count}}", implicitApplied: "انقضت {{period.grievance.decision}} يوما – رفض ضمني", courtRoute: "الطعن أمام المحكمة متاح حتى {{date}}",
    fee: "الرسوم", refunded: "مردودة", unpaid: "غير مسددة", contested: "القرار المتظلم منه", reasoning: "الحيثيات", openOriginal: "فتح الملف الأصلي",
    outcomes: { Accepted: "قبل – أعيد النظر في القرار وردت الرسوم", Rejected: "رفض – القرار قائم", ImplicitRejection: "رفض ضمني (لا قرار خلال {{period.grievance.decision}} يوما)" },
  },
  interest: {
    promptTitle: "هل لك مصلحة في هذا الملف؟",
    promptBody: "مصلحة مالية، أو عمل لدى أحد الأطراف، أو صلة قرابة، أو مشورة سابقة. ويحال التصريح إلى المدير قبل أي إجراء في الملف.",
    promptBodyOptional: "مصلحة مالية، أو عمل لدى أحد الأطراف، أو صلة قرابة، أو مشورة سابقة. ويحال التصريح إلى المدير.",
    none: "لا مصلحة لي", have: "التصريح بمصلحة", nature: "طبيعة المصلحة", details: "بيان المصلحة", detailsHint: "جملة أو جملتان: ما هي المصلحة وبأي طرف تتعلق.",
    stepAside: "التنحي عن هذا الملف الآن (دون اتخاذ أي إجراء فيه بعد ذلك)", declare: "التصريح بالمصلحة", cancel: "إلغاء",
    pendingTitle: "المصلحة المصرح بها بانتظار قرار المدير", pendingBody: "المصلحة المصرح بها: {{nature}}. وإلى أن يقرر المدير، يقتصر العمل على الاطلاع على الملف دون اتخاذ أي إجراء فيه.",
    recusedTitle: "تمت التنحية عن هذا الملف", recusedBody: "يبقى الاطلاع على الملف متاحا، دون اتخاذ أي إجراء فيه أو إسناده.", decidedBy: "بقرار من {{name}} بتاريخ {{date}}",
    awaitingTitle: "تصريح بمصلحة من {{name}} – بانتظار القرار", declaredOn: "تاريخ التصريح: {{date}}", decisionNote: "أسباب القرار", decisionNoteHint: "تسجل في سجل الملف وفي سجل التدقيق.",
    clear: "الاستمرار في الملف", recuse: "التنحية عن الملف",
    noneToast: "سجل التصريح", declaredToast: "تم التصريح بالمصلحة", clearedToast: "سجل قرار الاستمرار في الملف", recusedToast: "سجل قرار التنحية عن الملف",
  },
  report: {
    title: "بلاغ عن مخالفة",
    identified: "مبلغ معرف",
    entities: "المنشآت المعنية",
    observed: "فترة الملاحظة",
    ongoing: "مستمر",
    evidence: "الأدلة المتاحة",
    narrative: "إفادة المبلغ",
    study: "دراسة الظاهرة وتقييم الأثر",
    studyHint: "يتطلب المسار 6 من الإدارة دراسة الظاهرة المبلغ عنها وآثارها السلبية وكافة أبعادها قبل تجميع المعلومات.",
    phenomenon: "دراسة الظاهرة",
    impact: "تقييم الأثر",
    protection: "حماية المبلغ",
    anonymousTitle: "بلاغ مجهول",
    anonymousHint: "لم تجمع أي بيانات هوية أو اتصال. يتابع المبلغ البلاغ برمز الإيصال.",
    encryptedTitle: "بيانات الاتصال مشفرة",
    encryptedHint: "محفوظة بحماية البيانات؛ الكشف عنها مقصور على رئيس قسم الشكاوى والمدير ويسجل في سجل التدقيق.",
    reveal: "الكشف عن بيانات الاتصال (مسجل)",
    revealRole: "لا يمكن الكشف عن بيانات الاتصال إلا لرئيس قسم الشكاوى أو المدير.",
    ask: {
      title: "المراسلة مع المبلغ", hint: "تصل الأسئلة إلى المبلغ عبر رمز الإيصال، وترد الإجابات والملفات هنا. تبقى هوية المبلغ غير معروفة.",
      awaiting: "بانتظار إجابة المبلغ", empty: "لم يرسل أي سؤال إلى المبلغ بعد.", ministry: "الإدارة", reporter: "المبلغ", attachments: "ملفات من المبلغ",
      suggested: "أسئلة لم يجب عنها البلاغ", question: "السؤال الموجه إلى المبلغ", questionAr: "النسخة العربية", questionArHint: "اختياري – يظهر للمبلغ الذي يطلع على البلاغ بالعربية.",
      identityGuard: "هذا البلاغ مجهول الهوية. يلزم ألا يطلب السؤال الاسم أو بيانات التواصل أو أي معلومة تكشف هوية المبلغ.",
      send: "إرسال السؤال", sent: "أرسل السؤال. يطلع عليه المبلغ باستخدام رمز الإيصال.",
      suggestions: {
        when: "متى لوحظ هذا السلوك أول مرة، وهل ما زال مستمرا؟",
        evidence: "هل تتوفر مستندات أو رسائل أو قوائم أسعار تثبت السلوك؟ يمكن إرفاقها في صفحة حالة البلاغ باستخدام رمز الإيصال.",
        others: "هل توجد منشآت أخرى معنية، مثل منافسين أو موزعين يطبقون الأسعار أو الشروط نفسها؟",
        source: "ما مصدر العلم بهذا السلوك، مثلا بصفة عميل أو مورد أو موظف؟",
      },
    },
    signal: "إشارة السوق المرتبطة",
    signalHint: "يربط مراقب السوق كل بلاغ بإشارات الأسعار والمناقصات والأخبار في التدفق.",
    noSignal: "لا توجد إشارة سوق مرتبطة بهذا البلاغ بعد.",
    notice: {
      title: "الإخطار بفتح التحقيق",
      hint: "تخطر كل منشأة قيد التحقيق كتابة بفتح التحقيق: رقم الملف والسند القانوني وموضوع التحقيق وحقوقها. ويجوز إرجاء الإخطار ما دام يضر بالتحقيق، لكن لا يمكن إنهاء الاجتماعات مع الأطراف قبل إخطار كل المنشآت.",
      notYet: "يرسل الإخطار عند انتقال الملف إلى مرحلة التحقيق.",
      pendingTitle: "لم تخطر المنشآت بعد",
      pendingBody: "يلزم إرسال الإخطار، أو تسجيل سبب إرجائه والموعد الذي يعاد فيه النظر.",
      sent: "أرسل",
      deferred: "مرجأ",
      pending: "لم يرسل بعد",
      decidedBy: "بقرار من {{name}} في {{date}}",
      deferredUntil: "مرجأ حتى {{date}}",
      lapsedTitle: "انقضى موعد الإرجاء",
      lapsedBody: "يلزم إرسال الإخطار الآن، أو تسجيل سبب استمرار الإرجاء.",
      letters: "الخطابات المرسلة",
      undertakings: "المنشآت المطلوب إخطارها",
      undertakingsHint: "المنشآت التي يسميها البلاغ وأي منشأة مدرجة في الملف. لا يذكر اسم المبلغ في الإخطار أبدا.",
      addUndertaking: "إضافة منشأة",
      name: "الاسم",
      nameAr: "الاسم بالعربية",
      email: "البريد الإلكتروني للخطاب",
      subject: "موضوع التحقيق",
      subjectAr: "الموضوع بالعربية",
      subjectHint: "السلوك المشتبه فيه والسوق، دون أي معلومة قد تكشف هوية المبلغ أو أدلة سرية.",
      send: "إرسال الإخطار",
      sendMore: "إخطار منشأة أخرى",
      defer: "إرجاء الإخطار",
      extend: "تمديد الإرجاء",
      reason: "سبب الإرجاء",
      note: "ملاحظة",
      noteHint: "مطلوبة إذا كان السبب سببا آخر.",
      until: "يعاد النظر في موعد أقصاه",
      untilHint: "{{days}} يوما على الأكثر. تستحق مهمة إعادة النظر في هذا التاريخ.",
      recordDeferral: "تسجيل الإرجاء",
      cancel: "إلغاء",
      role: "يرسل رئيس قسم الشكاوى أو المدير الإخطار أو يقرر إرجاءه.",
      sentToast: "تم إرسال الإخطار",
      deferredToast: "سجل الإرجاء",
    },
  },
  advocacy: {
    title: "السياسات",
    subtitle: "تقييم أثر القوانين واللوائح والسياسات الصادرة عن الوزارات والهيئات الاتحادية والدوائر المحلية على المنافسة – تطبيق أداة منظمة التعاون الاقتصادي والتنمية لتقييم المنافسة بواسطة وكيل تقييم المنافسة بموجب المادة (18).",
    newReview: "تقييم تشريع",
    tabs: { reviews: "المراجعات", framework: "المنهجية وقائمة التحقق" },
    stats: { total: "تشريعات تم تقييمها", inProgress: "قيد التحليل: {{count}}", inReview: "بانتظار الاعتماد", inReviewHint: "مسودات في صندوق الذكاء الاصطناعي", adopted: "آراء معتمدة", adoptedHint: "اعتمدتها الوزارة", highOrSevere: "عال أو شديد", highOrSevereHint: "متوسط المؤشر {{index}}/100", flagged: "أحكام معلمة", flaggedHint: "في جميع التشريعات" },
    filters: { search: "العنوان أو الجهة أو المرجع", allStatuses: "كل الحالات", allBands: "كل النطاقات", allStages: "كل المراحل" },
    status: { Received: "مستلم", Screening: "تم الفرز", Analysing: "قيد التحليل", InReview: "بانتظار الاعتماد", Adopted: "معتمد" },
    band: { None: "لا قيود", Low: "منخفض", Moderate: "متوسط", High: "عال", Severe: "شديد" },
    severity: { None: "لا شيء", Indicative: "إشاري", Likely: "محتمل", Strong: "قوي" },
    verdict: { NoConcern: "لا مخاوف تنافسية", MinorConcerns: "مخاوف محدودة", SignificantConcerns: "مخاوف جوهرية", SevereConcerns: "مخاوف شديدة" },
    pit: { Justified: "مبرر", PartlyJustified: "مبرر جزئيا", NotJustified: "غير مبرر", InsufficientInformation: "معلومات غير كافية" },
    stage: { Draft: "مسودة", InForce: "نافذ", Proposed: "تعديل مقترح", Consultation: "استشارة عامة" },
    instrumentTypes: { Law: "قانون اتحادي", DecreeLaw: "مرسوم بقانون", CabinetResolution: "قرار مجلس الوزراء", MinisterialDecision: "قرار وزاري", Circular: "تعميم", Policy: "سياسة", Guideline: "لائحة / دليل", DraftBill: "مشروع قانون", LocalOrder: "أمر محلي (إمارة)", Other: "أخرى" },
    columns: { instrument: "التشريع", authority: "الجهة المصدرة", index: "المؤشر", provisions: "الأحكام", flagged: "مرصودة", status: "الحالة", updated: "آخر تحديث" },
    provisionsFlagged: "المعلمة: {{flagged}} من {{total}}",
    strongCount: "قوية: {{count}}",
    empty: "لم يسجل أي تشريع بعد.",
    emptyFiltered: "لا توجد مراجعة تطابق هذه المرشحات.",
    byAuthority: "التشريعات حسب الجهة المصدرة",
    byBand: "نطاقات التقييد",
    recent: "أحدث المراجعات",
    dialog: {
      title: "تشريع جديد",
      hint: "ملف التشريع أو نصه، مع الجهة المصدرة والمرحلة. ولأن منهجية القسم بانتظار اعتماد الإدارة، يسجل التشريع دون فرز أو تقييم.",
      upload: "تحميل ملف",
      paste: "لصق النص",
      dropzone: "ملف التشريع",
      dropzoneHint: "PDF أو وورد (.docx) أو نص أو صورة ممسوحة · حتى 25 ميغابايت",
      textPlaceholder: "النص الكامل للقانون أو اللائحة أو السياسة، بالعربية أو الإنجليزية.",
      titleLabel: "العنوان",
      titlePlaceholder: "مثال: مشروع لائحة إنتاج الخرسانة الجاهزة",
      titleArLabel: "العنوان (بالعربية)",
      authority: "الجهة المصدرة",
      authorityPlaceholder: "مثال: وزارة الطاقة والبنية التحتية، بلدية دبي",
      jurisdiction: "الاختصاص",
      instrumentType: "نوع التشريع",
      stage: "المرحلة",
      reference: "الرقم المرجعي",
      referencePlaceholder: "مثال: مشروع رقم 14/2026",
      sector: "القطاع (اختياري)",
      noSector: "غير محدد",
      notes: "ملاحظات (اختيارية)",
      notesPlaceholder: "سياق من الجهة المصدرة: هدف السياسة، وموعد الاستشارة، والنقاط المثارة.",
      submit: "تسجيل",
      submitting: "جار قراءة التشريع…",
      created: "تم استلام التشريع. الأحكام: {{provisions}}، المعلمة منها: {{flagged}}، المؤشر: {{index}} من 100. ويعد الوكيل مسودة التقييم.",
      needText: "ملف التشريع أو نصه مطلوب.",
    },
    detail: {
      back: "كل المراجعات",
      overview: "نظرة عامة",
      provisions: "الأحكام",
      opinion: "الرأي",
      instrument: "نص التشريع",
      sources: "المصادر",
      sourceTypes: { Regulation: "مستودع المعرفة القانونية", Document: "مستند الملف", Tool: "مخرجات الأداة" },
      indexTitle: "مؤشر تقييد المنافسة",
      indexHint: "يرتب التشريعات حسب الأولوية – وليس مقياسا للضرر. النطاقات: 0 لا شيء · 1–14 منخفض · 15–34 متوسط · 35–59 عال · 60+ شديد.",
      checklistTitle: "قائمة تحقق المنافسة",
      checklistHint: "بنود منظمة التعاون الاقتصادي والتنمية من A1 إلى D3 وبنود هيئة المنافسة والأسواق البريطانية من E1 إلى E2. اللون هو أعلى درجة خطورة، والرقم عدد الأحكام التي تمس البند.",
      marketGate: "بوابة السوق",
      marketGatePassed: "يؤثر التشريع في سوق للسلع أو الخدمات",
      marketGateFailed: "لا يبدو أن التشريع ينظم سوقا – تنظيمي أو إجرائي",
      affectedMarkets: "الأسواق المتأثرة",
      structureOnFile: "البنية المسجلة",
      exposure: "درجة التأثر",
      objectives: "أهداف السياسة",
      executiveSummary: "الملخص التنفيذي",
      publicInterest: "اختبار المصلحة العامة",
      publicInterestHint: "لا يقبل القيد إلا إذا رجحت منفعته للمجتمع على كلفته على المنافسة ولم يمكن تحقيق الهدف بوسيلة أقل تقييدا (الخطوة الثانية في منهجية منظمة التعاون الاقتصادي والتنمية؛ اتفاقية مبادئ المنافسة الأسترالية، البند 5).",
      pitFields: { policyObjective: "هدف السياسة", objectiveLegitimacy: "المشروعية", necessity: "الضرورة", proportionality: "التناسب", benefitsClaimed: "المنافع المزعومة", costsToCompetition: "الكلفة على المنافسة" },
      alternatives: "بدائل أقل تقييدا",
      altColumns: { restriction: "القيد", alternative: "البديل", how: "كيف يحقق الهدف", residual: "الخطر المتبقي" },
      recommendations: "التوصيات إلى الجهة المصدرة",
      openQuestions: "معلومات مطلوبة من الجهة المصدرة",
      comparative: "المراجع المقارنة – ممارسات أجنبية، وليست سوابق",
      provisionColumns: { provision: "الحكم", restriction: "القيد", items: "البنود", severity: "الخطورة", alternative: "البديل الأقل تقييدا" },
      provisionFields: { mechanism: "الآلية", who: "من يتحمل الكلفة", objective: "الهدف المحقق", alternative: "البديل", legalBasis: "الأساس القانوني", screen: "الفرز" },
      screenOnly: "تقييم الوكيل غير متاح بعد – هذا هو الفرز القطعي: قيود محتملة وليست نتائج.",
      rulesDraftProvisions: "قراءات هذه الأحكام من مسودة محرك القواعد (الفرز الآلي) وليست تقييما مكتملا من النموذج. ويلزم تأكيد كل علامة أو استبعادها قبل الاعتماد.",
      noFlags: "لم يرصد أي قيد على المنافسة في التشريع.",
      showClean: "إظهار الأحكام غير المعلمة",
      hideClean: "إخفاء الأحكام غير المعلمة",
      matched: "الصياغة المطابقة",
      mitigated: "وجدت عبارة مخففة",
      opinionDraft: "مسودة ذكاء اصطناعي – لم تعتمد بعد",
      opinionRulesDraft: "مسودة محرك القواعد – لم تعتمد بعد",
      opinionAdopted: "اعتمده {{name}} في {{date}}",
      opinionAdoptedNoName: "اعتمد في {{date}}",
      opinionPending: "وكيل تقييم المنافسة يعد مسودة الرأي. تحدث هذه الصفحة تلقائيا.",
      runFailed: "فشل آخر تشغيل للوكيل قبل كتابة المسودة – ما تراه هو نتيجة الفرز الآلي. ويعاد تشغيل التقييم بزر «إعادة التوليد».",
      runFailedWithError: "فشل آخر تشغيل للوكيل قبل كتابة المسودة ({{error}}). ما تراه هو الفرز القطعي: قيود محتملة وليست نتائج. ويعاد تشغيل التقييم بزر «إعادة التوليد».",
      rulesDraft: "تعذر على النموذج اللغوي إكمال هذا التشغيل. ما تراه مسودة من محرك القواعد انطلاقا من الفرز القطعي: علامات قائمة التحقق حولت إلى رأي أولي. ينبغي للخبير الاقتصادي استكمال القراءة قبل الاعتماد، أو استخدام «إعادة التوليد» عندما يتاح النموذج.",
      rulesDraftWithError: "تعذر على النموذج اللغوي إكمال هذا التشغيل ({{error}}). ما تراه مسودة من محرك القواعد انطلاقا من الفرز القطعي: علامات قائمة التحقق حولت إلى رأي أولي. ينبغي للخبير الاقتصادي استكمال القراءة قبل الاعتماد، أو استخدام «إعادة التوليد» عندما يتاح النموذج.",
      assessmentSource: "مصدر المسودة",
      assessmentSourceModel: "النموذج اللغوي",
      assessmentSourceRules: "محرك القواعد (احتياطي)",
      adoptInInbox: "الاعتماد في صندوق الذكاء الاصطناعي",
      adoptHint: "يصبح الرأي رأي الوزارة عندما يوافق الخبير الاقتصادي على المقترح في صندوق الذكاء الاصطناعي. التعديلات هناك هي ما يعتمد.",
      regenerate: "إعادة الإعداد",
      regenerateNote: "ملاحظة للوكيل (اختياري)",
      regenerateNotePlaceholder: "مثال: المادة 7 معيار للجودة وليست سقفا؛ وقد أكدت الجهة أن التراخيص غير محدودة.",
      regenerating: "تمت جدولة إعادة الإعداد – الوكيل يعيد قراءة التشريع.",
      exportPdf: "الرأي PDF",
      exportPdfAr: "PDF بالعربية",
      exported: "تم تصدير الرأي",
      delete: "حذف المراجعة",
      deleteConfirm: "حذف هذه المراجعة ومسودتها؟ لا يمكن حذف الآراء المعتمدة.",
      deleted: "تم حذف المراجعة",
      english: "English",
      arabic: "العربية",
      confidence: "الثقة",
      quality: "الجودة",
      run: "تشغيل الوكيل",
      requestedBy: "طلبها",
      language: "اللغة",
      pages: "{{count}} صفحة", pages_zero: "بلا صفحات", pages_one: "صفحة واحدة", pages_two: "صفحتان", pages_few: "{{count}} صفحات", pages_many: "{{count}} صفحة", pages_other: "{{count}} صفحة",
      pasted: "نص ملصوق",
      viewSource: "عرض المصدر",
      meta: { reference: "المرجع", instrument: "التشريع", stage: "المرحلة", jurisdiction: "الاختصاص", sector: "القطاع", authority: "الجهة المصدرة", received: "تاريخ الاستلام" },
      noSources: "لم تسجل مصادر مسترجعة بعد.",
      notFound: "هذه المراجعة غير موجودة أو حذفت.",
    },
    framework: {
      title: "كيف تقيم الأداة تشريعا",
      intro: "تتبع المنهجية أداة منظمة التعاون الاقتصادي والتنمية لتقييم المنافسة، وإرشادات هيئة المنافسة والأسواق البريطانية لتقييم الأثر التنافسي، وممارسات شبكة المنافسة الدولية في مجال السياسات، مكيفة مع اختصاصات الوزارة بموجب المرسوم بقانون اتحادي رقم (36) لسنة 2023. الخطوة الأولى فرز قطعي؛ الثانية تقييم معمق من الوكيل لكل حكم معلم؛ ثم يعتمد الخبير الاقتصادي الرأي.",
      steps: { one: "1 · الفرز", oneHint: "تقسيم النص إلى أحكام ومطابقة كل حكم مع معجم قائمة التحقق ثنائي اللغة. المخرجات: علامات، درجة خطورة، مؤشر تقييد.", two: "2 · التقييم", twoHint: "يقرأ الوكيل كل حكم معلم: القيد، الآلية، من يتحمل الكلفة، الهدف، البديل الأقل تقييدا، اختبار المصلحة العامة – مستندا إلى المتن القانوني وبنك البيانات.", three: "3 · الاعتماد", threeHint: "يراجع الخبير الاقتصادي المسودة في صندوق الذكاء الاصطناعي ويعدلها ويعتمدها رأيا للوزارة موجها إلى الجهة المصدرة." },
      checklist: "قائمة تحقق المنافسة",
      sources: { oecd: "مجموعة أدوات تقييم المنافسة لمنظمة التعاون الاقتصادي والتنمية، قائمة التحقق من المنافسة (المجلد الأول، الإصدار 4.0)", cma: "هيئة المنافسة والأسواق في المملكة المتحدة، تقييم الأثر على المنافسة: إرشادات لواضعي السياسات (2015، حدثت في يوليو 2023)" },
      checklistColumns: { item: "البند", question: "السؤال", alternatives: "بدائل أقل تقييدا معتادة", points: "النقاط الأساسية", source: "المصدر" },
      index: "مؤشر التقييد",
      screen: "الفرز",
      guidance: "الإرشادات في المتن القانوني",
      guidanceHint: "الإرشادات الأجنبية ومسودة المذكرة الإرشادية التي استمدت منها المنهجية المقترحة. ولا يطبق أي منها قبل اعتماد الإدارة للمنهجية.",
      instrumentTypes: "أنواع التشريعات المقبولة",
    },
  },
  legal: {
    answerEn: "الإجابة بالإنجليزية", answerAr: "الإجابة بالعربية",
    precedents: "السوابق",
    precedentsHint: "بحث محلل السياسات في القرارات الصادرة وتقارير التحقيق والردود الرسمية – نفس الفهرس الذي يستشهد به الوكيل عند صياغة الرد.",
    precedentPlaceholder: "مثال: تنسيق أسعار الأسمنت، حدود المشروع المشترك، سعر إعادة البيع…",
    precedentKinds: { Decision: "قرار", Report: "تقرير", OfficialAnswer: "رد رسمي" },
    relevance: "الصلة",
    noPrecedents: "لا توجد سوابق مطابقة لهذه المصطلحات.",
    subtitle: "الاستفسارات المصعدة بانتظار رد رسمي، واعتماد الأسئلة الشائعة، والمتن القانوني الذي يستند إليه المساعد العام.",
    addInstrument: "إضافة تشريع",
    importPrecedents: "استيراد السوابق",
    precedentsImported: "تم استيراد السوابق: {{files}} من القرارات المنشورة و{{decisions}} من قرارات المنصة",
    addInstrumentHint: "يلصق نص قانون أو قرار أو دليل، فيقسم على مستوى المواد ويضمن ويصبح قابلا للاستشهاد فورا.",
    key: "المفتاح",
    kind: "النوع",
    kinds: { Law: "قانون", CabinetDecision: "قرار مجلس الوزراء", MinisterialDecision: "قرار وزاري", Guideline: "دليل إرشادي", Procedure: "إجراء", Precedent: "سابقة", ComparativeReference: "مرجع مقارن (قرار جهة أجنبية)", Faq: "سؤال شائع", InternationalGuidance: "إرشادات دولية" },
    titleAr: "العنوان (عربي)",
    issuer: "الجهة المصدرة",
    text: "النص",
    textHint: "تحدد عناوين مثل «المادة 12» حدود المقاطع.",
    ingest: "إدراج",
    ingested: "تم الإدراج. المقاطع المضمنة: {{count}}",
    chunkCount_zero: "لا مقاطع",
    chunkCount_one: "مقطع واحد",
    chunkCount_two: "مقطعان",
    chunkCount_few: "{{count}} مقاطع",
    chunkCount_many: "{{count}} مقطعا",
    chunkCount_other: "{{count}} مقطع",
    stats: { documents: "التشريعات في المتن", chunks: "مقطع", embedded: "مضمن", enquiries: "محادثات الاستفسار", escalated: "مصعدة إلى الباحثين: {{count}}", confidence: "متوسط ثقة الإجابات", unsupported: "ادعاءات غير مدعومة رصدها المدقق: {{count}}", faqs: "أسئلة شائعة منشورة", faqsHint: "ردود رسمية يعيد المساعد استخدامها" },
    enquiries: "الاستفسارات",
    corpus: "المتن القانوني",
    corpusTitle: "التشريعات",
    corpusHint: "إيقاف التشريع يستبعده من الاستدعاء؛ ويعاد التضمين بعد تعديل النص.",
    testRetrieval: "اختبار الاستدعاء",
    testRetrievalHint: "عرض المواد التي سيستشهد بها المساعد لسؤال معين.",
    searchPlaceholder: "مثال: حدود الإخطار للمشاريع المشتركة",
    reembed: "إعادة التضمين",
    reembedded: "أعيد تضمين المقاطع",
    active: "نشط",
    embedded: "مضمن",
    notEmbedded: "بدون تضمين",
    noEnquiries: "لا استفسارات مصعدة",
    noEnquiriesHint: "محادثات مجهولة أجاب عنها المساعد دون تصعيد: {{count}}.",
    question: "السؤال",
    messages: "رسائل",
    messageCount: "{{count}} رسالة",
    messageCount_zero: "لا رسائل",
    messageCount_one: "رسالة واحدة",
    messageCount_two: "رسالتان",
    messageCount_few: "{{count}} رسائل",
    messageCount_many: "{{count}} رسالة",
    messageCount_other: "{{count}} رسالة",
    askedIn: { en: "بالإنجليزية", ar: "بالعربية" },
    statuses: { Open: "مفتوح", Escalated: "لدى باحث", Answered: "تم الرد", Closed: "مغلق", Flagged: "نبهت إليه الفحوص" },
    roles: { user: "المستفسر", assistant: "المساعد", officer: "الباحث القانوني" },
    draftReady: "مسودة الرد جاهزة",
    draftReadyHint: "أعد محلل السياسات مسودة رد رسمي، ويمكن فتحها في المحرر أو كتابة الرد أدناه.", useDraft: "فتح المسودة في المحرر",
    fromDraft: "مسودة محلل السياسات · درجة الثقة {{confidence}}%", fromDraftHint: "المسودة في المحرر. يلزم التحقق من الأحكام التي تستشهد بها وتعديل الصياغة؛ وإصدارها يعني اعتماد المسودة كما عدلت.",
    draftSources: "المصادر التي تستند إليها المسودة", startBlank: "البدء برد فارغ", draftApprovedNote: "عدلها الباحث القانوني وأصدرها من محرر الرد",
    published: "سؤال شائع منشور",
    notPublished: "غير منشور",
    faq: "سؤال شائع",
    enquiry: "الاستفسار",
    openCase: "فتح الملف",
    notEscalated: "أجاب عنه المساعد – لم يصعد",
    officialAnswer: "الرد الرسمي",
    publishFaq: "نشر كسؤال شائع",
    publishFaqHint: "يظهر في صفحة الاستفسارات القانونية العامة ويستخدمه المساعد كمصدر.",
    writeAnswer: "الرد الرسمي",
    writeAnswerHint: "يصدر كرسالة إلى المستفسر ويغلق مهلة الـ{{period.enquiry.answer}} أيام عمل.",
    issueAnswer: "إصدار الرد الرسمي",
    approveAnswer: "اعتماد الرد",
    answerArRequired: "يصدر الرد الرسمي باللغتين العربية والإنجليزية: يلزم النص العربي.",
    approvedAwaiting: "معتمد – بانتظار الإصدار",
    correctedAtIssue: "صحح عند الإصدار – ويحفظ النص الذي اعتمده {{name}} بتاريخ {{date}} كما اعتمد",
    approvedBy: "اعتمده {{name}} بتاريخ {{date}}. راجعه مرة أخيرة؛ ويمكن إجراء تصحيح أخير قبل إرساله.",
    issueApproved: "إصدار الرد الرسمي",
    faqQuestion: "السؤال كما ينشر",
    faqQuestionHint: "بعد إخفاء الهوية: دون أسماء أو أرقام أو تفاصيل تجارية. معبأ من الاستفسار بعد حذفها؛ ويحرر قبل النشر.",
    publishFaqAction: "النشر في الأسئلة الشائعة",
    answerIssued: "تم إصدار الرد الرسمي",
    conversation: "المحادثة",
    conversationHint: "إجابات المساعد مستندة إلى المتن القانوني؛ تعرض درجة الثقة لكل إجابة.",
    escalationReason: "سبب التصعيد",
    noConversation: "لا توجد محادثة مرتبطة بهذا الملف.",
  },
  fileAccess: {
    title: "الاطلاع على الملف",
    hint: "قبل أي قرار ضدها تطلع المنشآت المعنية على الملف: كل مستند كاملا أو بملخص غير سري أو يحجب مع بيان السبب. لا يمضي التقرير قبل انقضاء المدة أو رد جميع المنشآت. يسجل كل تنزيل على البوابة.",
    openUntil: "متاح حتى {{date}}",
    closed: "مغلق",
    notOpened: "لم يتح بعد",
    notRequired: "لا تشترط إعدادات المنصة الاطلاع على الملف، ويمكن إتاحته مع ذلك.",
    noPartiesTitle: "لا توجد منشأة معنية بعد",
    noParties: "يتاح الملف للمنشآت المعنية بعد تسجيلها في الملف، كأن يرسل إخطار فتح التحقيق أو طلب معلومات.",
    parties: "المنشآت المعنية",
    portal: "على البوابة",
    letterOnly: "بخطاب",
    openedBy: "أتاحه {{name}} في {{date}}",
    closes: "ويغلق في {{date}}",
    closedBy: "أغلقه {{name}} في {{date}}",
    inspections_zero: "لم يسجل أي تنزيل على البوابة",
    inspections_one: "تنزيل واحد مسجل على البوابة",
    inspections_two: "تنزيلان مسجلان على البوابة",
    inspections_few: "{{count}} تنزيلات مسجلة على البوابة",
    inspections_many: "{{count}} تنزيلا مسجلا على البوابة",
    inspections_other: "{{count}} تنزيل مسجل على البوابة",
    index: "فهرس الملف",
    empty: "لا يوجد مستند في الملف بعد.",
    access: "الاطلاع",
    levels: { Full: "كاملا", Summary: "ملخص غير سري", Withheld: "محجوب" },
    summary: "ملخص غير سري",
    summaryHint: "ما يبينه المستند دون الأسرار التجارية (20 حرفا على الأقل).",
    summaryAr: "الملخص بالعربية",
    reason: "سبب الحجب",
    otherReason: "سبب آخر…",
    ownReason: "اذكر السبب",
    days: "أيام العمل المتاحة",
    daysHint: "بين {{min}} و{{max}} يوم عمل.",
    prepare: "إعداد الاطلاع على الملف",
    reopen: "إتاحة الملف مرة أخرى",
    open: "إتاحة الملف وإرسال الخطابات",
    cancel: "إلغاء",
    incomplete: "قدم ملخصا لكل مستند يتاح بملخص، وسببا لكل مستند محجوب.",
    closeEarly: "الإغلاق قبل الموعد",
    closeReason: "سبب إغلاق الملف قبل الموعد",
    closeReasonHint: "ردت جميع المنشآت أو تنازلت عن الاطلاع كتابة (20 حرفا على الأقل).",
    close: "إغلاق الملف",
    openedToast: "أتيح الملف للمنشآت وأرسلت الخطابات.",
    closedToast: "أغلق الملف.",
    role: "يتيح رئيس قسم الشكاوى أو المدير الملف للمنشآت.",
  },
  investigation: {
    outcome: {
      title: "النتيجة الموصى بها",
      hint: "يعرض التقرير النهائي النتيجتين الممكنتين قانونا مع الأدلة المؤيدة والمعارضة لكل منهما، وتترك مسودته الاختيار مفتوحا. ويختار الموظفون إحداهما ويبينون أسبابهم قبل الاعتماد. ثم يراجع التقرير المعتمد خبير من خارج فريق الملف.",
      draftPick: "تقترح المسودة: {{outcome}}",
      draftNone: "لا توصي المسودة بأي من النتيجتين",
      evidenceFor: "الأدلة المؤيدة",
      evidenceAgainst: "الأدلة المعارضة",
      notWrittenUp: "لم تعرضها المسودة – يعرضها الموظفون في التقرير.",
      fullText: "التقرير كاملا",
      fullTextHint: "يقرأ التقرير حتى نهايته قبل اعتماده؛ ويعد مفتوحا متى ظهرت نهايته على الشاشة.",
      writeUp: "كتابة نتيجة «{{outcome}}» مع الأدلة المؤيدة لها والمعارضة",
      writeUpHint: "أغفلت المسودة هذه النتيجة المشروعة. تكتب النتيجتان كلتاهما قبل اعتماد التقرير ({{min}} حرفا على الأقل).",
      choose: "النتيجة التي يوصي بها الموظفون",
      reason: "أسباب الموظفين",
      reasonHint: "بعباراتكم: أي أسئلة الاختبار القانوني تجيب عنها الأدلة، ولماذا تحمل هذه النتيجة دون الأخرى ({{min}} حرفا على الأقل).",
      approve: "اعتماد التقرير بهذه النتيجة",
      approved: "اعتمد التقرير النهائي بالنتيجة التي اختارها الموظفون.",
      departs: "تخالف اقتراح المسودة",
      chosen: "اعتمد بتاريخ {{date}} – يوصي الموظفون بـ: {{outcome}}",
      reasonLabel: "الأسباب",
    },
    evidenceWeight: "الوزن {{w}}",
    notifyParties: "إخطار المشكو ضدهم (الرد خلال {{period.complaint.response}} يوم عمل)",
    notified: "تم إخطار المشكو ضدهم ({{count}})",
    requestInfo: "طلب معلومات",
    requestInfoHint: "{{period.complaint.informationRequest}} يوم عمل للرد؛ تذكيرات بعد {{period.informationRequest.firstReminder}} ثم {{period.informationRequest.secondReminder}} أيام عمل إضافية (إجراءات الوزارة)، ثم إخطار المادة 23(3) الذي يمنح {{period.complaint.finalNotice}} أيام عمل إضافية، وتعتمدونه وترسلونه بأنفسكم.",
    recipient: "المرسل إليه",
    questions: "الأسئلة",
    questionsHint: "سؤال واحد في كل سطر.",
    rfiSent: "تم إرسال طلب المعلومات",
    firstReminder: "التذكير الأول",
    schedule: "جدولة اجتماع / زيارة ميدانية",
    scheduleHint: "تحرر محاضر جلسات الأطراف والزيارات الميدانية المفاجئة لمأموري الضبط القضائي بواسطة مساعد الاجتماعات من النص المسجل.",
    scheduled: "تمت الجدولة",
    kind: "النوع",
    kinds: { FieldVisit: "زيارة ميدانية", PartyMeeting: "جلسة استماع للأطراف", Internal: "داخلي", Committee: "لجنة" },
    when: "التاريخ والوقت",
    location: "المكان",
    attendees: "الحاضرون",
    attendeesHint: "مفصولون بفواصل.",
    judicialOfficer: "زيارة ميدانية لمأمور ضبط قضائي (صلاحيات ضبط الأدلة)",
    // Scenario 6.39: who releases the preliminary report, at which stage, from which report; scenario 6.37: the complainant's request for confidential treatment.
    reportRelease: {
      refused: "لا يمكن إحالة التقرير بعد",
      approvedBy: "اعتمده {{name}}",
    },
    complainantIdentity: {
      title: "طلب الشاكي معاملة سرية",
      protected: "يحجب اسمه ومستندات هويته عن المشكو ضدهم: تشير الخطابات الموجهة إليهم إلى \"الشاكي\"، ويوقف فحص السرية أي خطاب أو تقرير ما زال يكشف هويته.",
      disclosed: "يجوز الإفصاح عن الهوية للمشكو ضدهم بقرار {{name}} في {{date}}.",
      reason: "سبب جواز الإفصاح عن الهوية",
      reasonPlaceholder: "موافقة الشاكي وتاريخها، أو سبب حاجة التحقيق إليها",
      record: "تسجيل قرار الإفصاح",
      recorded: "سجل قرار الإفصاح",
    },
    shareReport: "إحالة التقرير الأولي ({{period.complaint.preliminaryReportReply}} يوم عمل)",
    shared: "تمت إحالة التقرير إلى الأطراف ({{count}})",
    screenTitle: "قد يتضمن التقرير معلومات سرية",
    screenHint: "قورن التقرير بالمستندات المصنفة سرية في الملف. تحذف هذه المقاطع من التقرير، أو يسجل سبب جواز الإفصاح عنها (غير سرية، أو بموافقة صاحبها الكتابية – المادة 20/2).",
    screenKinds: { Passage: "مقطع من مستند سري", Figure: "رقم من مستند سري", Marked: "مقطع موسوم بالسرية", Email: "عنوان بريد إلكتروني", Phone: "رقم هاتف" },
    screenOverride: "سبب جواز الإفصاح عن هذه المقاطع",
    screenRelease: "الإحالة مع هذا السبب",
    runPlanner: "تشغيل مخطط التحقيق",
    runAnalyst: "تشغيل محلل الأدلة",
    plan: "خطة التحقيق",
    planHint: "الأهداف ونظرية الضرر والخطوات المتسلسلة مع المسؤولين والاستبيانات وقائمة الزيارة الميدانية – يعدها مخطط التحقيق ويعتمدها رئيس قسم الشكاوى.",
    planPending: "أعد مخطط التحقيق خطة، وتعتمد من النظرة العامة.",
    noPlan: "لا توجد خطة بعد. ويمكن تشغيل مخطط التحقيق، أو تعد الخطة تلقائيا عند دخول الملف مرحلة التحقيق.",
    objectives: "الأهداف",
    stepStatus: { Planned: "مخطط", InProgress: "جار", Done: "منجز", Blocked: "معلق" },
    stepStatusLabel: "حالة الخطوة {{step}}",
    planVersion: "الإصدار {{version}}",
    fieldVisit: "يوصى بزيارة ميدانية – قائمة التحقق",
    questionnaires: "الاستبيانات",
    evidence: "مصفوفة الأدلة",
    evidenceHint: "يربط كل دليل بادعاء مع اتجاهه والوزن الذي يقدره المحلل لموثوقيته. ويقدر كل دليل بما يبينه دون جمع الأدلة: فالأعداد تبين ما في المصفوفة فقط. ويؤكد التحقق من الدليل بالضغط على الدرع.",
    itemCounts: "{{supports}} مؤيد · {{contradicts}} معارض · {{unverified}} لم يتحقق منه بعد",
    evidencePending: "اقترح محلل الأدلة عناصر، وتعتمد من النظرة العامة.",
    supports: "يدعم",
    contradicts: "يناقض",
    verify: "تأكيد التحقق من قبل الموظف",
    requests: "طلبات المعلومات والردود",
    requestsHint: "ردود الأطراف ({{period.complaint.response}} يوم عمل) وطلبات التحقيق بوتيرة تذكير {{period.complaint.informationRequest}}/{{period.informationRequest.firstReminder}}/{{period.informationRequest.secondReminder}} يفرضها مراقب المدد.",
    cadence: "الوتيرة",
    remindersSent: "التذكيرات: {{count}}",
    warningSent: "صدر إنذار",
    finalNoticeAwaits: "إخطار المادة 23(3) بانتظار اعتمادكم",
    finalNoticeSend: "اعتماد إخطار المادة 23(3) وإرساله",
    finalNoticeSent: "أرسل إخطار المادة 23(3) – الرد حتى {{date}}",
    finalNoticeDone: "تم إرسال إخطار المادة 23(3)",
    nextReminder: "التذكير التالي",
    responses: "الردود",
    meetings: "جلسات الاستماع والزيارات الميدانية",
    meetingsHint: "يحول مساعد الاجتماعات النصوص المسجلة إلى محاضر ثنائية اللغة لاعتماد الموظف.",
    transcript: "نص مسجل",
    minutesApproved: "تم اعتماد المحضر",
    minutes: "المحضر",
    recordTranscript: "تسجيل النص",
    recordTranscriptHint: "يلصق هنا النص المحول من الكلام لجلسة الاستماع أو الزيارة الميدانية؛ يعد مساعد الاجتماعات محضرا ثنائي اللغة للاعتماد.",
    transcriptFormat: "جملة واحدة في كل سطر بالشكل [hh:mm] المتحدث: النص. أسماء المتحدثين اختيارية.",
    transcriptRecorded: "تم تسجيل النص",
    transcriptRecordedHint: "يعد مساعد الاجتماعات المحضر، ويراجع في النظرة العامة.",
    recording: "أو رفع التسجيل الصوتي",
    recordingHint: "mp3 أو wav أو m4a أو ogg أو webm حتى 25 ميغابايت. ينتج نموذج تحويل الكلام إلى نص محضرا مؤرخا بالوقت ومنسوبا إلى المتحدثين باللغة المنطوقة.",
    transcribeAndDraft: "تفريغ التسجيل وصياغة المحضر",
    transcribed: "تم تفريغ التسجيل",
    transcribedHint: "تم التفريغ في {{seconds}} ث، ويصوغ مساعد الاجتماعات المحضر.",
    draftMinutes: "إعداد المحضر",
  },
  analysis: {
    scenarios: "اختبار السيناريوهات",
    scenariosHint: "سيناريوهات المعالجة تحتسب بالطريقة نفسها المستخدمة في التقييم (النطاقات {{moderate}} / {{high}}، وتغير مؤشر هيرفندال-هيرشمان المثير للقلق {{delta}}). تضبط في استوديو الإجراءات ← سياسة التحليل.",
    scenario: "السيناريو",
    verdict: "القراءة",
    safeHarbour: "ضمن الملاذ الآمن",
    customDivest: "التنازل (%)",
    divestTo: "المشتري",
    newEntrant: "داخل جديد إلى السوق",
    runScenario: "تشغيل السيناريو",
    scenarioFooter: "حساب رياضي فقط: السيناريو ليس تقييما للمعالجة ولا يلزم الوزارة.",
    marketHistory: "سجل التركز",
    since: "منذ {{year}}",
    comparables: "حالات سابقة مماثلة",
    comparablesHint: "مرتبة بحسب تشابه مفردات السوق ونوع الملف، لمراجعة التوصية في ضوء سوابق الوزارة.",
    similarity: "التشابه",
    conditions: "{{count}} شرط", conditions_zero: "بلا شروط", conditions_one: "شرط واحد", conditions_two: "شرطان", conditions_few: "{{count}} شروط", conditions_many: "{{count}} شرطا", conditions_other: "{{count}} شرط",
    commitments: "التعهدات بعد القرار",
    commitmentsHint: "الشروط والتعهدات التي تتابعها الوزارة بعد القرار، مع الجهة الملتزمة وطريقة التحقق وموعد المراجعة القادمة.",
    buildRegister: "إنشاء من القرار",
    fromRemedyPackage: "من الحزمة المقبولة",
    trustee: "الوصي",
    sunset: "أجل الانتهاء",
    divestBy: "التنازل بحلول",
    indicator: "مؤشر الفعالية",
    registerCreated: "أضيفت التعهدات إلى السجل ({{count}})",
    commitmentUpdated: "تم تحديث التعهد",
    noCommitments: "لا توجد تعهدات مسجلة",
    noCommitmentsHint: "ينشأ السجل من الشروط المرفقة بالقرار، أو تضاف التعهدات يدويا.",
    performance: "هدف الأداء",
    performanceHint: "الكفاءة التي تقوم عليها الموافقة بوصفها هدفا قابلا للقياس؛ وإذا لم يتحقق طبق شرط التعويض.",
    baseline: "الأساس",
    target: "الهدف",
    targetBy: "بحلول",
    measured: "المقاس",
    notMeasured: "لم يقس بعد",
    targetMet: "تحقق الهدف",
    targetMissed: "لم يتحقق الهدف – يطبق شرط التعويض",
    compensationClause: "شرط التعويض",
    recordMeasurement: "تسجيل قياس",
    measuredValue: "القيمة المقاسة",
    measuredOn: "تاريخ القياس",
    measurementNote: "مصدر الرقم (تقرير الامتثال، الحسابات المدققة، بنك البيانات)",
    measurementSaved: "سجل القياس؛ وتتبع الحالة الهدف",
    directions: { AtLeast: "لا يقل عن", AtMost: "لا يزيد على" },
    nextReview: "المراجعة القادمة",
    lastVerified: "تم التحقق",
    monitoringTitle: "متابعة التعهدات",
    monitoringSubtitle: "كل شرط وتعهد تتابعه الوزارة عبر الحالات المفصول فيها.",
    open: "مفتوح",
    overdue: "متأخر",
    breached: "مخالف",
    met: "مستوفى",
  },
  dataBank: {
    entityStatus: { Active: "قائمة", Inactive: "غير قائمة", Merged: "مدمجة في منشأة أخرى", null: "غير مسجلة" },
    agreementOf: "اتفاق ({{type}})",
    agreementKinds: { Horizontal: "أفقي", Vertical: "رأسي", JointPurchasing: "شراء مشترك", Standardisation: "توحيد المعايير" },
    outcomes: { clearedWithConditions: "موافقة مشروطة", completedNotified: "منجزة بعد الإخطار", completed: "منجزة", lapsed: "انقضت دون إتمام", notifiedExemption: "مخطر بها – قيد دراسة الإعفاء", belowThresholds: "غير مخطر بها – دون حدود قرار مجلس الوزراء رقم (3) لسنة 2025" },
    title: "بنك بيانات المنافسة الوطني",
    subtitle: "المنشآت والقطاعات والأسواق والحصص السوقية والصفقات مجمعة من الحالات والسجلات الحكومية.",
    entities: "المنشآت",
    sectors: "القطاعات",
    markets: "الأسواق",
    shares: "الحصص السوقية",
    transactions: "الصفقات",
    jurisdictions: "الاختصاصات",
    mergeProposals: "مقترحات الدمج",
    ingestion: "مهام الاستيراد",
    studio: "استوديو دراسات السوق",
    curation: "التنقية",
    reference: "البيانات المرجعية",
    lastSync: "آخر مزامنة",
    registeredOf: "مطابقة للسجل الاقتصادي الوطني: {{count, number}}",
    sectorsAndJurisdictions: "القطاعات: {{sectors}} · الاختصاصات: {{jurisdictions}}",
    curatedOf: "مؤكدة من المنسق: {{count}}",
    lineageRecords: "سجلات النسب: {{count, number}}",
    studiesCount: "دراسات السوق: {{count}}",
    searchEntities: "البحث بالاسم العربي أو الإنجليزي أو الاسم البديل أو رقم الرخصة…",
    entityCount: "المنشآت: {{count}}",
    entity: "المنشأة",
    licence: "الرخصة",
    authority: "جهة الترخيص",
    legalForm: "الشكل القانوني",
    emirate: "الإمارة",
    sector: "القطاع",
    turnover: "آخر إيرادات",
    ultimateOwner: "المالك النهائي",
    unregistered: "غير مسجلة",
    aliases: "الأسماء البديلة",
    mergedAway: "مدمجة",
    pendingMergeNotice: "مقترحات دمج تتعلق بهذه المنشأة: {{count}}، وتراجع في تبويب التنقية.",
    partyRoles: { Applicant: "مقدم الطلب", Representative: "الممثل", AcquiringParty: "المستحوذ", TargetParty: "المستهدف", Complainant: "الشاكي", Respondent: "المشكو في حقه", InterestedParty: "ذو مصلحة", Objector: "المعترض", NotifyingParty: "الطرف المخطر", JointVenturePartner: "شريك المشروع المشترك", Whistleblower: "المبلغ", Other: "أخرى" },
    noShares: "لم تسجل حصص سوقية بعد.",
    year: "السنة",
    share: "الحصة",
    revenue: "الإيرادات",
    source: "المصدر",
    curated: "منقاة",
    curatedYes: "منقاة",
    demonstrationRow: "بيانات عرض توضيحي",
    demonstrationRowHint: "سجل اختبار أو عرض توضيحي: يعرض للعلم ولا يحتسب في أرقام السوق.",
    curate: "تأكيد",
    sources: { "company-self-report": "إفادة ذاتية من الشركة", "open-data-master": "بيانات مفتوحة", mergermarket: "ميرجرماركت", "simulated-economy": "بيانات اقتصادية تجريبية", AdvocacySeed: "تقييم تشريع", MarketStudy: "دراسة سوق", Registry: "السجل", FCSC: "المركز الاتحادي للتنافسية والإحصاء", MOF: "وزارة المالية", CaseFiling: "الملف", MarketFeed: "تغذية السوق", Curator: "المنسق", Merge: "دمج", Estimate: "تقدير", Regulator: "جهة تنظيمية" },
    sourceKeys: { registry: "السجل الاقتصادي الوطني", fcsc: "إحصاءات المركز الاتحادي", mof: "التحقق من الإيرادات – وزارة المالية" },
    sourceKinds: { Registry: "سجل", Statistics: "إحصاءات", Regulator: "جهة تنظيمية", CaseFiling: "ملفات الطلبات", Web: "تغذية ويب", Customs: "الجمارك", SelfReport: "تصريح الشركة", OpenData: "بيانات مفتوحة", Exchange: "بورصة", Commercial: "مزود تجاري", Aggregator: "مجمع بيانات", FreeZone: "منطقة حرة" },
    confidenceHint: "يحمل كل مصدر درجة ثقة افتراضية – وزن المصدر لكل رقم يستقى منه. تتقدم بيانات الجمارك والجهات التنظيمية على البيانات المفتوحة؛ ويتحقق من تصريحات الشركات مقابلها.",
    customs: {
      title: "التجارة الجمركية حسب رمز النظام المنسق (الهيئة الاتحادية للجمارك)",
      hint: "واردات وصادرات على مستوى البند منسوبة إلى هذه السوق عبر ربط رموز النظام المنسق – المستوى التفصيلي الذي تحتاجه الإدارة للتحقق من الحصص المصرح بها. وتظهر البيانات حين يربط تكامل الوزارة مع الهيئة.",
      noMapping: "لا رمز مربوط",
      empty: "لم تحمل بيانات جمركية بعد لرموز النظام المنسق المربوطة.",
      emptyNoMapping: "يربط رمز من النظام المنسق بهذه السوق في البيانات المرجعية لنسب التجارة الجمركية إليها.",
      sync: "جلب من الهيئة الاتحادية للجمارك",
      synced: "حملت البيانات الجمركية للرمز {{hs}} (السجلات: {{count}})",
      imports: "الواردات",
      exports: "الصادرات",
      records: "البيانات الجمركية",
      topTraders: "أكبر المستوردين / المصدرين",
      unresolved: "غير مطابق بعد لمنشأة في بنك البيانات",
    },
    editConfidence: "تعديل درجة الثقة الافتراضية",
    applyExisting: "إعادة وزن الأرقام الحالية",
    confidenceSaved: "تم تحديث درجة الثقة (الأرقام المعاد وزنها: {{count}})",
    operations: { Insert: "إنشاء", Update: "تحديث", Merge: "دمج", Curate: "تنقية", Delete: "حذف" },
    tables: { market_entities: "منشأة", market_shares: "حصة", markets: "سوق", market_transactions: "صفقة", external_deals: "صفقة مرصودة" },
    lineage: "النسب",
    lineageFields: {
      NameEn: "الاسم بالإنجليزية", NameAr: "الاسم بالعربية", aliases: "الأسماء الأخرى", tags: "الوسوم", RegistryNumber: "رقم القيد", RegistryAuthority: "جهة القيد",
      LegalForm: "الشكل القانوني", Emirate: "الإمارة", IsicCode: "رمز النشاط", Lei: "معرف الكيان القانوني", Ticker: "رمز التداول", Exchange: "السوق المالية", Website: "الموقع الإلكتروني",
      UltimateOwner: "المالك النهائي", EmployeeCount: "عدد الموظفين", LatestTurnoverAed: "آخر رقم أعمال", LatestTurnoverYear: "سنة رقم الأعمال",
      VerifiedTurnoverAed: "رقم الأعمال الموثق", VariancePercent: "نسبة الفرق", ProductDimension: "سوق المنتج", GeographicDimension: "السوق الجغرافية",
      revenueAed: "الإيرادات", sharePercent: "الحصة", Status: "الحالة", status: "الحالة", filingStatus: "حالة الإخطار", score: "الدرجة",
      completedOn: "تاريخ الإتمام", expectedCloseOn: "الإتمام المتوقع", lastEditAt: "آخر تعديل",
    },
    lineageHint: "كل إنشاء وتحديث ودمج مع مصدره ومرجعه والملف المرتبط.",
    filterMarkets: "صف الأسواق حسب المنتج أو الجغرافيا أو القطاع…",
    marketCount: "الأسواق: {{count}}",
    market: "السوق",
    size: "حجم السوق",
    firms: "منشآت",
    curatedCount: "منقاة: {{count}}",
    hhi: { high: "شديدة التركز", moderate: "متوسطة التركز", low: "غير مركزة" },
    coverage: "حصص {{year}} تغطي {{percent}}% من السوق",
    requestStudy: "طلب دراسة سوق",
    sharesLatest: "الحصص السوقية {{year}}",
    others: "أخرى",
    concentrationTrend: "التركز عبر الزمن",
    trendNeedsYears: "يلزم سنتان على الأقل من الحصص لعرض الاتجاه.",
    shareSeries: "سلسلة الحصص مع المصدر",
    shareSeriesHint: "يحتفظ كل رقم بمصدره ومرجعه ودرجة الثقة. وتأكيد الرقم يجعله القيمة المنقاة المستخدمة في مؤشر التركز ونسبة تركز أكبر أربع منشآت.",
    definingCases: "الحالات التي عرفت هذه السوق",
    studies: "دراسات السوق",
    definition: "التعريف",
    sectorNotes: "إحصاءات القطاع",
    primary: "السجل الباقي",
    duplicate: "المكرر",
    similarity: "التشابه",
    mergeProposalsHint: "يقترح منسق بنك البيانات دمج المتغيرات في الرخصة والنقحرة والشكل القانوني. تنقل الموافقة الأسماء البديلة والحصص وروابط الحالات إلى السجل الباقي.",
    runCurator: "تشغيل المنسق",
    curatorQueued: "منسق بنك البيانات يفحص التكرارات وتعارضات الحصص.",
    noMerges: "لا توجد عمليات دمج بانتظار المراجعة",
    noMergesHint: "يمكن تشغيل المنسق أو مراجعة فحص التكرارات أدناه.",
    approveMerge: "الموافقة على الدمج",
    keepSeparate: "إبقاؤهما منفصلتين",
    openInInbox: "فتح المقترح في صندوق الذكاء الاصطناعي",
    merged: "تم الدمج في {{name}}",
    mergeRejected: "تم تسجيلهما كمنشأتين مختلفتين",
    merge: "دمج",
    duplicateScan: "فحص التكرارات",
    matchReason: {
      unlicensed: "السجل غير المرخص يطابق الاسم المسجل بنسبة {{score}}%.",
      names: "تطابق الاسمين بنسبة {{score}}%؛ الإمارة نفسها: {{emirate}}.",
      sameEmirate: { yes: "نعم", unknown: "غير معروف" },
    },
    duplicateScanHint: "فحص تشابه مباشر عبر الأسماء والأسماء البديلة – تدرج المرشحات دون حد الاقتراح الآلي للمنسق للحكم اليدوي.",
    noDuplicates: "لا توجد مرشحات تكرار أخرى.",
    sourcesTitle: "مصادر البيانات",
    sourcesHint: "السجلات والإحصاءات الحكومية التي تغذي البنك مع درجة الثقة الافتراضية.",
    defaultConfidence: "الثقة",
    syncNow: "مزامنة الآن",
    synced: "{{source}}: السجلات المزامنة: {{count}}",
    sectorsHint: "رموز التصنيف الصناعي الدولي المراجعة الرابعة مع الجهات التنظيمية وعلامات الحساسية؛ الأعداد تعرض المنشآت · الأسواق.",
    sensitive: "حساس",
    jurisdictionsHint: "الاختصاصات الاتحادية والمحلية والمناطق الحرة والأجنبية ذات الصلة بالإخطارات متعددة الاختصاصات.",
    jurisdictionKinds: { Federal: "اتحادي", Emirate: "الإمارات", FreeZone: "المناطق الحرة", Foreign: "أجنبية وإقليمية" },
    transactionsHint: "التركزات والاتفاقيات المسجلة من الإخطارات وطلبات الإعفاء.",
    studioHint: "يجمع وكيل دراسات السوق البنية واتجاهات التركز والصفقات وخيارات السياسات من بنك البيانات؛ وينشر الخبير الاقتصادي من صندوق الذكاء الاصطناعي.",
    newStudy: "دراسة سوق جديدة",
    newStudyHint: "يختار سوق أو قطاع ويوصف النطاق، ثم يعد الوكيل مسودة الدراسة للمراجعة.",
    noStudies: "لا توجد دراسات سوق بعد",
    noStudiesHint: "طلب دراسة لأي سوق في البنك.",
    study: "دراسة السوق",
    studyStatus: { Draft: "مطلوبة", Generating: "قيد الإعداد", InReview: "بانتظار الخبير", Published: "منشورة" },
    analysis: {
      atAGlance: "الدراسة في لمحة",
      definition: "تعريف السوق",
      product: "البعد السلعي",
      geographic: "البعد الجغرافي",
      rationale: "منطق الإحلال",
      adjacentMarkets: "المنتجات المجاورة وسوق ما بعد البيع",
      keyFindings: "النتائج الرئيسية",
      theoriesOfHarm: "نظريات الضرر",
      theory: "النظرية",
      mechanism: "الآلية",
      evidence: "الأدلة في بنك البيانات",
      likelihood: "الاحتمالية",
      likelihoodValue: { Low: "منخفضة", Medium: "متوسطة", High: "عالية" },
      indicators: "تقييم المؤشرات",
      indicator: "المؤشر",
      latest: "الأحدث",
      trend: "الاتجاه",
      benchmark: "المعيار المرجعي",
      assessment: "التقييم",
      assessmentValue: { Favourable: "إيجابي", Neutral: "محايد", Concern: "مثير للقلق" },
      recommendations: "التوصيات للوزارة",
      action: "الإجراء",
      owner: "الجهة المسؤولة",
      timeline: "الإطار الزمني",
      legalBasis: "الأساس القانوني",
      monitoringKpis: "مؤشرات المتابعة وعتبات التحرك",
      charts: "الرسوم البيانية",
      fullReport: "التقرير الكامل",
      sources: "المصادر المستشهد بها",
      leader: "المنشأة الرائدة",
      priceGap: "مؤشر السعر مقابل المعيار",
    },
    indicatorsTitle: "مؤشرات السوق",
    indicatorsHint: "مؤشرات الأسعار والمشاركة في العطاءات ومنشأ الواردات والأجهزة المثبتة والارتباط بمستهلكات ما بعد البيع مسجلة مع مصدرها؛ ويقرأ وكيل دراسات السوق هذه السلاسل.",
    noIndicators: "لم تسجل سلاسل مؤشرات لهذه السوق بعد.",
    chartCount: "الرسوم: {{count}}",
    awaitingPublish: "المسودة جاهزة",
    publishFromInbox: "المراجعة والنشر من صندوق الذكاء الاصطناعي",
    regenerate: "إعادة الإعداد",
    regenerating: "وكيل دراسات السوق يعيد إعداد الدراسة.",
    scope: "النطاق",
    scopeHint: "الأسئلة التي ينبغي أن تجيب عنها الدراسة – البنية، إشارات السلوك، الآثار على الحالات المعلقة.",
    generatingHint: "وكيل دراسات السوق يقرأ الحصص والصفقات وإحصاءات القطاع…",
    inReviewHint: "مسودة مع رسوم بانتظار الخبير الاقتصادي في صندوق الذكاء الاصطناعي. يحفظ النشر التقرير والرسوم هنا.",
    titleAr: "العنوان بالعربية",
    anyMarket: "— اختيار سوق —",
    anySector: "— أي قطاع —",
    studyDefaultTitle: "دراسة سوق",
    studyDefaultScope: "البنية واتجاه التركز والصفقات الأخيرة وإشارات السلوك في {{market}}؛ والآثار على الحالات المعلقة.",
    studyRequested: "تم طلب الدراسة – الوكيل يعدها الآن.",
    intel: {
      graphTab: "شبكة الملكية",
      sentinelTab: "الرصد",
      evidence: "حزمة الأدلة القطعية",
      evidenceHint: "حسبت بمحرك القواعد عند إعداد الدراسة ({{at}})؛ لكل رقم منهجيته.",
      reading: "القراءة",
      downloadReport: "التقرير المصور (PDF)",
      reportDownloaded: "تم تنزيل التقرير المصور.",
      hmt: "اختبار المحتكر الافتراضي (SSNIP)",
      distinctMarket: "سوق معنية بذاتها",
      widenMarket: "توسيع السوق المرشحة",
      undecidedMarket: "لم يحسم – يلزم تحويل مقيس",
      breakEvenRecapture: "نسبة الاسترداد عند التعادل",
      criticalLoss: "الخسارة الحرجة",
      actualLoss: "الخسارة الفعلية",
      margin: "هامش الربح الإجمالي",
      elasticity: "المرونة الضمنية",
      pricingPressure: "ضغط الأسعار الأحادي",
      guppiHint: "الحافز لرفع السعر بعد الاندماج؛ 10% هو المستوى الذي تفترض عنده الممارسة الدولية آثارا جوهرية.",
      cmcrHint: "وفر التكلفة الحدية الذي يعادل تماما حافز رفع السعر.",
      uppHint: "GUPPI بعد خصم كفاءة بنسبة {{credit}}%.",
      netUpp: "UPP الصافي",
      diversion: "نسب التحويل المتناسبة مع الحصص",
      simulation: "محاكاة اندماج معايرة",
      preMerger: "قبل الاندماج",
      scenario: "السيناريو",
      priceEffect: "أثر السعر",
      consumerSurplus: "فائض المستهلك",
      annualEffect: "الأثر السعري السنوي",
      priceChangeBy: "تغير السعر المتوقع بحسب المنشأة",
      screens: "فحوصات العطاءات والأسعار والاستحواذات",
      composite: "مركب",
      tenders: "مناقصات",
      bids: "عطاءات",
      redFlags: "المؤشرات الحمراء",
      noFlags: "لم ترفع الفحوصات أي مؤشر.",
      rotation: "التناوب",
      stability: "ثبات مجموعة المتنافسين",
      avgBidders: "متوسط المتنافسين",
      forensics: "تطابقات جنائية رقمية",
      priceVsBenchmark: "السعر مقابل المؤشر المرجعي",
      creeping: "استحواذات متتابعة",
      priceDivergence: "الزيادة عن المؤشر المرجعي: {{points}}",
      innocent: "تفسيرات بريئة يجب استبعادها",
      graph: "شبكة الملكية والسيطرة والوكالات",
      findingKind: { CommonOwnership: "مالك مشترك", InterlockingDirectorate: "تداخل إداري", Exclusivity: "حصرية" },
      catchments: "النطاقات المحلية (نصف قطر {{km}} كم)",
      centre: "المركز",
      facilities: "المرافق",
      ifLeadersCombine: "إذا اندمج المتصدران",
      reconciliation: "الحصص المطابقة",
      disputed: "متنازع عليها",
      agreed: "متفق عليها",
      straddles: "تتخطى {{share}}",
      dominanceBasis: "حد الهيمنة البالغ {{share}} تطبيق لـ{{basis}} – {{status}}.",
      sandbox: "أثر التدابير التصحيحية",
      sandboxHint: "ماذا لو اندمجت المنشأتان، وما الذي يستعيده التخارج الجزئي؟ محاكاة لوجيت برتراند–ناش معايرة؛ كل رقم محسوب ولا شيء مقدر من نموذج.",
      sandboxEmpty: "يختار المستحوذ والمستهدف ثم تشغل المحاكاة.",
      acquirer: "المستحوذ",
      target: "المستهدف",
      divest: "الجزء المتخارج منه من المستهدف",
      outsideShare: "حصة البديل الخارجي",
      efficiencies: "الكفاءات المزعومة (التكلفة الحدية)",
      independentPurchaser: "مشتر مستقل",
      simulate: "تشغيل المحاكاة",
      combinedShare: "الحصة المجمعة",
      withRemedy: "مع التدبير التصحيحي",
      merger: "الاندماج",
      note: "ملاحظة",
      sentinelHint: "كل سوق فيها مناقصات عامة أو سلسلة أسعار أو استحواذات تفحص، وتقيم مباشرة بالفحوصات القطعية. يكتب محلل فحص الكارتلات الملف؛ ويقرر رئيس الشكاوى.",
      sentinelEmpty: "لا شيء للفحص بعد",
      sentinelEmptyHint: "تسجيل مناقصات أو مؤشر أسعار أو استحواذات على سوق يشغل الفحوصات تلقائيا.",
      runAnalyst: "تشغيل محلل فحص الكارتلات",
      screeningQueued: "يفحص المحلل السوق – سيصل الملف إلى صندوق الذكاء الاصطناعي.",
      openSignals: "إشارات مفتوحة: {{count}}",
      noTenders: "لا مناقصات مسجلة لهذه السوق.",
      tender: "المناقصة",
      buyer: "الجهة المشترية",
      winner: "الفائز",
      coefficientOfVariation: "معامل الاختلاف",
      coefficientOfVariationHint: "معامل الاختلاف بين العطاءات",
      coverRatio: "نسبة التغطية",
      flags: "المؤشرات",
      score: "الدرجة",
      signals: "إشارات",
      lastRuns: "عمليات الفحص الأخيرة",
      graphHint: "المالكون وأعضاء المجالس والموكلون والوكالات المسجلة حول منشآت السوق. مالك مشترك خلف متنافسين، أو عضو في مجلسين، أو وكالة حصرية تغطي المتصدرين: هنا تختبئ القوة السوقية عن جدول الحصص.",
      graphEmpty: "لا شبكة لهذه السوق",
      graphEmptyHint: "تسجيل حصص أو روابط ملكية أو وكالات للسوق يرسم الشبكة تلقائيا.",
      graphLegend: "أزرق متصل: مساهمة · كحلي منقط: عضوية مجلس · ذهبي متقطع: وكالة تجارية حصرية مسجلة · باهت: الحصة في السوق",
      structuralFindings: "النتائج البنيوية",
      noFindings: "لا ملكية مشتركة أو تداخلات أو حصرية مسجلة.",
      agencies: "الوكالات التجارية المسجلة",
      noAgencies: "لا وكالات مسجلة.",
      exclusive: "حصرية",
      nonExclusive: "غير حصرية",
      until: "حتى",
      analytics: "الطبقة التحليلية",
      overcharge: "ما دفعه المشترون فوق مسار المؤشر المرجعي",
      cumulativeExcess: "الفائض التراكمي {{from}}–{{to}}",
      range: "النطاق (الأدنى – الأعلى)",
      latestPremium: "أحدث علاوة سعرية",
      priceDecomposition: "من أصل +{{points}} نقطة سعرية",
      cost: "التكلفة",
      marginShort: "الهامش",
      actualIndex: "مؤشر السعر الفعلي في الإمارات",
      butFor: "المسار الافتراضي (المؤشر المرجعي)",
      premium: "العلاوة",
      excess: "الإنفاق الزائد",
      perMachine: "لكل جهاز",
      structuralBreak: "قبل وبعد {{date}}",
      metric: "المقياس",
      before: "قبل",
      after: "بعد",
      significant: "دال إحصائيا",
      notSignificant: "غير دال",
      observedAlternation: "تناوب الفائزين المرصود",
      expectedAlternation: "المتوقع لو كانت الترسيات مستقلة",
      binomialP: "قيمة p (ثنائية الحدين)",
      allocation: "من يفوز حيث — مؤشر التخصيص {{index}}%",
      byBuyer: "بحسب الجهة المشترية",
      byEmirate: "بحسب الإمارة",
      topWinner: "الفائز الأكثر تكرارا",
      trend: "التركز بحسب السنة وما حركه",
      fromAcquisitions: "من الاستحواذات",
      organic: "تحول عضوي",
      lifetimeCost: "ما يلتزم به ترسية جهاز على مدى {{years}} سنوات",
      competedAtAward: "متنافس عليه عند الترسية",
      lockedAftermarket: "سوق لاحقة مقيدة",
      openAftermarket: "سوق لاحقة مفتوحة",
      machine: "الجهاز (الترسية)",
      consumables: "المستهلكات",
      service: "الخدمة",
      tco: "التكلفة الكلية",
      emirateStructure: "البنية بحسب الإمارة (الأجهزة المثبتة)",
      emirateStructureHint: "حصص الطاقة للأجهزة المثبتة بحسب المورد في كل إمارة من إقرارات ترخيص المرافق.",
      machines: "الأجهزة",
      scenarioMatrix: "أي الاندماجات يمكن للوزارة الموافقة عليها",
      scenarioMatrixHint: "كل زوج بين أكبر أربع منشآت، بالمعايرة نفسها؛ تقرأ الأحكام مقابل {{share}} للحصة و{{guppiWatch}} و{{guppiConcern}} لمؤشر GUPPI و{{priceConcern}} و{{priceSerious}} لأثر السعر.",
      combination: "الاندماج",
      verdict: "الحكم",
      commonOwnership: "الملكية المشتركة كاندماج قائم فعلا",
      remedySizing: "تحديد حجم التدبير التصحيحي",
      sensitivity: "هل تصمد الاستنتاجات أمام افتراضات أخرى؟",
      sensitivityHint: "تعيد كل خلية حساب الأداة بهامش أو نسبة استعادة أو حصة بديل خارجي مختلفة. الأخضر يحافظ على الاستنتاج؛ الأحمر يفقده.",
      harmAssessment: "تقييم نظريات الضرر",
      harmAssessmentHint: "مرتبة بحسب قوة الأدلة المجمعة حتى الآن — لا بحسب خطورة السلوك — مع ما يدحض كل نظرية وما يطلب بموجب المادة 18.",
      evidenceStrength: "الأدلة",
      statistics: "الإحصاءات",
      strongestEvidence: "أقوى دليل",
      rebuttals: "تفسيرات يجب اختبارها",
      informationToRequest: "المعلومات المطلوبة",
      decisionRelevance: "ما يتوقف عليه",
    },
    standardForm: {
      lead: "مؤشر يستدعي التحري، وليس دليلا.",
      column: "النموذج الموحد",
      prescribed: "مفروض",
      notPrescribed: "غير مفروض",
      notRecorded: "غير مسجل",
      record: "تسجيل ما إذا كان النموذج الموحد مفروضا",
      title: "النموذج الموحد للمناقصة {{tender}}",
      hint: "ما إذا كانت الجهة المشترية قد فرضت نموذجا موحدا لعطاءات هذه المناقصة، كما تبينه وثائقها. فإن فرضته، حمل تداخل المستندات بين المتقدمين هذا النموذج تفسيرا بديلا له وخصمت نقاطه من الدرجة المركبة.",
      question: "هل فرضت الجهة المشترية نموذجا موحدا؟",
      yes: "نعم، فرض نموذج موحد",
      no: "لم يفرض نموذج موحد",
      unknown: "غير مسجل",
      formName: "اسم النموذج أو مرجعه",
      note: "ملاحظة (اختيارية)",
      save: "تسجيل",
      saved: "تم التسجيل، وتقرأ الفحوصات المناقصة على أساسه.",
      alternative: "التفسير البديل",
      discounted: "خصمت {{points}} من {{total}} نقطة",
    },
  },
  confirm: {
    finaliseReport: { title: "اعتماد تقرير الدراسة؟", body: "يحفظ التقرير في ملف الطلب بوصفه سجل الإدارة، وينتقل الطلب إلى المرحلة التالية. ولا يمكن تعديل التقرير بعد اعتماده، ويلزم لأي تغيير إعداد مسودة جديدة.", action: "اعتماد التقرير" },
    issueDecision: { title: "توقيع القرار وإصداره؟", body: "يقيد القرار في السجل باسمك، وتبدأ مدة التظلم، ويخطر الأطراف إذا اخترت ذلك. ولا يمكن سحب القرار بعد توقيعه.", action: "توقيع وإصدار" },
    notifyParties: { title: "إخطار الأطراف؟", body: "يرسل إشعار التحقيق إلى جميع الأطراف، وتبدأ مدة الرد اعتبارا من اليوم.", action: "إخطار الأطراف" },
    shareReport: { title: "مشاركة التقرير الأولي؟", body: "يتسلم الأطراف التقرير الأولي، ولهم الرد عليه خلال المدة المقررة قانونا.", action: "مشاركة التقرير" },
    preNotification: { title: "إرسال الرأي وإنهاء المناقشة؟", body: "يتسلم مقدم الطلب رأي الإدارة، وتنتهي مناقشة ما قبل الإخطار.", action: "إرسال وإنهاء" },
    publish: { title: "نشر الإعلان؟", body: "يظهر الإعلان على موقع الوزارة باللغتين العربية والإنجليزية، وتبدأ مدة إبداء المرئيات.", action: "نشر" },
    enforcementIssue: { title: "إصدار قرار الإنفاذ؟", body: "يقيد القرار في السجل ويخطر الأطراف إذا اخترت ذلك. ولا يمكن سحب القرار بعد إصداره.", action: "إصدار القرار" },
    sendLetter: { title: "إرسال الخطاب؟", body: "يرسل الخطاب إلى المرسل إليه ويحفظ في ملف الطلب.", action: "إرسال" },
    finalNotice: { title: "إرسال إخطار المادة 23(3)؟", body: "يمنح الطرف {{period.complaint.finalNotice}} أيام عمل إضافية، وينبه إلى المادة (27) من المرسوم بقانون، وإلى جواز سير الإدارة في الإجراءات بناء على الأدلة الواردة في الملف. ويرسل الإخطار باسمكم.", action: "اعتماد وإرسال" },
    mergeRecords: { title: "دمج السجلين؟", body: "تنقل أسماء السجل المكرر وحصصه السوقية وصفقاته وارتباطه بالملفات إلى السجل الأساسي، ويقيد السجل المكرر مدمجا فلا يظهر بعد ذلك في بنك البيانات. ولا يمكن فصل السجلين بعد الدمج.", action: "دمج السجلين" },
  },
  search: {
    open: "بحث",
    kinds: { case: "الملفات", party: "الأطراف والمنشآت في الملفات", decision: "أرقام القرارات", payment: "إيصالات السداد", company: "المنشآت في بنك البيانات", policy: "السياسات والتشريعات" },
    sections: { concentration: "التركز الاقتصادي", investigations: "التحقيقات والشكاوى", policies: "السياسات", requests: "طلبات أخرى", databank: "بنك البيانات" },
  },
  r3Process: {
    blockers: {
      BindingFindings_zero: "نتائج للمنصة تمنع كل مخارج هذه المرحلة: {{detail}}",
      BindingFindings_one: "نتيجة للمنصة تمنع كل مخارج هذه المرحلة: {{detail}}",
      BindingFindings_two: "نتيجتان للمنصة تمنعان كل مخارج هذه المرحلة: {{detail}}",
      BindingFindings_few: "نتائج للمنصة تمنع كل مخارج هذه المرحلة: {{detail}}",
      BindingFindings_many: "نتائج للمنصة تمنع كل مخارج هذه المرحلة: {{detail}}",
      BindingFindings_other: "نتائج للمنصة تمنع كل مخارج هذه المرحلة: {{detail}}",
    },
    finding: {
      title: "نتيجة المنصة",
      open: "مراجعة",
      reason: "الأسباب",
      reasonHint: "تدون في سجل الملف وفي مذكرة القرار.",
      record: "تسجيل التجاوز",
      recordAndApprove: "التجاوز والاعتماد",
      recorded: "سجل التجاوز",
      authorisedOnly: "تجاوز نتائج المنصة مقصور على الموظف المخول.",
      notOverridable: "شرط من شروط الإجراء، ولا يجوز تجاوزه.",
    },
    left: {
      needed_zero: "لا شيء مطلوب",
      needed_one: "مطلوب بند واحد",
      needed_two: "مطلوب بندان",
      needed_few: "مطلوب {{count}} بنود",
      needed_many: "مطلوب {{count}} بندا",
      needed_other: "مطلوب {{count}} بند",
      confirm: "تأكيد",
      refuse: "رفض",
      refuseTitle: "رفض طلب السحب",
      refuseReasons: "أسباب الرفض",
      refuseHint: "يبلغ الشاكي بهذه الأسباب في إشعار على البوابة وفي سجل الملف، ولا يرسل الرفض خطابا. ويستمر النظر في الشكوى في مرحلتها.",
      refused: "رفض الطلب – يستمر النظر في الشكوى",
    },
    primary: {
      finding: "مراجعة النتيجة: {{title}}",
      confirm: "{{title}}",
      confirmed: "أكدت الخطوة",
    },
    conclude: { ruledOut: "ممنوع حتى تعالج النتيجة أو تتجاوز: {{findings}}" },
    criterion: {
      showPlatform: "عرض تقييم المنصة",
      hidePlatform: "إخفاء",
      agree: "الموافقة على تقييم المنصة",
      agreeNeeds: "تضاف الأسباب أو دليل واحد على الأقل للموافقة.",
      agreed: "سجلت الموافقة على تقييم المنصة مع الأسباب",
      key: "دليل الرموز",
    },
    history: {
      ended: "{{route}} · {{name}} · {{date}}",
      route: { Review: "أنهيت بتوقيع المراجعة", Step: "أنهيت بخطوة من موظف", Party: "أنهيت بإجراء من مقدم الطلب", Automatic: "أنهتها المنصة" },
      by: { Automatic: "دون موظف", Party: "مقدم الطلب" },
    },
    clock: {
      recalculate: "تحديث المدة من سجل الملف",
      confirmInterruption: "تأكيد وقف المدة",
      confirmResumption: "تأكيد استئناف المدة",
      resumptionAwaits: "ورد ما ينهي وقف المدة – استئنافها بانتظار تأكيدكم",
      resumptionFrom: "ورد في {{date}}. وعند التأكيد تستأنف المدة اعتبارا من ذلك الوقت، لا من تاريخ التأكيد.",
    },
    lists: { unattended: "مدة سارية دون موظف مسؤول" },
    signals: {
      open: "فتح ملف",
      title: "قرار فتح ملف",
      reasons: "أسباب فتح الملف",
      reasonsHint: "الإشارة مؤشر للاستقصاء، ويفتح الملف بقرار من الموظف المخول.",
      decide: "فتح الملف",
    },
    inbox: {
      count_zero: "لا مقترحات",
      count_one: "مقترح واحد",
      count_two: "مقترحان",
      count_few: "{{count}} مقترحات",
      count_many: "{{count}} مقترحا",
      count_other: "{{count}} مقترح",
      quick: "الاعتمادات السريعة",
      quickCount_zero: "لا اعتمادات سريعة",
      quickCount_one: "اعتماد سريع واحد",
      quickCount_two: "اعتمادان سريعان",
      quickCount_few: "{{count}} اعتمادات سريعة",
      quickCount_many: "{{count}} اعتمادا سريعا",
      quickCount_other: "{{count}} اعتماد سريع",
      quickHint_zero: "اعتمادات تمت دون فتح المسودة.",
      quickHint_one: "اعتمادات تمت قبل مضي ثانية واحدة على فتح المعتمد للمسودة (أقل مدة للمراجعة حددتها الإدارة)، أو دون فتحها.",
      quickHint_two: "اعتمادات تمت قبل مضي ثانيتين على فتح المعتمد للمسودة (أقل مدة للمراجعة حددتها الإدارة)، أو دون فتحها.",
      quickHint_few: "اعتمادات تمت قبل مضي {{count}} ثوان على فتح المعتمد للمسودة (أقل مدة للمراجعة حددتها الإدارة)، أو دون فتحها.",
      quickHint_many: "اعتمادات تمت قبل مضي {{count}} ثانية على فتح المعتمد للمسودة (أقل مدة للمراجعة حددتها الإدارة)، أو دون فتحها.",
      quickHint_other: "اعتمادات تمت قبل مضي {{count}} ثانية على فتح المعتمد للمسودة (أقل مدة للمراجعة حددتها الإدارة)، أو دون فتحها.",
      quickNone: "لا توجد اعتمادات سريعة.",
      reviewTime_zero: "اعتمدت فور فتحها",
      reviewTime_one: "اعتمدت بعد ثانية واحدة من فتحها",
      reviewTime_two: "اعتمدت بعد ثانيتين من فتحها",
      reviewTime_few: "اعتمدت بعد {{count}} ثوان من فتحها",
      reviewTime_many: "اعتمدت بعد {{count}} ثانية من فتحها",
      reviewTime_other: "اعتمدت بعد {{count}} ثانية من فتحها",
      notOpened: "اعتمدت دون فتح المسودة",
      withoutFile: "دون ملف",
    },
    peerAgainstFindings: "تؤيد هذه المراجعة التقرير مع بقاء نتائج قائمة",
    peerAgainstFindingsBody: "لا يزال الملف يتضمن: {{findings}}. ولا يتجاوزها إلا بأسباب مسجلة من موظف مخول.",
  },
  demo: {
    title: "ساعة الاختبار",
    hint: "في منصة الاختبار وحدها: تقديم الساعة لتنتهي المدد وترسل التذكيرات.",
    platformTime: "وقت المنصة",
    realTime: "الوقت الفعلي",
    offset_zero: "دون تقديم", offset_one: "مقدمة يوما واحدا", offset_two: "مقدمة يومين", offset_few: "مقدمة {{count}} أيام", offset_many: "مقدمة {{count}} يوما", offset_other: "مقدمة {{count}} يوم",
    advanceBy_zero: "دون تقديم", advanceBy_one: "يوم واحد", advanceBy_two: "يومان", advanceBy_few: "{{count}} أيام", advanceBy_many: "{{count}} يوما", advanceBy_other: "{{count}} يوم",
    advanced_zero: "لم تتقدم الساعة", advanced_one: "تقدمت الساعة يوما واحدا", advanced_two: "تقدمت الساعة يومين", advanced_few: "تقدمت الساعة {{count}} أيام", advanced_many: "تقدمت الساعة {{count}} يوما", advanced_other: "تقدمت الساعة {{count}} يوم",
    sweepResult: "مدد في خطر: {{atRisk}} · متجاوزة: {{breached}} · انتقالات انتهاء مفعلة: {{fired}}",
    reset: "أعيدت ساعة المنصة إلى الوقت الفعلي",
    resetClock: "إعادة",
    runSweep: "تشغيل فحص المدد",
    sweepDone: "اكتمل فحص المدد",
    replay: "إعادة التشغيل",
    replayTitle: "إعادة تشغيل – {{caseNumber}}",
    replayHint: "كل انتقال ورسالة ومقترح وكيل وقرار بترتيب حدوثه؛ ويتبع المخطط حالة الطلب.",
    play: "تشغيل",
    pause: "إيقاف",
  },
  r3Library: {
    signatory: "يوقعه {{capacity}} بموجب {{authority}}",
    verificationRequired: "يتطلب تحقق الإدارة",
    status: { Confirmed: "مؤكدة من الإدارة", Awaiting: "بانتظار تأكيد الإدارة", Excluded: "مستبعدة" },
    confirmed: "تم التأكيد، ويجوز للوكلاء الاستشهاد بهذه الوثيقة الآن.",
    added: "أضيفت الوثيقة، وهي بانتظار تأكيد الإدارة.",
    notRecorded: "غير مسجل",
    downloadList: "تنزيل القائمة",
    addDocument: "إضافة وثيقة",
    add: "إضافة",
    addHint: "تبقى الوثيقة بانتظار تأكيد خبير قانوني أو المدير قبل أن يستشهد بها أي وكيل.",
    stats: { documents: "الوثائق في المكتبة", awaiting: "بانتظار تأكيد الإدارة", escalated: "استفسارات محالة إلى باحث", faqs: "إجابات منشورة كأسئلة شائعة" },
    tabs: { library: "المكتبة" },
    filter: "العنوان أو الرمز أو الجهة المصدرة",
    libraryHint: "لا يستشهد الوكلاء بالوثائق المنسوبة إلى الوزارة ولا بالوثائق المضافة يدويا قبل أن تؤكدها الإدارة.",
    effective: "تاريخ النفاذ",
    withdrawn: "مسحوبة من الوكلاء",
    precedentSearch: "البحث في السوابق",
    precedentKinds: { Precedent: "سابقة", ComparativeReference: "مرجع مقارن", OfficialAnswer: "إجابة رسمية" },
    jurisdiction: "دولة الجهة المصدرة",
    comparativeHint: "يدرج قرار الجهة الأجنبية ويصنف ويستشهد به مرجعا مقارنا، ولا يعد أبدا سابقة للوزارة.",
    initialView: {
      title: "رأيك الأولي",
      hint: "تسجل السوق المعنية ونظرية الضرر (أو الشاغل التنافسي) كما يراهما الموظف الآن. ولا تقترح السوابق، على الموظف أو على الوكلاء، إلا بعد تسجيلهما معا.",
      product: "السوق السلعية أو سوق الخدمة المعنية",
      geography: "السوق الجغرافية",
      theory: "نظرية الضرر أو الشاغل التنافسي",
      by: "سجله",
      record: "تسجيل الرأي الأولي",
      change: "تسجيل رأي جديد",
      incomplete: "الرأي غير مكتمل: يلزم بيان السوق ونظرية الضرر معا.",
      recordedToast: "سجل الرأي الأولي",
    },
    studyConsistency: {
      title: "الاتساق مع تعريفات الإدارة السابقة للسوق",
      none: "لا يضيق تعريف السوق في الدراسة تعريفا سابقا للإدارة للنشاط نفسه ولا يوسعه ولا يخالفه.",
    },
    citedByAgents: "يستشهد بها الوكلاء",
    source: "المصدر",
    supplier: "الجهة الموردة",
    confirmedBy: "أكدها",
    issuedOn: "تاريخ الإصدار",
    text: "النص",
    ministryAwaiting: "منسوبة إلى الوزارة: لا يستشهد بها الوكلاء قبل أن تؤكد الإدارة نصها ووضعها.",
    confirm: "تأكيد النص والوضع",
    withdraw: "سحب من الوكلاء",
    restore: "إعادة إلى الوكلاء",
    precedents: {
      awaitingView: "تقترح السوابق بعد تسجيل تعريف السوق ونظرية الضرر في الدراسة، والبحث أعلاه متاح في أي وقت.",
      noneFound: "لا توجد سابقة تطابق هذا البحث.",
      noneClose: "لا توجد في المكتبة سابقة قريبة من هذا الملف من حيث السوق والنشاط.",
      definitionDiffers: "يختلف تعريف السوق عن تعريف الإدارة في القرار {{reference}} ({{year}}) للنشاط نفسه.",
      ground: "أساس التمييز",
      recordGround: "تسجيل أساس التمييز",
      groundRecorded: "سجل الأساس",
      save: "تسجيل",
      clear: "مسح البحث",
      open: "فتح",
      differs: "أوجه الاختلاف",
      matches: "أوجه التشابه",
      levels: { Close: "قريبة", PartlyComparable: "قابلة للمقارنة جزئيا", Distant: "بعيدة" },
      dimensions: { activity: "النشاط", product: "المنتج", geography: "السوق الجغرافية", overlap: "التداخل", transaction: "الصفقة", provisions: "المواد المستند إليها" },
      awaitingRecordedView: "تقترح السوابق بعد أن تسجل رأيك الأولي: السوق المعنية ونظرية الضرر. والبحث أعلاه متاح في أي وقت.",
      enquiryHint: "تقارن الردود الرسمية السابقة وسوابق المكتبة بهذا الاستفسار من حيث المواد التي يستند إليها كل منها، مع حجب أسماء الملفات الأخرى وأرقامها.",
      awaitingEnquiryView: "قاعدة مؤقتة إلى أن تحدد الإدارة الرأي المسجل الذي يتيح الاقتراح: تقترح الردود الرسمية السابقة والسوابق – عليك وعلى الوكيل الذي يعد الرد – بعد تسجيل نتيجة «تحديد الأحكام الناظمة» في المراجعة. والبحث أعلاه متاح في أي وقت.",
      noneEnquiry: "لا يشترك أي رد رسمي سابق أو سابقة مع هذا الاستفسار في مادة أو في كلمات السؤال.",
    },
    consistencyAlert: {
      relation: {
        Narrower: "تعريف السوق أضيق من تعريف الإدارة في {{reference}} ({{year}}) للنشاط نفسه.",
        Wider: "تعريف السوق أوسع من تعريف الإدارة في {{reference}} ({{year}}) للنشاط نفسه.",
        Different: "يختلف تعريف السوق عن تعريف الإدارة في {{reference}} ({{year}}) للنشاط نفسه.",
      },
      source: { Decision: "قرار صادر على المنصة", Library: "قرار للإدارة محفوظ في المكتبة", DataBank: "سوق في سجل بنك البيانات، مع الملف الذي عرفت فيه" },
      basis: { OfficerDefinition: "تعريف السوق الذي سجلته", Declared: "الأسواق كما صرح بها (لم يسجل موظف تعريفا للسوق بعد)", MarketStudy: "تعريف السوق في الدراسة" },
      thisFile: "هذا الملف",
      earlier: "التعريف السابق",
      product: "السوق السلعية",
      geography: "السوق الجغرافية",
      dimension: { Same: "مطابقة", Narrower: "أضيق", Wider: "أوسع", Different: "مختلفة" },
      ask: "يلزم تسجيل أساس التمييز. ويبقى التنبيه مفتوحا حتى يسجل، ولا يوقف أي خطوة.",
    },
    ops: {
      noFiles: "لا يستند هذا الرقم إلى أي ملف.",
      days_zero: "{{value}} يوم",
      days_one: "يوم واحد",
      days_two: "يومان",
      days_few: "{{value}} أيام",
      days_many: "{{value}} يوما",
      days_other: "{{value}} يوم",
      onTime: "في موعدها",
      late: "متأخرة",
      draftsLine: "دون تعديل: {{unchanged}}، معدلة: {{amended}}، مرفوضة: {{rejected}}",
      times_zero: "لم يحتسب",
      times_one: "محتسب مرة واحدة",
      times_two: "محتسب مرتين",
      times_few: "محتسب {{count}} مرات",
      times_many: "محتسب {{count}} مرة",
      times_other: "محتسب {{count}} مرة",
      figures: { openCases: "الملفات المفتوحة", ecFormalReviewDays: "المراجعة الأولية لطلبات التركز", ecDecisionDays: "قرارات التركز", complaintResolutionDays: "الشكاوى المغلقة", slaCompliance: "المدد القانونية المنجزة في موعدها", aiAcceptance: "مسودات الذكاء الاصطناعي المقبولة" },
      method: {
        ecFormalReviewDays: "متوسط الأيام التقويمية من تقديم الطلب إلى قبوله شكليا، لكل ملفات التركز المقبولة منذ بدء السجلات. ولا تحتسب ملفات الاختبار.",
        ecDecisionDays: "متوسط الأيام التقويمية من تقديم الطلب إلى صدور القرار، لكل ملفات التركز التي صدر فيها قرار منذ بدء السجلات.",
        complaintResolutionDays: "متوسط الأيام التقويمية من تقديم الشكوى إلى إغلاقها، لكل الشكاوى المغلقة منذ بدء السجلات.",
        slaCompliance: "نسبة المدد القانونية للوزارة التي أنجزت في موعدها أو قبله. ولا تحتسب المدد الممنوحة للأطراف.",
        aiAcceptance: "المسودات المعتمدة دون تعديل أو بعد تعديلها، مقسومة على المسودات التي بت فيها موظف (معتمدة دون تعديل أو معدلة أو مرفوضة). ولا تحتسب المسودات التي لم يبت فيها، ويعرض الجدول أدناه الأعداد نفسها لكل وكيل.",
      },
      counts: {
        openCases: "مفتوحة الآن",
        ecFormalReviewDays: "متوسط الملفات المقبولة منذ بدء السجلات",
        ecDecisionDays: "متوسط الملفات التي صدر فيها قرار منذ بدء السجلات",
        complaintResolutionDays: "متوسط الشكاوى المغلقة منذ بدء السجلات",
        slaCompliance: "نسبة المدد المنجزة منذ بدء السجلات",
        aiAcceptance: "نسبة المسودات التي بت فيها منذ بدء السجلات",
      },
      files_zero: "لا ملفات",
      files_one: "ملف واحد",
      files_two: "ملفان",
      files_few: "{{count}} ملفات",
      files_many: "{{count}} ملفا",
      files_other: "{{count}} ملف",
      excluded_zero: "لا تستثنى أي ملفات اختبار من هذه الأرقام.",
      excluded_one: "يستثنى من هذه الأرقام ملف اختبار واحد.",
      excluded_two: "يستثنى من هذه الأرقام ملفا اختبار.",
      excluded_few: "يستثنى من هذه الأرقام {{count}} ملفات اختبار.",
      excluded_many: "يستثنى من هذه الأرقام {{count}} ملف اختبار.",
      excluded_other: "يستثنى من هذه الأرقام {{count}} ملف اختبار.",
      drafts: "مسودات الذكاء الاصطناعي حسب الوكيل",
      bySection: "مسودات الذكاء الاصطناعي حسب القسم",
      bySectionHint: "الأعداد ذاتها وبالطريقة ذاتها لكل قسم من أقسام الإدارة وحده: المسودات على ملفاته، أو مسودات وكلاء بنك البيانات ورصد الصفقات والسياسات. ويظهر الوكلاء وراء كل صف عند المرور عليه.",
      section: "القسم",
      accepted: "المقبولة",
      unchanged: "معتمدة دون تعديل",
      amended: "معدلة",
      rejected: "مرفوضة",
      pending: "بانتظار البت",
      total: "جميع الوكلاء",
      decisions: "القرارات الصادرة حسب النتيجة",
      clocksHint: "المدد القانونية للملفات الفعلية حسب نوعها وحالتها: السارية كما هي الآن، والمنجزة منذ بدء السجلات.",
      filesOf: "الملفات التي يستند إليها الرقم {{value}}",
      showFiles: "الملفات",
    },
    opsBasis: {
      decision: "القرار {{number}} بتاريخ {{date}}",
      due: "الموعد {{date}}",
      outcomeFiles: "القرارات: {{outcome}}",
      typeFiles: "الملفات المفتوحة: {{type}}",
      cellFiles: "{{clock}} – {{health}}",
      clickHint: "يمكن اختيار خلية أو نتيجة أو نوع ملف لعرض الملفات التي يستند إليها.",
      draftsHint: "يمكن اختيار أي عدد لعرض المسودات التي يحتسبها، مع ملف كل منها.",
      draftsFiles: "{{agent}} – {{outcome}}",
      draftDecided: "{{outcome}} في {{date}}",
      draftPending: "بانتظار القرار منذ {{date}}",
      draftNoFile: "مسودة غير مرتبطة بملف",
      openDraft: "فتح المسودة",
    },
    advocacy: {
      newInstrument: "تشريع جديد",
      suspended: "منهجية هذا القسم بانتظار اعتماد الإدارة. تسجل التشريعات، ولا يجرى فرز أو تقييم حتى ذلك الحين.",
      suspendedDraft: "منهجية هذا القسم بانتظار اعتماد الإدارة. والفرز والمسودة في هذا السجل سابقان لتعليقها ولا يطبقان.",
      caseData: "يذكر هذا الرأي ملف قضية أو طرفا فيه، ولذلك يحجب: لا يجوز أن تظهر في رأي السياسات بيانات أي قضية أو طرف.",
      stats: { recorded: "التشريعات المسجلة", inReview: "مسودات بانتظار الاعتماد", adopted: "الآراء المعتمدة", sent: "المرسلة إلى الجهات المصدرة", highOrSevere: "مرتفع أو شديد" },
      methodTab: "المنهجية",
      proposed: "منهجية مقترحة بانتظار اعتماد الإدارة، ولا تطبق.",
      proposedTitle: "المنهجية المقترحة",
      recorded: "سجل التشريع.",
      approval: {
        title: "اعتماد الإدارة للمنهجية",
        approved: "معتمدة",
        awaiting: "بانتظار اعتماد الإدارة",
        notApproved: "المنهجية بانتظار اعتماد الإدارة: إلى أن يسجل الاعتماد تسجل التشريعات دون فحصها، ولا يعد أي وكيل تقييما.",
        recordHint: "لا يسجل الاعتماد إلا بعد صدوره كتابة من الإدارة، ويقيد في سجل التدقيق باسمك.",
        reference: "مرجع الاعتماد المكتوب",
        referenceHint: "كتاب الإدارة أو مذكرتها ورقمها.",
        referenceAr: "المرجع بالعربية (اختياري)",
        approvedOn: "تاريخ الاعتماد المكتوب",
        recordedBy: "سجله",
        note: "ملاحظة (اختيارية)",
        record: "تسجيل اعتماد الإدارة",
        recorded: "سجل اعتماد الإدارة للمنهجية",
        whoRecords: "يسجل اعتماد الإدارة: {{roles}}. ولا يسجله مسؤول النظام.",
      },
    },
    advocacyGuard: {
      held: "يحمل هذا الرأي بيانات من ملفات الإدارة – رقم ملف أو اسم طرف أو رقما مصرحا به في ملف – فهو موقوف: لا يعتمد ولا يصدر حتى تزال هذه البيانات. ولا يجوز أن تظهر في رأي المناصرة أي بيانات من الملفات أو أطرافها.",
      found: "ما وجد",
    },
  },
  account: {
    menu: "الحساب",
  keys: {
    title: "اختصارات لوحة المفاتيح",
    search: "البحث في الملفات والشركات والأرقام المرجعية",
    rows: "التنقل بين صفوف القائمة",
    open: "فتح الصف المحدد",
    close: "إغلاق اللوحة أو النافذة",
  },
},
};

export default ar;
